import React, { useState, useMemo, useCallback, useRef, useEffect, lazy, Suspense } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  deleteDoc,
  deleteField,
  type DocumentData,
  type UpdateData,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { useCRM, useCRMCollections, type LazyCrmCollection } from '../utils/CRMContext';
import {
  ProjectHubScopedDataProvider,
  useProjectHubProjectData,
} from '../hooks/useProjectHubProjectData';
import {
  collectRollupCommentMentionProjectIds,
  collectRollupPdfMentionProjectIds,
  collectTaskCommentMentionProjectIds,
  computeRollupRowStats,
  rollupHasUnreadFileActivity,
  rollupHasUnreadHubActivity,
  subscribeToProjectHubRollups,
  type ProjectHubRollup,
} from '../utils/projectHubRollups';
import { useProjectTimeContext } from '../context/ProjectTimeContext';
import DealSupplierRfqPanels from '../components/vendor/DealSupplierRfqPanels';
import DealChangeOrderPanel from '../components/projects/DealChangeOrderPanel';
import { ProjectDepartment, ProjectTask, TaskGroup, TaskComment, EnhancedDeal, OnboardingItem, Milestone, TaskDependencyEdge, UpdateDependencyPayload, type ChangeOrder, type DependencyEdgeType, type LeadComm, type PdfAnnotation, type ProjectMeeting, type ProjectMeetingNote, type ProjectPhase, type ProjectStatus, type ProposalBuilderData, type RfqSubmission, TimberProject, PROJECT_PHASE_LABELS, isEarlyFunnelProjectPhase, type TaskReminder, type TaskActivityLog } from '../types';
import { subscribeChangeOrdersForDeal } from '../utils/changeOrders';
import {
  closeRfqProjectsForDeal,
  reopenRfqProjectsForDeal,
} from '../utils/rfqProjectHubCloseSync';
import { getSignedCommercialListTotal } from '../utils/proposalPublicGrandTotals';
import { buildPaymentScheduleBreakdown } from '../utils/paymentSchedule';
import { computePassedInstallments, computePaymentDueForecast, isProductionGroupName, resolvePaymentDueTaskAnchors } from '../utils/paymentDueFlags';
import { invoiceClosesMilestone } from '../utils/partialInvoiceAllocation';
import { scopeProductionScheduleToSupplier } from '../utils/supplierProductionScope';
import {
  buildSupplierPortalPreviewSearch,
  parseSupplierPortalPreviewRole,
} from '../utils/vendorPortalPreviewQuery';
import { buildSupplierProductionGroupContext, dealIdFromProductionCommentKey, isProductionCommentKey } from '../utils/productionCommentAccess';
import { buildProjectHubTeamMembers } from '../utils/projectHubTeamMembers';
import { buildProductionMentionCandidates } from '../utils/productionMentionCandidates';
import SupplierTaskPanel from '../components/pm/SupplierTaskPanel';
import { usePaymentLagSettings } from '../hooks/usePaymentLagSettings';
import { recordPaymentForecastSnapshot } from '../utils/paymentForecastLog';
import {
  onboardingItemToProjectTask,
  isOnboardingVirtualGroupId,
  isOnboardingBridgeTaskId,
  onboardingTaskIdToItemId,
  parseOnboardingBridgeTaskId,
  findProjectTaskForDeepLink,
  normalizeOnboardingDepartment,
  resolveDepartmentFromTaskGroupName,
  resolveTaskGroupIdForDepartment,
  mirrorAllOnboardingTasks,
} from '../utils/onboardingTaskBridge';
import AppHeader from '../components/ui/AppHeader';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import EngineerHeader from '../components/pm/EngineerHeader';
import { isLimitedProjectViewer, canAccessProjectHubClientPortalControls, canAccessInspirationBoard, canAccessProjectHubSuppliersTab, canAccessProjectHubChangeOrdersTab, canViewTaskActivityHistory, canViewActionRequiredWaitAnalytics, canViewProjectTimeResults, canLogProjectTaskTime, canPromoteProjectToPreconstruction, canSimulateUnderContract, isDesignerRole, isArchitectRole, canPreviewPartnerPortals, isDesignPartnerOrEngineerRole, isCrmTestUser } from '../utils/userRoles';
import StaffPortalViewAs from '../components/pm/StaffPortalViewAs';
import ProjectNotificationPrefsControl from '../components/pm/ProjectNotificationPrefsControl';
import { staffPortalPreviewExitHref, staffPortalPreviewHref } from '../utils/staffPortalPreview';
import { projectHasArchitectUid } from '../utils/architectProjectAccess';
import { isDesignGroupName } from '../utils/designGroupName';
import {
  buildArchitectPortalPreviewSearch,
  parseArchitectPreviewUid,
} from '../utils/architectPortalPreviewQuery';
import { useSimulateUnderContract } from '../hooks/useSimulateUnderContract';
import {
  isSimulatedUcGroupId,
  isSimulatingUnderContract,
  isSimulationSeededOnboarding,
  mergeOnboardingsForPlanning,
  mergeTaskGroupsForPlanning,
  simulatedScheduleFromItemPatch,
  simulatedSchedulesFromOnboardingItems,
} from '../utils/simulateUnderContract';
import { resolveFileViewerLeadId } from '../utils/projectFileViewerLeadId';
import { canViewAllProjectsTimeReport, canEditPreconBillableStatus } from '../utils/projectTimeAccess';
import { applyActionRequiredSideEffects } from '../utils/actionRequiredWorkflow';
import { userHasSalesRepDesignation } from '../utils/employeeDesignations';
import PMSidebar from '../components/pm/PMSidebar';
import TaskTable from '../components/pm/TaskTable';
import { sortTaskGroupsForProject } from '../utils/taskGroupOrder';
import { partitionHubProjects, sortHubProjectSections } from '../utils/hubProjectSections';
import {
  GROUP_TIMELINE_FAMILIES,
  GROUP_TIMELINE_FAMILY_LABELS,
  type GroupTimelineColorMode,
  type GroupTimelineFamily,
  type GroupTimelineLayoutMode,
  type GroupTimelineProjectLabel,
  type GroupTimelineSortMode,
  applyGroupTimelineGroupColors,
  buildGroupTimelineColorLegend,
  buildGroupTimelineView,
  formatGroupTimelineSectionLabel,
  isGroupTimelineSupplierFocusFamily,
  listGroupTimelineFamilyParentTasks,
  parseGroupTimelineFamily,
  readStoredGroupTimelineColorMode,
  readStoredGroupTimelineExtraTasks,
  readStoredGroupTimelineFamily,
  readStoredGroupTimelineLayout,
  readStoredGroupTimelineSort,
  resolveGroupTimelineBarColor,
  storeGroupTimelineColorMode,
  storeGroupTimelineExtraTasks,
  storeGroupTimelineFamily,
  storeGroupTimelineLayout,
  storeGroupTimelineSort,
  type GroupTimelineExtraTasksByFamily,
} from '../utils/groupTimeline';
import {
  applyGroupTimelineDetailFilter,
  buildGroupTimelineFilterOptions,
  countActiveGroupTimelineFilters,
  detailFilterFromConflictIssues,
  EMPTY_GROUP_TIMELINE_FILTER,
  readStoredGroupTimelineFilter,
  storeGroupTimelineFilter,
  type GroupTimelineDetailFilter,
} from '../utils/groupTimelineFilter';
import {
  buildGroupTimelineSupplierIndex,
  filterTasksVisibleToSupplierOrg,
  quoteDerivedSupplierLabel,
  supplierOptionsForTaskPanel,
  unwrapSupplierDisplayTaskId,
} from '../utils/groupTimelineSupplier';
import {
  buildGroupTimelineConflictBoard,
  emptyConflictBoard,
  hasConflictIssues,
  isGroupTimelineSupplierAssignableTitle,
  issuesForFamilyChip,
  supplierCapabilityForTaskTitle,
  type GroupTimelineConflictIssues,
  type ProposalStructureRef,
} from '../utils/groupTimelineConflicts';
import { loadDealProposalStructures } from '../utils/loadDealProposalStructures';
import GroupTimelineConflictBadge from '../components/pm/GroupTimelineConflictBadge';
import GroupTimelineColorControls from '../components/pm/GroupTimelineColorControls';
import GroupTimelineFilterControls from '../components/pm/GroupTimelineFilterControls';
import GroupTimelineFocusBanner from '../components/pm/GroupTimelineFocusBanner';
import GroupTimelineViewControls from '../components/pm/GroupTimelineViewControls';
import TaskDetailPanel from '../components/pm/TaskDetailPanel';
import GroupCommentsPopout from '../components/pm/GroupCommentsPopout';
import type { GroupCommentsTab } from '../components/pm/TaskComments';
import { architectDiscussionUnreadCount, resolveGroupCommentsTab } from '../utils/commentAudience';
import { clearArchitectDecisionsTourSeen } from '../components/pm/ArchitectDecisionsTour';
import { useCommentsMovedTour } from '../components/pm/CommentsMovedTour';
import CommentsHelpButton from '../components/pm/CommentsHelpButton';
import { ProjectHubMetaFlyout } from '../components/pm/ProjectHubMetaFlyout';
import ProjectStandupCell from '../components/pm/ProjectStandupCell';
import { canViewArchitectStandupReports, canSubmitArchitectStandup } from '../utils/architectStandupAccess';
import { useArchitectStandupLatestByProject } from '../hooks/useArchitectStandupLatestByProject';
import AllProjectsPaneToolbar from '../components/pm/AllProjectsPaneToolbar';
import GroupTimelineSavedViewsControl from '../components/pm/GroupTimelineSavedViewsControl';
import GanttTimeline, { type GanttAllProjectsApi } from '../components/pm/GanttTimeline';
import type { GanttSyncedViewport } from '../utils/ganttTimelineViewSync';
import {
  seedGroupTimelineColorEntries,
  type GroupTimelineColorEntry,
} from '../utils/groupTimelineCanonicalColors';
import { subscribeGroupTimelineColors } from '../utils/groupTimelineColorsSettings';
import {
  applySavedViewToHub,
  buildSavedViewOutlineFromHubState,
  readLastAppliedSavedViewId,
  storeLastAppliedSavedViewId,
  subscribeGroupTimelineSavedViewsForUser,
  type GroupTimelineSavedView,
} from '../utils/groupTimelineSavedViews';
import '../components/pm/GanttTimelineSplit.css';
import GanttTimelineSyncChip, { type GanttTimelineSyncChipHandle } from '../components/pm/GanttTimelineSyncChip';
import {
  useGanttTimelineCollaboration,
  type GanttTimelineCollaborationMode,
} from '../hooks/useGanttTimelineCollaboration';
import { GANTT_COMPARE_PIN_LIMIT } from '../utils/ganttComparePins';
import {
  buildChildTasksByParent,
  defaultSubtaskDateRange,
  migrateLegacySubtasksForParent,
  tasksNeedingSubtaskMigration,
} from '../utils/subtaskMigration';
import { subtaskStructurePayload } from '../utils/subtaskStructures';
import { parentRollupPatch, parentRollupPatchAfterChildChange, rollupDatesFromChildren, sameRollupDates } from '../utils/subtaskDateRollup';
import {
  cancelPendingTaskRemindersForTask,
  cancelTaskReminder,
  createTaskReminder,
  scheduleTaskReminderFlush,
  subscribeTaskReminders,
} from '../utils/taskReminderApi';
import { loadProposalStructures, type StructureRef } from '../utils/proposalStructureApi';
import type { StructureFilterValue } from '../utils/ganttSubtaskRows';
import DependencyDateCommitModal, { type DependencyCommitMode } from '../components/pm/DependencyDateCommitModal';
import CompleteTaskActualsModal from '../components/pm/CompleteTaskActualsModal';
import { resolveTaskCompletionPrompt } from '../utils/taskCompletionPrompt';
import {
  mergeTaskFieldOverride,
  pruneTaskFieldOverrides,
  type TaskFieldOverride,
} from '../utils/taskFieldOverrides';
import PmToastStack, { type PmToastItem, type PmToastKind } from '../components/pm/PmToast';
import ConvertToProjectModal from '../components/projects/ConvertToProjectModal';
import OnboardingModal from '../components/pm/OnboardingModal';
import AdvancedFeaturesMenu from '../components/pm/AdvancedFeaturesMenu';
import RecordMoveReasonPopup from '../components/pm/RecordMoveReasonPopup';
import LoadingScreen from '../components/ui/LoadingScreen';
import { subscribeToReadReceipts, markTaskAsRead, markGroupAsRead, markArchitectDiscussionAsRead, markProjectFilesSeen, ReadReceiptMap } from '../utils/taskReadReceipts';
import {
  collectGroupCommentContext,
  buildGroupContextForTask,
  buildGroupCommentKey,
  buildGroupMemberTasks,
  aggregateGroupComments,
  groupWithLatestComment,
  groupHasUnread,
  normalizeGroupName,
  type GroupCommentContext,
} from '../utils/groupComments';
import {
  markAnnotationAsRead,
  pdfAnnotationMentionsUserDeep,
} from '../utils/pdfAnnotationService';
import { resolveAnnotationViewerOpenTarget } from '../utils/projectFileService';
import { subscribeProjectMeetingNotes } from '../utils/projectMeetingNotesService';
import { subscribeLeadCommsForProject } from '../utils/leadCommsService';
import { subscribeProjectMeetings } from '../utils/projectMeetingsService';
import { filterMeetingsForTimeline } from '../utils/projectMeetingsTimeline';
import { buildProjectHubUnreadListItems, displayFileNameFromAnnotation, fileKindFromAnnotation, type ProjectHubUnreadListItem } from '../utils/projectHubUnreadItems';
import { ProjectHubUnreadPopover } from '../components/pm/ProjectHubUnreadPopover';
import { subscribeToSettings, AppSettings } from '../utils/appSettings';
import { LayoutList, GanttChart as GanttIcon, Plus, CheckCircle2, Trash2, AlertTriangle, FileCheck, ChevronRight, ClipboardCheck, Loader2, Paperclip, UserCircle, Mail, Phone, Flag, User, MapPin, Eye, EyeOff, Building2, GitBranch, PenLine, Lock, Unlock, MessageSquare, Clock, ListOrdered, FileText, ClipboardList, LayoutGrid, Search, FolderKanban, ListTodo, Calculator, X, Check, FlaskConical, GitCompare, Link2, HardHat } from 'lucide-react';
import HubQuickSearch from '../components/search/HubQuickSearch';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchProjectHub } from '../utils/hubSearch/searchProjectHub';
import { HubSearchResult } from '../types/hubSearch';
import ProjectHubAddressFields, { type ProjectHubAddressFieldsValue } from '../components/projects/ProjectHubAddressFields';
import CompanyBillingAddressFields from '../components/projects/CompanyBillingAddressFields';
import {
  formatLocation,
  hasRecordedProjectSiteAddress,
  projectSiteAddressDraftsEqual,
  resolveProjectSiteAddressDraft,
  setProjectAddressEverywhere,
} from '../utils/projectAddressService';
import {
  getAssignedDesigner,
  getExternalArchitectName,
  listDesigners,
} from '../utils/dealDesignerArchitect';
import { getAssignedArchitectUids, listArchitects, listAssignableArchitects } from '../utils/architectProjectAccess';
import { NameProjectWizard } from '../components/projects/NameProjectWizard';
import { ProjectDealTeamFields, type ProjectDealTeamFieldsValue } from '../components/projects/ProjectDealTeamFields';
import {
  defaultClientLabelFromLead,
  defaultDescriptorFromProjectType,
  getTaskDetailPanelProjectContext,
  getProjectDisplayName,
  parseLegacyTitle,
} from '../utils/projectName';
import ProjectFiles, { type ProjectFilesView } from '../components/pm/ProjectFiles';
import CrewSlackHubPanel from '../components/projects/CrewSlackHubPanel';
import ProjectReport from '../components/pm/ProjectReport';
import ArchEngineerWorkQueueModal from '../components/pm/ArchEngineerWorkQueueModal';
import { useArchEngineerWorkQueues } from '../hooks/useArchEngineerWorkQueues';
import { engineeringFirmsWithCrmEngineers } from '../utils/eorEngineerOptions';
import { useInstallWeatherForecast } from '../hooks/useInstallWeatherForecast';
import { listHorizonYmdsInTaskRange } from '../utils/installWeather';
import { ProjectUploadModalProvider } from '../components/pm/ProjectUploadModalProvider';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import '../components/pm/pm-shared.css';
import './ProjectHub.css';
import { createTaskAssignmentNotification } from '../utils/taskAssignmentNotification';
import { ARCH_ENGINEER_WORK_QUEUE_DOC_ID } from '../utils/archEngineerWorkQueue';
import {
  filterTimberProjectsForArchEngineerHub,
  mergeArchEngineerHubProjectsWithPdfMentions,
  engineerIsInvolvedInProject,
  engineerIsInvolvedInProjectForFileComment,
} from '../utils/archEngineerProjectHubAccess';
import { architectIsInvolvedInProject, architectIsInvolvedInProjectForFileComment } from '../utils/architectProjectInvolvement';
import { userIsTaskCommentNotifyTarget, buildProjectHubCommentDeepLinkPath } from '../utils/commentNotificationAccess';
import { buildScheduleMoveActivityEntry, recordTaskActivityChanges } from '../utils/taskActivityLogger';
import {
  buildTimelineDriftGhosts,
  canonicalizeTasksForTimelineDrift,
  isTimelineDriftFocusTask,
  mapTimelineDriftGhostsToRows,
  TIMELINE_DRIFT_CAUSE_LABELS,
  type TimelineDriftCause,
} from '../utils/timelineDrift';
import {
  scheduleAfterMove,
  scheduleShiftConnectedComponent,
  diffDateChanges,
  enforceAllFsDependencies,
  impliedLagForNewDependencyEdge,
  CREATE_DEP_IMPLIED_LAG_MOVE_THRESHOLD,
  CREATE_DEP_LARGE_GAP_LAG_BUSINESS_DAYS,
  reinferFsLagsForDates,
  sameLocalCalendarDay,
  isTaskLocked,
  type TaskDateState,
  type ScheduleLockViolation,
} from '../utils/taskSchedule';
import {
  fmtDay,
  ganttScheduleDebugGroup,
  ganttScheduleDebugLog,
  ganttScheduleDebugWarn,
  installGanttScheduleDebugApi,
  isGanttScheduleDebugEnabled,
  logPersistScheduleSnapshot,
} from '../utils/ganttScheduleDebug';
import { formatGanttTaskIdForLog } from '../utils/ganttTaskIds';
import { applySchedulePersistToGanttOverrides } from '../utils/ganttDateOverrideReconcile';
import {
  dependencyPayload,
  getDependencyEdges,
  taskParticipatesInDependencyGraph,
  wouldAddingEdgeCreateCycle,
} from '../utils/taskDependencies';
import type { CommitTaskDatesHint } from '../utils/taskDateCommit';
import { decideTaskDateCommit, normalizeCommitTaskDatesHint } from '../utils/taskDateCommit';
import {
  mergePmDateReviewIntoOnboardingPatch,
  mergePmDateReviewIntoTaskPatch,
} from '../utils/pmDateReview';
import {
  isMilestoneBridgeId,
  milestoneBridgeId,
  milestoneToPseudoTask,
  parseMilestoneBridgeId,
} from '../utils/milestoneSchedule';
import {
  TIMELINE_UNDO_MAX_STACK,
  captureTimelineUndoSnapshot,
  restoreTimelineUndoSnapshot,
  type TimelineUndoSnapshot,
} from '../utils/timelineUndo';
import {
  projectHubBillingPath,
  projectHubViewModeFromTabParam,
  projectHubViewModeHotkeyLabel,
  useProjectHubBillingHotkey,
  useProjectHubExternalNavHotkeys,
  useProjectHubLatestCommentHotkey,
  shouldHandleProjectHubLetterHotkey,
  useProjectHubViewModeHotkeys,
} from '../hooks/useProjectHubViewModeHotkeys';
import {
  allProjectsFamilyHotkeyLabel,
  useAllProjectsHubHotkeys,
} from '../hooks/useAllProjectsHubHotkeys';

const PdfViewerModal = lazy(() => import('../components/pdf/PdfViewerModal'));
const ImageViewerModal = lazy(() => import('../components/pdf/ImageViewerModal'));
const CsvViewerModal = lazy(() => import('../components/csv/CsvViewerModal'));

type ViewMode = 'table' | 'gantt' | 'files' | 'suppliers' | 'changeOrders' | 'crew';

const ProjectHub: React.FC = () => {
  const { user } = useAuth();
  const userDesignations = user?.designations;
  const canAccessSuppliersTabBase = canAccessProjectHubSuppliersTab(user?.role);
  const canAccessChangeOrdersTabBase = canAccessProjectHubChangeOrdersTab(user?.role, userDesignations);
  const canViewTimeResultsBase = canViewProjectTimeResults(user?.role);
  const canLogTaskTimeBase = canLogProjectTaskTime(user?.role);
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    timberProjects: crmTimberProjects,
    projectTasks: crmProjectTasks,
    milestones: crmMilestones,
    taskGroups: crmTaskGroups,
    taskComments: crmTaskComments,
    createProjectTask, updateProjectTask, deleteProjectTask,
    createTaskGroup, updateTaskGroup, deleteTaskGroup,
    createTaskActivity,
    updateTimberProject, deleteTimberProject, createPreconProject,
    createProjectTimeEntry, updateProjectTimeEntry,
    projectTimeEntries: crmProjectTimeEntries,
    enhancedDeals, crmUsers, architectInvites, loading, engagementLetters, leads, updateEnhancedDeal,
    projectOnboardings: crmProjectOnboardings,
    updateOnboardingItem: updateOnboardingItemCrm, deleteOnboardingItem,
    updateMilestone,
    rfqProjects, rfqPackages, rfqSubmissions, supplierOrgs, updateRfqProject,
    productionCommentAccess,
    persistProductionCommentAccessForDeal,
  } = useCRM();

  /* ── Supplier hub mode ─────────────────────────────────────────────────────
   * Supplier accounts (and the staff "View as supplier" preview via
   * `?viewAs=<orgId>`) get the real Project Hub, scoped to Production groups
   * on projects where the org has an awarded quote or is assigned on a hub
   * task (`supplierOrgIds`). Supplier accounts arrive
   * pre-scoped from the `supplierProductionSchedule` callable (CRM hydration);
   * staff preview scopes the full CRM arrays client-side.
   */
  const isSupplierUserRole = user?.role === 'supplier';
  const canPreviewAsSupplier = user?.role === 'admin' || user?.role === 'employee';
  const supplierPreviewOrgId =
    !isSupplierUserRole && canPreviewAsSupplier
      ? searchParams.get('viewAs')?.trim() || null
      : null;
  const supplierPreviewRole = parseSupplierPortalPreviewRole(searchParams.get('as'));
  const supplierHubMode = isSupplierUserRole || !!supplierPreviewOrgId;
  const supplierViewerOrgId =
    supplierPreviewOrgId
    || (isSupplierUserRole ? user?.supplierOrgId?.trim() || null : null);
  const isArchitectUserRole = isArchitectRole(user?.role);
  const architectPreviewUid =
    !isArchitectUserRole && canPreviewPartnerPortals(user?.role)
      ? parseArchitectPreviewUid(searchParams)
      : null;
  const architectHubMode = isArchitectUserRole || !!architectPreviewUid;
  const architectViewAsSuffix = architectPreviewUid
    ? buildArchitectPortalPreviewSearch(architectPreviewUid)
    : '';
  const architectScopeUid = isArchitectUserRole ? user?.id : architectPreviewUid;
  const supplierViewAsSuffix = supplierPreviewOrgId
    ? buildSupplierPortalPreviewSearch({
        orgId: supplierPreviewOrgId,
        role: supplierPreviewRole,
      })
    : '';
  const partnerHubMode = supplierHubMode || architectHubMode;
  const hubSurfaceRole = architectHubMode
    ? 'architect'
    : supplierHubMode
      ? 'supplier'
      : user?.role;
  const hubSurfaceDesignations =
    !partnerHubMode && hubSurfaceRole === user?.role ? userDesignations : undefined;
  const hideCommercialSurfaces = partnerHubMode || isDesignPartnerOrEngineerRole(user?.role);
  const hidePaymentFlags =
    supplierHubMode
    || architectHubMode
    || isDesignerRole(user?.role)
    || user?.role === 'engineer';
  const canAccessSuppliersTab = canAccessSuppliersTabBase && !partnerHubMode;
  const canAccessChangeOrdersTab = canAccessChangeOrdersTabBase && !partnerHubMode;
  const canViewTimeResults = canViewTimeResultsBase && !partnerHubMode;
  const canLogTaskTime = canLogTaskTimeBase && !partnerHubMode;
  const canUseSimulateUnderContract =
    canSimulateUnderContract(user?.role, userDesignations) && !partnerHubMode;
  const {
    simulatedProjectIds: simulateUcProjectIds,
    simulatedSchedules: simulateUcSchedules,
    saveSimulatedItemSchedule,
    saveSimulatedProjectSchedule,
    hasSimulations: simulateUnderContractEnabled,
    toggleProject: toggleSimulateUnderContractProject,
    clearAll: clearSimulateUnderContractProjects,
    persistError: simulateUcPersistError,
    templateItems: simulateUcTemplateItems,
    templateLoadError: simulateUcTemplateError,
  } = useSimulateUnderContract({
    allowed: canUseSimulateUnderContract,
    userId: user?.id,
    userName: user?.name || user?.email || null,
  });
  const [showSimulateUcMenu, setShowSimulateUcMenu] = useState(false);
  const [simulateUcQuery, setSimulateUcQuery] = useState('');
  const simulateUcMenuRef = useRef<HTMLDivElement>(null);

  const supplierSubmissionsByPackage = useMemo(() => {
    const map = new Map<string, RfqSubmission[]>();
    if (!supplierPreviewOrgId) return map;
    rfqSubmissions.forEach(s => {
      const list = map.get(s.rfqPackageId) || [];
      list.push(s);
      map.set(s.rfqPackageId, list);
    });
    return map;
  }, [supplierPreviewOrgId, rfqSubmissions]);

  const supplierHubScope = useMemo(() => {
    if (!supplierHubMode) return null;
    const base = supplierPreviewOrgId
      ? scopeProductionScheduleToSupplier({
          orgId: supplierPreviewOrgId,
          rfqProjects,
          rfqPackages,
          submissionsByPackage: supplierSubmissionsByPackage,
          timberProjects: crmTimberProjects,
          projectTasks: crmProjectTasks,
          taskGroups: crmTaskGroups,
          // No onboardings here: the hub mirrors checklist rows itself from the
          // scoped `projectOnboardings` below (avoids double-mirroring).
        })
      : { projects: crmTimberProjects, tasks: crmProjectTasks, groups: crmTaskGroups };
    const productionGroups = base.groups.filter(g => isProductionGroupName(g.name || ''));
    const productionGroupIds = new Set(productionGroups.map(g => g.id));
    const projectIds = new Set(
      base.projects.map(p => p.id).filter((id): id is string => !!id),
    );
    // Preview: keep only production checklist items for the scoped projects so
    // the hub's onboarding mirror stays inside the Production family. Supplier
    // accounts stream no onboardings (virtual rows already come pre-mirrored
    // from the callable inside `projectTasks`).
    const onboardings = supplierPreviewOrgId
      ? crmProjectOnboardings
          .filter(ob => !!ob.projectId && projectIds.has(ob.projectId))
          .map(ob => ({
            ...ob,
            items: (ob.items ?? []).filter(
              it =>
                (it.taskGroupId && productionGroupIds.has(it.taskGroupId))
                || it.department === 'production',
            ),
          }))
      : crmProjectOnboardings;
    return {
      projects: base.projects,
      groups: productionGroups,
      tasks: base.tasks.filter(t => !!t.groupId && productionGroupIds.has(t.groupId)),
      onboardings,
    };
  }, [
    supplierHubMode,
    supplierPreviewOrgId,
    rfqProjects,
    rfqPackages,
    supplierSubmissionsByPackage,
    crmTimberProjects,
    crmProjectTasks,
    crmTaskGroups,
    crmProjectOnboardings,
  ]);

  const architectHubScope = useMemo(() => {
    if (!architectHubMode || !architectScopeUid) return null;
    const dealsById = new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d]));
    const projects = crmTimberProjects.filter((project) => {
      const deal = project.dealId ? dealsById.get(project.dealId) : undefined;
      return projectHasArchitectUid(project, deal, architectScopeUid);
    });
    const projectIds = new Set(projects.map((p) => p.id).filter((id): id is string => !!id));
    const groups = crmTaskGroups.filter(
      (g) => g.projectId && projectIds.has(g.projectId) && isDesignGroupName(g.name),
    );
    const groupIds = new Set(groups.map((g) => g.id).filter(Boolean));
    return {
      projects,
      groups,
      tasks: crmProjectTasks.filter((t) => t.groupId && groupIds.has(t.groupId)),
      onboardings: [],
    };
  }, [
    architectHubMode,
    architectScopeUid,
    enhancedDeals,
    crmTimberProjects,
    crmTaskGroups,
    crmProjectTasks,
    crmProjectOnboardings,
  ]);

  const timberProjects = supplierHubScope
    ? supplierHubScope.projects
    : architectHubScope
      ? architectHubScope.projects
      : crmTimberProjects;
  const userCanViewAllProjectsTimeReport = useMemo(() => {
    const me = crmUsers.find(u => u.uid === user?.id);
    return canViewAllProjectsTimeReport({
      authRole: user?.role,
      designations: user?.designations ?? me?.designations,
    });
  }, [user?.role, user?.id, user?.designations, crmUsers]);
  const userCanEditPreconBillable = useMemo(() => {
    const me = crmUsers.find(u => u.uid === user?.id);
    return canEditPreconBillableStatus({
      authRole: user?.role,
      designations: user?.designations ?? me?.designations,
    });
  }, [user?.role, user?.id, user?.designations, crmUsers]);
  const userCanPromoteToPreconstruction = useMemo(() => {
    const me = crmUsers.find(u => u.uid === user?.id);
    return canPromoteProjectToPreconstruction({
      authRole: user?.role,
      designations: user?.designations ?? me?.designations,
    });
  }, [user?.role, user?.id, user?.designations, crmUsers]);
  const { setActiveProject } = useProjectTimeContext();

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(urlProjectId || null);
  const [projectsSidebarOpen, setProjectsSidebarOpen] = useState(true);
  const [renamingProject, setRenamingProject] = useState(false);
  const [renameTeamDraft, setRenameTeamDraft] = useState<ProjectDealTeamFieldsValue>({
    designerUid: '',
    architectName: '',
    architectUids: [],
    engineerFirmId: '',
    engineerId: '',
  });
  const renameTeamBaselineRef = useRef<ProjectDealTeamFieldsValue>({
    designerUid: '',
    architectName: '',
    architectUids: [],
    engineerFirmId: '',
    engineerId: '',
  });
  const [renameAddressDraft, setRenameAddressDraft] = useState<ProjectHubAddressFieldsValue>({
    address: { street: '', city: '', state: '', zipCode: '', country: 'United States' },
    location: '',
  });
  const renameAddressBaselineRef = useRef<ProjectHubAddressFieldsValue>({
    address: { street: '', city: '', state: '', zipCode: '', country: 'United States' },
    location: '',
  });
  const renameAddressDraftRef = useRef(renameAddressDraft);
  const renameModalInitializedRef = useRef(false);

  useEffect(() => {
    setSelectedProjectId(urlProjectId || null);
  }, [urlProjectId]);

  useEffect(() => {
    try {
      if (localStorage.getItem('projectHubSidebarOpen') === 'false') {
        setProjectsSidebarOpen(false);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    installGanttScheduleDebugApi();
  }, []);

  /** Email / links / billing-card hotkeys: ?tab=table|files|suppliers|changeOrders|gantt */
  useEffect(() => {
    if (!urlProjectId) return;
    const mode = projectHubViewModeFromTabParam(searchParams.get('tab'));
    if (!mode) return;
    if (mode === 'suppliers' && !canAccessSuppliersTab) return;
    if (mode === 'changeOrders' && !canAccessChangeOrdersTab) return;
    if (architectHubMode && mode !== 'table' && mode !== 'files') return;
    if (supplierHubMode && mode !== 'table' && mode !== 'gantt' && mode !== 'files') return;
    setViewMode(mode);
  }, [urlProjectId, searchParams, canAccessSuppliersTab, canAccessChangeOrdersTab, supplierHubMode, architectHubMode]);

  const filesDeepLink = React.useMemo(() => {
    if (searchParams.get('tab') !== 'files') return null;
    const file = searchParams.get('file');
    if (!file) return null;
    const pageRaw = searchParams.get('page');
    const pageNumber = pageRaw ? parseInt(pageRaw, 10) : undefined;
    return {
      documentId: file,
      annotationId: searchParams.get('comment') || undefined,
      pageNumber: pageNumber !== undefined && Number.isFinite(pageNumber) ? pageNumber : undefined,
    };
  }, [searchParams]);

  const filesInitialTrackId = React.useMemo(() => {
    if (searchParams.get('tab') !== 'files') return null;
    const t = searchParams.get('track') || searchParams.get('stream');
    return t && t.trim() ? t.trim() : null;
  }, [searchParams]);

  /** `?tab=files&view=folders&folder=admin/project-brief` — from `#files-…` comment chips. */
  const filesInitialView = React.useMemo((): ProjectFilesView | null => {
    if (searchParams.get('tab') !== 'files') return null;
    const v = searchParams.get('view');
    return v === 'deliverables' || v === 'submittals' || v === 'rfqs' || v === 'folders' ? v : null;
  }, [searchParams]);
  const filesInitialFolderPath = React.useMemo(() => {
    if (searchParams.get('tab') !== 'files') return null;
    const f = searchParams.get('folder');
    return f && f.trim() ? f.trim() : null;
  }, [searchParams]);
  const clearFilesViewQuery = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('view');
        next.delete('folder');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const clearFilesDeepLinkParams = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        next.delete('file');
        next.delete('comment');
        next.delete('page');
        next.delete('stream');
        next.delete('track');
        next.delete('view');
        next.delete('folder');
        next.delete('request');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const clearFilesTrackQuery = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('stream');
        next.delete('track');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  /** `?tab=files&view=rfqs&request=<releaseId,releaseId>` — Suppliers tab hand-off into the request wizard. */
  const filesInitialRequestReleaseIds = React.useMemo((): string[] | null => {
    if (searchParams.get('tab') !== 'files') return null;
    const raw = searchParams.get('request');
    if (raw === null) return null;
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  }, [searchParams]);
  const clearFilesRequestQuery = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('request');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const [viewMode, setViewMode] = useState<ViewMode>(() => (
    !urlProjectId && projectHubViewModeFromTabParam(searchParams.get('tab')) === 'gantt'
      ? 'gantt'
      : 'table'
  ));
  const [groupTimelineFamilyState, setGroupTimelineFamily] = useState<GroupTimelineFamily>(
    () => parseGroupTimelineFamily(searchParams.get('group')) ?? readStoredGroupTimelineFamily(),
  );
  const [groupTimelineColorModeState, setGroupTimelineColorMode] = useState<GroupTimelineColorMode>(
    readStoredGroupTimelineColorMode,
  );
  const [groupTimelineLayoutState, setGroupTimelineLayout] = useState<GroupTimelineLayoutMode>(
    readStoredGroupTimelineLayout,
  );
  const [groupTimelineSortState, setGroupTimelineSort] = useState<GroupTimelineSortMode>(
    readStoredGroupTimelineSort,
  );
  const [groupTimelineExtraTasks, setGroupTimelineExtraTasks] = useState<GroupTimelineExtraTasksByFamily>(
    readStoredGroupTimelineExtraTasks,
  );
  const [groupTimelineFilter, setGroupTimelineFilter] = useState<GroupTimelineDetailFilter>(
    readStoredGroupTimelineFilter,
  );
  const [canonicalColorEntries, setCanonicalColorEntries] = useState<GroupTimelineColorEntry[]>(() =>
    seedGroupTimelineColorEntries(),
  );
  const [savedTimelineViews, setSavedTimelineViews] = useState<GroupTimelineSavedView[]>([]);
  const [activeSavedTimelineViewId, setActiveSavedTimelineViewId] = useState<string | null>(() =>
    readLastAppliedSavedViewId(),
  );
  const ganttOutlineApiRef = useRef<GanttAllProjectsApi | null>(null);
  const ganttUserSyncChipRef = useRef<GanttTimelineSyncChipHandle | null>(null);
  const ganttSplitApiRef = useRef<GanttAllProjectsApi | null>(null);
  const ganttSplitStageRef = useRef<HTMLDivElement | null>(null);
  const splitLeaderRef = useRef<'left' | 'right' | null>(null);
  const toggleSplitRef = useRef<() => void>(() => {});
  const toggleMatchRef = useRef<() => void>(() => {});
  const matchingOutlineRef = useRef(false);
  const splitOutlineSeededRef = useRef(false);
  const splitApplyTimerRef = useRef<number | null>(null);
  const splitFrameRef = useRef<number | null>(null);
  const pendingSplitViewportRef = useRef<GanttSyncedViewport | null>(null);
  const [ganttSplitOpen, setGanttSplitOpen] = useState(false);
  const [ganttSplitSync, setGanttSplitSync] = useState(true);
  const [ganttSplitRatio, setGanttSplitRatio] = useState(0.5);
  const [ganttSplitFamily, setGanttSplitFamily] = useState<GroupTimelineFamily>('all');
  const [ganttSplitSeedZoom, setGanttSplitSeedZoom] = useState<GanttSyncedViewport['zoom']>('day');
  const [ganttSplitLayout, setGanttSplitLayout] = useState<GroupTimelineLayoutMode>('project');
  const [ganttSplitSort, setGanttSplitSort] = useState<GroupTimelineSortMode>('date');
  const [ganttSplitColorMode, setGanttSplitColorMode] = useState<GroupTimelineColorMode>('project');
  const [ganttSplitFilter, setGanttSplitFilter] = useState<GroupTimelineDetailFilter>(EMPTY_GROUP_TIMELINE_FILTER);
  const [ganttSplitExtraTasks, setGanttSplitExtraTasks] = useState<GroupTimelineExtraTasksByFamily>({});
  const [ganttSplitPinnedCount, setGanttSplitPinnedCount] = useState(0);
  const [ganttPinnedCount, setGanttPinnedCount] = useState(0);

  // Supplier hub is locked to the Production family with the plain project
  // layout — the family chips and view/color controls stay staff-only.
  const groupTimelineFamily: GroupTimelineFamily = supplierHubMode
    ? 'production'
    : architectHubMode
      ? 'all'
      : groupTimelineFamilyState;
  const groupTimelineColorMode: GroupTimelineColorMode = supplierHubMode ? 'project' : groupTimelineColorModeState;
  const groupTimelineLayout: GroupTimelineLayoutMode = supplierHubMode ? 'project' : groupTimelineLayoutState;
  const groupTimelineSort: GroupTimelineSortMode = supplierHubMode ? 'date' : groupTimelineSortState;

  /** Aggregate list only supports table + group timeline; reset Files/Suppliers/COs when leaving a project. */
  useEffect(() => {
    if (selectedProjectId === null && viewMode !== 'table' && viewMode !== 'gantt') {
      setViewMode('table');
    }
  }, [selectedProjectId, viewMode]);

  const setGroupTimelineSearchParams = useCallback((family: GroupTimelineFamily | null) => {
    setSearchParams(
      (prev) => {
        if (family) {
          if (prev.get('tab') === 'gantt' && prev.get('group') === family) return prev;
        } else if (!prev.get('tab') && !prev.get('group')) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        if (family) {
          next.set('tab', 'gantt');
          next.set('group', family);
        } else {
          next.delete('tab');
          next.delete('group');
        }
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  /** `/project-hub?tab=gantt&group=production` — all-projects group timeline deep link. */
  useEffect(() => {
    if (urlProjectId || architectHubMode) return;
    const mode = projectHubViewModeFromTabParam(searchParams.get('tab'));
    if (mode !== 'gantt') return;
    setViewMode('gantt');
    const family = parseGroupTimelineFamily(searchParams.get('group'));
    if (family) {
      setGroupTimelineFamily(family);
      storeGroupTimelineFamily(family);
    }
  }, [urlProjectId, searchParams, architectHubMode]);

  const toggleAllProjectsTimeline = useCallback(() => {
    if (architectHubMode) return;
    if (viewMode === 'gantt') {
      setViewMode('table');
      setGroupTimelineSearchParams(null);
    } else {
      setViewMode('gantt');
      storeGroupTimelineFamily(groupTimelineFamily);
      setGroupTimelineSearchParams(groupTimelineFamily);
    }
  }, [architectHubMode, viewMode, groupTimelineFamily, setGroupTimelineSearchParams]);

  const selectAllProjectsTimelineFamily = useCallback((family: GroupTimelineFamily) => {
    setGroupTimelineFamily(family);
    storeGroupTimelineFamily(family);
    setGroupTimelineSearchParams(family);
    if (viewMode !== 'gantt') setViewMode('gantt');
  }, [viewMode, setGroupTimelineSearchParams]);

  useEffect(() => {
    if (selectedProjectId !== null || viewMode !== 'gantt') return;
    setGroupTimelineSearchParams(groupTimelineFamily);
  }, [selectedProjectId, viewMode, groupTimelineFamily, setGroupTimelineSearchParams]);

  const userCanSubmitArchitectStandup = canSubmitArchitectStandup(user?.role);
  const userCanViewArchitectStandupReports = useMemo(() => {
    const me = crmUsers.find(u => u.uid === user?.id);
    return canViewArchitectStandupReports({
      authRole: user?.role,
      designations: user?.designations ?? me?.designations,
    });
  }, [user?.role, user?.id, user?.designations, crmUsers]);
  const showAllProjectsRollup = viewMode === 'table' && !selectedProjectId;
  const architectStandupLatestByProject = useArchitectStandupLatestByProject(
    showAllProjectsRollup && userCanViewArchitectStandupReports,
  );

  useEffect(() => {
    if (!canAccessSuppliersTab && viewMode === 'suppliers') {
      setViewMode('table');
    }
  }, [canAccessSuppliersTab, viewMode]);

  useEffect(() => {
    if (!canAccessChangeOrdersTab && viewMode === 'changeOrders') {
      setViewMode('table');
    }
  }, [canAccessChangeOrdersTab, viewMode]);

  /** Supplier hub has Table, Files, and Timeline. Architects only get Table and Files. */
  useEffect(() => {
    if (architectHubMode && viewMode !== 'table' && viewMode !== 'files') {
      setViewMode('table');
      return;
    }
    if (supplierHubMode && viewMode !== 'table' && viewMode !== 'gantt' && viewMode !== 'files') {
      setViewMode('table');
    }
  }, [supplierHubMode, architectHubMode, viewMode]);

  useProjectHubViewModeHotkeys({
    selectedProjectId,
    canAccessSuppliersTab,
    canAccessChangeOrdersTab,
    onViewModeChange: (mode) => {
      if (architectHubMode && mode === 'gantt') return;
      setViewMode(mode);
    },
  });

  const [showTimeReport, setShowTimeReport] = useState(false);
  /** Timeline: show manual critical-path overlay (amber chain); off by default. */
  const [showCriticalPathOverlay, setShowCriticalPathOverlay] = useState(false);
  const [showTimelineDrift, setShowTimelineDrift] = useState(false);
  /** Advanced → Show meetings (same in-memory persistence as timeline drift). */
  const [showMeetings, setShowMeetings] = useState(true);
  const [moveReasonPrompt, setMoveReasonPrompt] = useState<{ activityId: string; taskTitle: string } | null>(null);
  const [timelineDriftLogs, setTimelineDriftLogs] = useState<TaskActivityLog[]>([]);
  const [timelineDriftCauseFilter, setTimelineDriftCauseFilter] = useState<Set<TimelineDriftCause> | null>(null);
  useEffect(() => {
    setShowCriticalPathOverlay(false);
    setMoveReasonPrompt(null);
  }, [selectedProjectId]);
  useEffect(() => {
    setShowTimeReport(false);
  }, [selectedProjectId]);
  const [showWorkQueueModal, setShowWorkQueueModal] = useState(false);
  const aeWorkQueuesByUser = useArchEngineerWorkQueues();
  useEffect(() => {
    setShowWorkQueueModal(false);
  }, [selectedProjectId]);
  const [showClosedProjects, setShowClosedProjects] = useState(false);
  const [showQuickSearch, setShowQuickSearch] = useState(false);
  useAllProjectsHubHotkeys({
    enabled: selectedProjectId === null,
    viewMode,
    blocked: showWorkQueueModal || showQuickSearch || showTimeReport,
    onToggleTimeline: architectHubMode ? () => undefined : toggleAllProjectsTimeline,
    onOpenPriority: () => {
      if (architectHubMode || supplierHubMode) return;
      setShowWorkQueueModal(true);
    },
    onSelectFamily: (family) => {
      selectAllProjectsTimelineFamily(family);
      if (ganttSplitOpen && ganttSplitSync) setGanttSplitFamily(family);
    },
    onToggleSplit:
      !architectHubMode && !supplierHubMode ? () => toggleSplitRef.current() : undefined,
    onToggleMatch: () => toggleMatchRef.current(),
    onClearFilters:
      !architectHubMode && !supplierHubMode
        ? () => {
            const empty = { ...EMPTY_GROUP_TIMELINE_FILTER };
            setGroupTimelineFilter(empty);
            storeGroupTimelineFilter(empty);
            if (ganttSplitOpen) setGanttSplitFilter({ ...EMPTY_GROUP_TIMELINE_FILTER });
          }
        : undefined,
  });

  /* ── Progressive data loading ─────────────────────────────────────────────
   * Landing view renders from projectHubRollups (one small doc per project);
   * tasks/comments/annotations stream in only for the selected project's deal
   * family; org-wide CRM collections start only for views that span projects.
   */
  const isArchEngineerRole = isDesignerRole(user?.role) || user?.role === 'engineer';

  const scopedDealId = useMemo(() => {
    if (!selectedProjectId) return null;
    return timberProjects.find(p => p.id === selectedProjectId)?.dealId?.trim() || null;
  }, [selectedProjectId, timberProjects]);
  const scopedSiblingProjectIds = useMemo(() => {
    if (!selectedProjectId) return [];
    if (!scopedDealId) return [selectedProjectId];
    return timberProjects.filter(p => p.id && p.dealId?.trim() === scopedDealId).map(p => p.id!);
  }, [selectedProjectId, scopedDealId, timberProjects]);
  // Real supplier accounts cannot subscribe to deal/project comment streams (rules);
  // staff "View as supplier" preview still can — keep those listeners so posts refresh.
  // Tasks/groups stay production-scoped via supplierHubScope below.
  const scopedData = useProjectHubProjectData(
    isSupplierUserRole ? null : selectedProjectId,
    isSupplierUserRole ? null : scopedDealId,
    isSupplierUserRole ? [] : scopedSiblingProjectIds,
    { decisionOnly: architectHubMode },
  );

  const [hubRollups, setHubRollups] = useState<Map<string, ProjectHubRollup>>(() => new Map());
  useEffect(() => {
    if (isSupplierUserRole) return undefined; // rules block rollup reads for suppliers
    return subscribeToProjectHubRollups(setHubRollups);
  }, [isSupplierUserRole]);

  const orgWideNeeds = useMemo(() => {
    const needs: LazyCrmCollection[] = [];
    // Architect/engineer sidebar visibility scans involvement across all projects;
    // the priority work-queue modal and the all-projects group timeline do too.
    // Simulate-UC also needs org-wide groups/onboardings to overlay planning tasks.
    if (
      isArchEngineerRole
      || showWorkQueueModal
      || (!selectedProjectId && viewMode === 'gantt')
      || simulateUnderContractEnabled
    ) {
      needs.push('projectTasks', 'taskGroups', 'projectOnboardings');
    }
    // Supplier preview scopes org-wide arrays client-side (supplier accounts
    // no-op here — their listeners are hydrated from the callable instead).
    if (supplierHubMode) needs.push('projectTasks', 'taskGroups', 'projectOnboardings');
    if (isArchEngineerRole) needs.push('taskComments');
    if (!selectedProjectId && showTimeReport) needs.push('projectTimeEntries');
    return needs;
  }, [
    isArchEngineerRole,
    showWorkQueueModal,
    selectedProjectId,
    viewMode,
    showTimeReport,
    supplierHubMode,
    simulateUnderContractEnabled,
  ]);
  const orgWideDataReady = useCRMCollections(orgWideNeeds);

  // Inside a selected project, all Hub state comes from the scoped subscriptions
  // (except real suppliers, who use callable-hydrated arrays / supplierHubScope).
  const scopedActive = !isSupplierUserRole && !isArchitectUserRole && !!selectedProjectId && !!scopedData;
  const projectTasks = supplierHubScope
    ? supplierHubScope.tasks
    : architectHubScope
      ? architectHubScope.tasks
    : scopedActive ? scopedData!.tasks : crmProjectTasks;
  const taskGroupsRaw = supplierHubScope
    ? supplierHubScope.groups
    : architectHubScope
      ? architectHubScope.groups
    : scopedActive ? scopedData!.taskGroups : crmTaskGroups;
  const taskComments = scopedActive ? scopedData!.taskComments : crmTaskComments;
  const projectOnboardingsRaw = supplierHubScope
    ? supplierHubScope.onboardings
    : architectHubScope
      ? architectHubScope.onboardings
    : scopedActive ? scopedData!.projectOnboardings : crmProjectOnboardings;

  // Staff planning overlay: virtual UC groups + onboarding checklist rows for
  // early-funnel projects when "Simulate under contract" is on. Never written
  // to Firestore; turning simulation off drops these from the view model.
  const taskGroups = useMemo(
    () =>
      mergeTaskGroupsForPlanning(
        taskGroupsRaw,
        timberProjects,
        simulateUcProjectIds,
      ),
    [taskGroupsRaw, timberProjects, simulateUcProjectIds],
  );
  /**
   * Checklists the old simulator wrote onto estimating/preconstruction projects.
   * They are not a real under-contract list — hide them and lift any dates into
   * the planning overlay.
   */
  const simulationSeededOnboardingIds = useMemo(() => {
    const ids = new Set<string>();
    if (!canUseSimulateUnderContract || !simulateUcTemplateItems?.length) return ids;
    for (const ob of projectOnboardingsRaw) {
      if (!ob.projectId) continue;
      const project = timberProjects.find((p) => p.id === ob.projectId);
      if (!project || !isEarlyFunnelProjectPhase(project.phase)) continue;
      if (isSimulationSeededOnboarding(ob, simulateUcTemplateItems)) ids.add(ob.projectId);
    }
    return ids;
  }, [
    canUseSimulateUnderContract,
    simulateUcTemplateItems,
    projectOnboardingsRaw,
    timberProjects,
  ]);
  const persistedOnboardings = useMemo(
    () => projectOnboardingsRaw.filter(
      (ob) => !ob.projectId || !simulationSeededOnboardingIds.has(ob.projectId),
    ),
    [projectOnboardingsRaw, simulationSeededOnboardingIds],
  );
  const schedulesForPlanning = useMemo(() => {
    const merged: typeof simulateUcSchedules = { ...simulateUcSchedules };
    for (const ob of projectOnboardingsRaw) {
      if (!ob.projectId || !simulationSeededOnboardingIds.has(ob.projectId)) continue;
      const fromDoc = simulatedSchedulesFromOnboardingItems(ob.items || []);
      merged[ob.projectId] = { ...fromDoc, ...(simulateUcSchedules[ob.projectId] || {}) };
    }
    return merged;
  }, [simulateUcSchedules, projectOnboardingsRaw, simulationSeededOnboardingIds]);
  const projectOnboardings = useMemo(
    () =>
      mergeOnboardingsForPlanning(
        persistedOnboardings,
        timberProjects,
        simulateUcProjectIds,
        simulateUcTemplateItems,
        schedulesForPlanning,
      ),
    [
      persistedOnboardings,
      timberProjects,
      simulateUcProjectIds,
      simulateUcTemplateItems,
      schedulesForPlanning,
    ],
  );
  const milestones = scopedActive ? scopedData!.milestones : crmMilestones;
  const projectTimeEntries = scopedActive ? scopedData!.timeEntries : crmProjectTimeEntries;
  const getOnboardingForProject = useCallback(
    (pid: string) => projectOnboardings.find(ob => ob.projectId === pid),
    [projectOnboardings],
  );

  /** Persist checklist edits. Simulated rows keep dates in staff planning only. */
  const updateOnboardingItem = useCallback(
    async (
      projectId: string,
      itemId: string,
      updates: Partial<OnboardingItem>,
      userName: string,
    ) => {
      const persisted = persistedOnboardings.find((o) => o.projectId === projectId);
      if (!persisted) {
        const project = timberProjects.find((p) => p.id === projectId);
        if (!isSimulatingUnderContract(project, simulateUcProjectIds)) return;
        const schedule = simulatedScheduleFromItemPatch(updates);
        if (!schedule) return;
        await saveSimulatedItemSchedule(projectId, itemId, schedule);
        return;
      }
      return updateOnboardingItemCrm(projectId, itemId, updates, userName);
    },
    [
      persistedOnboardings,
      timberProjects,
      simulateUcProjectIds,
      saveSimulatedItemSchedule,
      updateOnboardingItemCrm,
    ],
  );

  const unseededSimOnboardingRef = useRef(new Set<string>());
  useEffect(() => {
    if (!canUseSimulateUnderContract || simulationSeededOnboardingIds.size === 0) return;
    const pending = projectOnboardingsRaw.filter(
      (ob) => ob.projectId
        && simulationSeededOnboardingIds.has(ob.projectId)
        && !unseededSimOnboardingRef.current.has(ob.projectId),
    );
    if (!pending.length) return;
    for (const ob of pending) unseededSimOnboardingRef.current.add(ob.projectId!);
    void (async () => {
      for (const ob of pending) {
        const projectId = ob.projectId!;
        try {
          const fromDoc = simulatedSchedulesFromOnboardingItems(ob.items || []);
          const kept = { ...fromDoc, ...(simulateUcSchedules[projectId] || {}) };
          if (Object.keys(kept).length) {
            await saveSimulatedProjectSchedule(projectId, kept);
          }
          await deleteDoc(doc(db, 'projectOnboarding', projectId));
        } catch (err) {
          unseededSimOnboardingRef.current.delete(projectId);
          console.warn('Could not remove simulated under-contract checklist', projectId, err);
        }
      }
    })();
  }, [
    canUseSimulateUnderContract,
    simulationSeededOnboardingIds,
    projectOnboardingsRaw,
    simulateUcSchedules,
    saveSimulatedProjectSchedule,
  ]);

  const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);
  /**
   * Scoped Project Hub listeners do not see CRMContext's optimistic `projectTasks`
   * patch. Keep status/progress/priority overrides until the scoped snapshot catches
   * up so gantt left + task detail StatusPill clicks feel instant (CRM #38).
   */
  const [taskFieldOverrides, setTaskFieldOverrides] = useState<Record<string, TaskFieldOverride>>({});
  const [taskSupplierOverrides, setTaskSupplierOverrides] = useState<
    Record<string, { supplierOrgIds: string[]; supplierManual: boolean }>
  >({});
  useEffect(() => {
    setTaskSupplierOverrides((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const id of ids) {
        const liveTask = projectTasks.find((t) => t.id === id);
        const parsed = isOnboardingBridgeTaskId(id) ? parseOnboardingBridgeTaskId(id) : null;
        const liveItem = parsed?.projectId
          ? projectOnboardings
              .find((o) => o.projectId === parsed.projectId)
              ?.items.find((it) => it.id === parsed.itemId)
          : undefined;
        const liveIds = liveTask?.supplierOrgIds ?? liveItem?.supplierOrgIds;
        if (!liveIds) continue;
        const liveKey = liveIds.join('\0');
        const patchKey = prev[id].supplierOrgIds.join('\0');
        if (liveKey === patchKey) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [projectTasks, projectOnboardings]);
  useEffect(() => {
    setTaskFieldOverrides((prev) => pruneTaskFieldOverrides(prev, [
      ...projectTasks,
      ...mirrorAllOnboardingTasks(projectOnboardings, taskGroups),
    ]));
  }, [projectTasks, projectOnboardings, taskGroups]);
  const [panelParentTask, setPanelParentTask] = useState<ProjectTask | null>(null);
  const [panelTaskReminders, setPanelTaskReminders] = useState<TaskReminder[]>([]);
  const [structureFilter, setStructureFilter] = useState<Set<StructureFilterValue> | null>(null);
  const [dealStructures, setDealStructures] = useState<StructureRef[]>([]);
  const subtaskMigrationRanRef = useRef<Set<string>>(new Set());
  const [taskPdfViewer, setTaskPdfViewer] = useState<{
    url: string;
    title: string;
    documentId: string;
    annotationId?: string;
    pageNumber?: number;
  } | null>(null);
  const [taskImageViewer, setTaskImageViewer] = useState<{
    url: string;
    title: string;
    documentId: string;
    mimeType?: string;
    annotationId?: string;
  } | null>(null);
  const [taskCsvViewer, setTaskCsvViewer] = useState<{
    url: string;
    title: string;
    documentId: string;
    annotationId?: string;
  } | null>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState<'precon' | 'contracted'>('precon');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [lockAllTasksConfirm, setLockAllTasksConfirm] = useState<'lock' | 'unlock' | null>(null);
  const [cardMenuOpenId, setCardMenuOpenId] = useState<string | null>(null);
  const cardMenuRef = useRef<HTMLDivElement>(null);

  // Task read receipts for unread indicators
  const [readReceipts, setReadReceipts] = useState<ReadReceiptMap>({});
  useEffect(() => {
    if (!user?.id) return;
    return subscribeToReadReceipts(user.id, setReadReceipts);
  }, [user?.id]);

  // Opening a project's Files tab advances the `files:{projectId}` cursor that
  // drives the landing "new file comments" dot.
  useEffect(() => {
    if (!user?.id || !selectedProjectId || viewMode !== 'files') return;
    void markProjectFilesSeen(user.id, selectedProjectId);
  }, [user?.id, selectedProjectId, viewMode]);

  /** Selected project's annotations (from the scoped subscription), keyed for legacy consumers. */
  const pdfAnnotationsByProject = useMemo(() => {
    const map = new Map<string, PdfAnnotation[]>();
    if (selectedProjectId && scopedData) map.set(selectedProjectId, scopedData.pdfAnnotations);
    return map;
  }, [selectedProjectId, scopedData]);

  /** All-projects unread picker (?task= / Files deep links opened from the list). */
  const [unreadHubPopover, setUnreadHubPopover] = useState<{ projectId: string; projectLabel: string } | null>(null);

  // The popover loads its project's data on demand (the landing dot itself comes from rollups).
  const unreadPopoverDealId = useMemo(() => {
    if (!unreadHubPopover) return null;
    return timberProjects.find(p => p.id === unreadHubPopover.projectId)?.dealId?.trim() || null;
  }, [unreadHubPopover, timberProjects]);
  const unreadPopoverSiblingIds = useMemo(() => {
    if (!unreadHubPopover) return [];
    if (!unreadPopoverDealId) return [unreadHubPopover.projectId];
    return timberProjects.filter(p => p.id && p.dealId?.trim() === unreadPopoverDealId).map(p => p.id!);
  }, [unreadHubPopover, unreadPopoverDealId, timberProjects]);
  const unreadPopoverData = useProjectHubProjectData(
    unreadHubPopover?.projectId ?? null,
    unreadPopoverDealId,
    unreadPopoverSiblingIds,
    { decisionOnly: architectHubMode },
  );

  const handleUnreadHubPopoverPick = useCallback(
    (item: ProjectHubUnreadListItem) => {
      if (item.kind === 'group' && item.groupKey && item.groupProjectId) {
        if (user?.id) void markGroupAsRead(user.id, item.groupKey);
        setUnreadHubPopover(null);
        navigate(
          `/project-hub/${item.groupProjectId}?group=${encodeURIComponent(item.groupKey)}`,
        );
        return;
      }
      if (item.kind === 'task' && item.task?.projectId && item.task.id) {
        if (user?.id) void markTaskAsRead(user.id, item.task.id);
        setUnreadHubPopover(null);
        navigate(`/project-hub/${item.task.projectId}`, {
          state: { openTaskId: item.task.id, openTaskCommentsPopout: true },
        });
        return;
      }
      if (item.kind === 'file' && item.annotation) {
        const ann = item.annotation;
        const pid = ann.projectId;
        const docId = ann.documentId;
        const commentId = ann.id;
        if (!pid || !docId || !commentId) return;
        if (user?.id) {
          void markAnnotationAsRead(commentId, user.id);
          void markProjectFilesSeen(user.id, pid);
        }
        setUnreadHubPopover(null);
        const url = ann.documentUrl || docId;
        const title = displayFileNameFromAnnotation(ann);
        const kind = fileKindFromAnnotation(ann);
        // URL may be a stale download token — openFileViewer effect re-resolves via projectFiles.
        navigate(`/project-hub/${pid}`, {
          state: {
            openFileViewer: {
              kind,
              url,
              title,
              documentId: docId,
              annotationId: commentId,
              pageNumber: ann.pageNumber || 1,
            },
          },
        });
      }
    },
    [navigate, user?.id],
  );

  const unreadHubPopoverItems = useMemo((): ProjectHubUnreadListItem[] => {
    if (!unreadHubPopover || !unreadPopoverData) return [];
    return buildProjectHubUnreadListItems({
      projectId: unreadHubPopover.projectId,
      projectTasks: unreadPopoverData.tasks,
      pdfAnnotations: unreadPopoverData.pdfAnnotations,
      userId: user?.id || '',
      readReceipts,
      crmUsers,
      taskGroups: unreadPopoverData.taskGroups,
      taskComments: unreadPopoverData.taskComments,
      timberProjects,
      projectOnboardings: unreadPopoverData.projectOnboardings,
    });
  }, [
    unreadHubPopover,
    unreadPopoverData,
    user?.id,
    readReceipts,
    crmUsers,
    timberProjects,
  ]);

  // Self-heal the coarse landing file dot: once the popover has full
  // per-annotation data and shows no unread file comments, advance the
  // `files:{projectId}` receipt so the rollup dot matches reality (e.g. old
  // annotations read before the rollup cursor existed).
  useEffect(() => {
    if (!unreadHubPopover || !user?.id || !unreadPopoverData?.ready) return;
    if (unreadHubPopoverItems.some(i => i.kind === 'file')) return;
    const rollup = hubRollups.get(unreadHubPopover.projectId);
    if (rollupHasUnreadFileActivity(rollup, user.id, readReceipts)) {
      void markProjectFilesSeen(user.id, unreadHubPopover.projectId);
    }
  }, [unreadHubPopover, unreadPopoverData, unreadHubPopoverItems, hubRollups, user?.id, readReceipts]);

  // Engineering firms from app settings (for EOR assignment)
  const [engineeringFirms, setEngineeringFirms] = useState<AppSettings['engineeringFirms']>([]);
  const eorFirmOptions = useMemo(
    () => engineeringFirmsWithCrmEngineers(engineeringFirms, crmUsers),
    [engineeringFirms, crmUsers],
  );
  useEffect(() => {
    return subscribeToSettings((s) => setEngineeringFirms(s.engineeringFirms || []));
  }, []);

  const handleSelectTask = useCallback((task: ProjectTask) => {
    const realId = unwrapSupplierDisplayTaskId(task.id) ?? task.id;
    const realParentId = unwrapSupplierDisplayTaskId(task.parentTaskId) ?? task.parentTaskId;
    const override = realId ? taskSupplierOverrides[realId] : undefined;
    const realBase =
      projectTasks.find((t) => t.id === realId)
      ?? { ...task, id: realId, parentTaskId: realParentId };
    const real = override ? { ...realBase, ...override } : realBase;
    setSelectedTask(real);
    if (real.parentTaskId) {
      const parent =
        projectTasks.find((t) => t.id === real.parentTaskId)
        ?? null;
      setPanelParentTask(parent);
    } else {
      setPanelParentTask(null);
    }
    if (user?.id && real.id) {
      markTaskAsRead(user.id, real.id);
    }
  }, [user?.id, projectTasks, taskSupplierOverrides]);

  /** Token bumped each time the per-row comments button is pressed. The current
   *  Date.now() is forwarded down to TaskComments via TaskDetailPanel; a fresh
   *  value re-fires the open-popup effect even when the user has previously
   *  closed it on the same task. */
  /* ── Consolidated group comments popup ───────────────────────────────── */
  const [groupCommentsContext, setGroupCommentsContext] = useState<GroupCommentContext | null>(null);
  const [groupCommentsPopoutToken, setGroupCommentsPopoutToken] = useState<number | null>(null);
  const [pendingHighlightGroupCommentId, setPendingHighlightGroupCommentId] = useState<string | null>(null);
  const [pendingGroupCommentsTab, setPendingGroupCommentsTab] = useState<GroupCommentsTab | null>(null);
  const [pendingOpenPlanReviewId, setPendingOpenPlanReviewId] = useState<string | null>(null);
  const [projectMeetingNotes, setProjectMeetingNotes] = useState<ProjectMeetingNote[]>([]);
  const [leadCommsForProject, setLeadCommsForProject] = useState<LeadComm[]>([]);
  const [projectMeetings, setProjectMeetings] = useState<ProjectMeeting[]>([]);

  const handleCloseGroupComments = useCallback(() => {
    setGroupCommentsContext(null);
    setGroupCommentsPopoutToken(null);
    setPendingHighlightGroupCommentId(null);
    setPendingGroupCommentsTab(null);
    setPendingOpenPlanReviewId(null);
  }, []);

  const openGroupCommentsForContext = useCallback(
    (
      ctx: GroupCommentContext,
      highlightCommentId?: string | null,
      options?: { markAsRead?: boolean; planReviewId?: string | null; initialTab?: GroupCommentsTab | null },
    ) => {
      setGroupCommentsContext(ctx);
      setPendingHighlightGroupCommentId(highlightCommentId ?? null);
      setPendingGroupCommentsTab(options?.initialTab ?? null);
      setPendingOpenPlanReviewId(options?.planReviewId?.trim() || null);
      // The walkthrough opens a thread purely to demo the UI — don't clear unread state then.
      if (user?.id && options?.markAsRead !== false) {
        void markGroupAsRead(user.id, ctx.groupKey);
        if (architectHubMode) void markArchitectDiscussionAsRead(user.id, ctx.groupKey);
      }
      setGroupCommentsPopoutToken(Date.now());
    },
    [architectHubMode, user?.id],
  );

  /** Supplier mode: the production thread lives on the deal-scoped
   *  `groupCommentKey` carve-out (same thread the vendor portal opens). */
  const openSupplierProductionComments = useCallback(
    (projectId: string, highlightCommentId?: string | null) => {
      const project = timberProjects.find((p) => p.id === projectId);
      const dealId = project?.dealId?.trim();
      if (!dealId) return;
      openGroupCommentsForContext(
        buildSupplierProductionGroupContext({ dealId, hubProjectId: projectId }),
        highlightCommentId ?? null,
      );
    },
    [timberProjects, openGroupCommentsForContext],
  );

  const handleOpenGroupComments = useCallback(
    (group: TaskGroup) => {
      if (supplierHubMode) {
        openSupplierProductionComments(group.projectId);
        return;
      }
      if (architectHubMode && !isDesignGroupName(group.name)) return;
      const currentProj = timberProjects.find((p) => p.id === group.projectId);
      const ctx = collectGroupCommentContext(
        group,
        currentProj,
        timberProjects,
        taskGroups,
        projectTasks,
        projectOnboardings,
      );
      openGroupCommentsForContext(ctx);
    },
    [supplierHubMode, architectHubMode, openSupplierProductionComments, timberProjects, taskGroups, projectTasks, projectOnboardings, openGroupCommentsForContext],
  );

  const openLatestCommentGroup = useCallback(() => {
    if (!selectedProjectId) return;
    const projectGroups = taskGroups.filter((group) => group.projectId === selectedProjectId);
    const visibleGroups = architectHubMode
      ? projectGroups.filter((group) => isDesignGroupName(group.name))
      : projectGroups;
    const currentProj = timberProjects.find((project) => project.id === selectedProjectId);
    const group = groupWithLatestComment({
      groups: visibleGroups,
      currentProject: currentProj,
      timberProjects,
      taskGroups,
      projectTasks,
      projectOnboardings,
      taskComments,
    });
    if (!group) return;
    handleOpenGroupComments(group);
  }, [
    selectedProjectId,
    architectHubMode,
    taskGroups,
    timberProjects,
    projectTasks,
    projectOnboardings,
    taskComments,
    handleOpenGroupComments,
  ]);

  useProjectHubLatestCommentHotkey({
    enabled:
      !!selectedProjectId &&
      !showQuickSearch &&
      !showWorkQueueModal &&
      !showTimeReport,
    onOpen: openLatestCommentGroup,
  });

  /** Open the consolidated group thread for a task (legacy task deep links / notifications). */
  const openGroupCommentsForTask = useCallback(
    (task: ProjectTask, highlightCommentId?: string | null): boolean => {
      const ctx = buildGroupContextForTask(
        task,
        timberProjects,
        taskGroups,
        projectTasks,
        projectOnboardings,
      );
      if (!ctx) return false;
      const comment = highlightCommentId
        ? taskComments.find((c) => c.id === highlightCommentId)
        : undefined;
      openGroupCommentsForContext(ctx, highlightCommentId ?? null, {
        initialTab: resolveGroupCommentsTab({
          commentAudience: comment?.audience,
          groupCommentKey: ctx.groupKey,
        }),
      });
      return true;
    },
    [timberProjects, taskGroups, projectTasks, projectOnboardings, openGroupCommentsForContext, taskComments],
  );

  const isGroupUnread = useCallback(
    (group: TaskGroup) => {
      if (!user?.id) return false;
      const currentProj = timberProjects.find((p) => p.id === group.projectId);
      const ctx = collectGroupCommentContext(
        group,
        currentProj,
        timberProjects,
        taskGroups,
        projectTasks,
        projectOnboardings,
      );
      const comments = aggregateGroupComments(taskComments, ctx);
      if (architectHubMode) {
        return architectDiscussionUnreadCount(comments, readReceipts, ctx.groupKey, user.id) > 0;
      }
      return groupHasUnread(comments, ctx, user.id, readReceipts);
    },
    [architectHubMode, timberProjects, taskGroups, projectTasks, projectOnboardings, taskComments, readReceipts, user?.id],
  );

  const groupCommentsProjectAnnotations = useMemo(
    () =>
      groupCommentsContext
        ? pdfAnnotationsByProject.get(groupCommentsContext.primaryProjectId) ?? []
        : [],
    [groupCommentsContext, pdfAnnotationsByProject],
  );

  const groupCommentsDealId = useMemo(() => {
    if (!groupCommentsContext) return undefined;
    return timberProjects.find((p) => p.id === groupCommentsContext.primaryProjectId)?.dealId;
  }, [groupCommentsContext, timberProjects]);

  const groupCommentMemberTasks = useMemo(() => {
    if (!groupCommentsContext) return [];
    return buildGroupMemberTasks(
      groupCommentsContext,
      projectTasks,
      timberProjects,
      taskGroups,
      projectOnboardings,
    );
  }, [groupCommentsContext, projectTasks, timberProjects, taskGroups, projectOnboardings]);

  // Close card grid menu on outside click
  useEffect(() => {
    if (!cardMenuOpenId) return;
    const handler = (e: MouseEvent) => {
      if (cardMenuRef.current && !cardMenuRef.current.contains(e.target as Node)) {
        setCardMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [cardMenuOpenId]);

  useEffect(() => {
    if (!showSimulateUcMenu) return;
    const handler = (e: MouseEvent) => {
      if (simulateUcMenuRef.current && !simulateUcMenuRef.current.contains(e.target as Node)) {
        setShowSimulateUcMenu(false);
        setSimulateUcQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSimulateUcMenu]);

  const teamMembers = useMemo(() => {
    const staffList = buildProjectHubTeamMembers({
      crmUsers,
      dealId: scopedDealId,
      productionCommentAccess,
      rfqProjects,
      rfqSubmissions,
      timberProjects,
      supplierOrgs,
    });
    if (!supplierHubMode) return staffList;

    const deal = scopedDealId
      ? enhancedDeals.find((d) => d.id === scopedDealId) || null
      : null;
    const projectIds = Array.from(
      new Set(
        timberProjects
          .filter((p) => !scopedDealId || p.dealId === scopedDealId)
          .map((p) => p.id)
          .filter((id): id is string => !!id),
      ),
    );
    return buildProductionMentionCandidates({
      viewerRole: 'supplier',
      crmUsers,
      dealId: scopedDealId,
      deal,
      projectIds,
      taskComments,
      pdfAnnotations: selectedProjectId
        ? (pdfAnnotationsByProject.get(selectedProjectId) ?? [])
        : Array.from(pdfAnnotationsByProject.values()).flat(),
    });
  }, [
    crmUsers,
    scopedDealId,
    productionCommentAccess,
    rfqProjects,
    rfqSubmissions,
    timberProjects,
    supplierOrgs,
    supplierHubMode,
    enhancedDeals,
    taskComments,
    selectedProjectId,
    pdfAnnotationsByProject,
  ]);

  const timeReportEntries = useMemo(
    () => (selectedProjectId ? projectTimeEntries.filter(e => e.projectId === selectedProjectId) : projectTimeEntries),
    [selectedProjectId, projectTimeEntries],
  );

  const hubVisibleProjectsBase = useMemo(
    () =>
      filterTimberProjectsForArchEngineerHub(
        timberProjects,
        user?.id,
        user?.role,
        enhancedDeals,
        projectTasks,
        taskGroups,
        projectOnboardings,
        crmUsers,
        engineeringFirms ?? [],
      ),
    [
      timberProjects,
      user?.id,
      user?.role,
      enhancedDeals,
      projectTasks,
      taskGroups,
      projectOnboardings,
      crmUsers,
      engineeringFirms,
    ],
  );

  // PDF pins and comment @mentions grant designer/engineer hub access without a task assignment.
  const mentionProjectIds = useMemo(() => {
    const ids = collectRollupPdfMentionProjectIds(hubRollups, user?.id);
    collectRollupCommentMentionProjectIds(hubRollups, user?.id).forEach((id) => ids.add(id));
    collectTaskCommentMentionProjectIds(crmTaskComments, timberProjects, user?.id).forEach((id) => ids.add(id));
    return ids;
  }, [hubRollups, crmTaskComments, timberProjects, user?.id]);

  /** Projects opened via notification/email deep link — show in sidebar for this session. */
  const [deepLinkGrantedProjectIds, setDeepLinkGrantedProjectIds] = useState<Set<string>>(() => new Set());

  const hubVisibleProjects = useMemo(
    () => {
      const merged = mergeArchEngineerHubProjectsWithPdfMentions(
        hubVisibleProjectsBase,
        timberProjects,
        mentionProjectIds,
      );
      if (deepLinkGrantedProjectIds.size === 0) return merged;
      const seen = new Set(merged.map((p) => p.id).filter(Boolean));
      const extras = timberProjects.filter(
        (p) => p.id && deepLinkGrantedProjectIds.has(p.id) && !seen.has(p.id),
      );
      return extras.length > 0 ? [...merged, ...extras] : merged;
    },
    [hubVisibleProjectsBase, timberProjects, mentionProjectIds, deepLinkGrantedProjectIds],
  );

  /** Files deep links wait for the selected project's scoped annotations only. */
  const pdfAnnotationsHydrated = !selectedProjectId || !!scopedData?.ready;

  // Employees designated as Project Managers
  const projectManagers = useMemo(() =>
    crmUsers.filter(u => u.isActive && (u.role === 'employee' || u.role === 'admin') && u.designations?.includes('project_manager')),
    [crmUsers]
  );

  const isCurrentUserProjectManager = useMemo(() => {
    if (!user?.id) return false;
    const row = crmUsers.find((u) => u.uid === user.id);
    return !!(
      row &&
      row.isActive &&
      (row.role === 'employee' || row.role === 'admin') &&
      row.designations?.includes('project_manager')
    );
  }, [user?.id, crmUsers]);

  useEffect(() => {
    // Supplier hub (real account or staff viewAs) must never see PM-only Fathom notes.
    if (!selectedProjectId || !isCurrentUserProjectManager || supplierHubMode) {
      setProjectMeetingNotes([]);
      return;
    }
    return subscribeProjectMeetingNotes(selectedProjectId, setProjectMeetingNotes);
  }, [selectedProjectId, isCurrentUserProjectManager, supplierHubMode]);

  useEffect(() => {
    // Timeline calendar meetings: staff CRM workspace only (rules block client/architect/supplier).
    if (!selectedProjectId || supplierHubMode || architectHubMode) {
      setProjectMeetings([]);
      return;
    }
    return subscribeProjectMeetings(selectedProjectId, setProjectMeetings);
  }, [selectedProjectId, supplierHubMode, architectHubMode]);

  const timelineProjectMeetings = useMemo(
    () => filterMeetingsForTimeline(projectMeetings, showMeetings),
    [projectMeetings, showMeetings],
  );

  const isCurrentUserSalesRep = useMemo(() => {
    if (!user?.id) return false;
    const row = crmUsers.find((u) => u.uid === user.id);
    return userHasSalesRepDesignation(row) || userHasSalesRepDesignation(user);
  }, [user, crmUsers]);

  const [showPMPicker, setShowPMPicker] = useState(false);
  const [pmTooltipVisible, setPmTooltipVisible] = useState(false);
  const pmPickerRef = useRef<HTMLDivElement>(null);
  const pmTooltipTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!showPMPicker) return;
    const handler = (e: MouseEvent) => {
      if (pmPickerRef.current && !pmPickerRef.current.contains(e.target as Node)) {
        setShowPMPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPMPicker]);

  const wonDeals = useMemo((): EnhancedDeal[] =>
    enhancedDeals.filter(d =>
      d.outcome === 'won'
      && !d.plHistoricalShell
      && !timberProjects.some(p => p.dealId === d.id && p.phase !== 'preconstruction'),
    ),
    [enhancedDeals, timberProjects]
  );

  const activeDealsForPrecon = useMemo((): EnhancedDeal[] =>
    enhancedDeals.filter(
      d => d.outcome === 'active'
        && !timberProjects.some(p => p.dealId === d.id && isEarlyFunnelProjectPhase(p.phase)),
    ),
    [enhancedDeals, timberProjects]
  );

  const currentProject = useMemo(() =>
    selectedProjectId ? timberProjects.find(p => p.id === selectedProjectId) : null,
    [selectedProjectId, timberProjects]
  );

  const currentProjectSimulatingUc = useMemo(
    () => isSimulatingUnderContract(currentProject, simulateUcProjectIds),
    [currentProject, simulateUcProjectIds],
  );

  const simulateUcSelectedProjects = useMemo(() => {
    const selected = new Set(simulateUcProjectIds);
    return timberProjects.filter((p) => p.id && selected.has(p.id));
  }, [simulateUcProjectIds, timberProjects]);

  const simulateUcPicker = useMemo(() => {
    const dealsById = new Map(
      enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d] as const),
    );
    const candidates = timberProjects.filter(
      (p) => p.status !== 'completed' && isEarlyFunnelProjectPhase(p.phase),
    );
    const { estimating, preconstruction } = sortHubProjectSections(
      partitionHubProjects(candidates),
      dealsById,
    );
    const labelFor = (p: TimberProject) => {
      const deal = p.dealId ? dealsById.get(p.dealId) : undefined;
      const num = (deal?.projectNumber || '').trim();
      const numberDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
      const descriptor = (
        (deal?.descriptor || '').trim()
        || parseLegacyTitle(deal?.title || '').descriptor
        || p.name
        || 'Untitled project'
      ).trim();
      return [numberDisplay, descriptor].filter(Boolean).join(' ');
    };
    return { estimating, preconstruction, labelFor };
  }, [enhancedDeals, timberProjects]);

  useEffect(() => {
    if (selectedProjectId && currentProject) {
      setActiveProject(selectedProjectId, currentProject.phase);
    } else {
      setActiveProject(null);
    }
    return () => setActiveProject(null);
  }, [selectedProjectId, currentProject?.phase, setActiveProject]);

  const cncRfqForProject = useMemo(() => {
    if (!currentProject?.dealId) return null;
    return (
      rfqProjects.find(
        (p) => p.workType === 'cnc_quote' && (p.dealId === currentProject.dealId || p.timberProjectId === currentProject.id),
      ) || null
    );
  }, [rfqProjects, currentProject?.dealId, currentProject?.id]);

  const currentDeal = useMemo(() =>
    currentProject?.dealId ? enhancedDeals.find(d => d.id === currentProject.dealId) : null,
    [currentProject?.dealId, enhancedDeals]
  );

  useEffect(() => {
    // Lead Comms carried onto the project (same leadComms docs; stamped projectId on conversion).
    if (!selectedProjectId || supplierHubMode || architectHubMode) {
      setLeadCommsForProject([]);
      return;
    }
    return subscribeLeadCommsForProject(
      { projectId: selectedProjectId, leadId: currentDeal?.leadId || null },
      setLeadCommsForProject,
    );
  }, [selectedProjectId, currentDeal?.leadId, supplierHubMode, architectHubMode]);

  // Files and Suppliers mount heavy listeners. Keep them mounted after the first
  // open so switching back does not show the spinner and refetch again.
  const [warmProjectId, setWarmProjectId] = useState<string | null>(null);
  const [filesWarm, setFilesWarm] = useState(false);
  const [suppliersWarm, setSuppliersWarm] = useState(false);
  if ((selectedProjectId || null) !== warmProjectId) {
    setWarmProjectId(selectedProjectId || null);
    setFilesWarm(false);
    setSuppliersWarm(false);
  }
  useEffect(() => {
    if (!selectedProjectId) return;
    if (viewMode === 'files') setFilesWarm(true);
    if (viewMode === 'suppliers' && currentDeal?.id) setSuppliersWarm(true);
  }, [viewMode, selectedProjectId, currentDeal?.id]);

  const openProjectBilling = useCallback((dealId: string) => {
    navigate(projectHubBillingPath(dealId));
  }, [navigate]);

  useProjectHubBillingHotkey({
    enabled: !!selectedProjectId && !partnerHubMode,
    dealId: currentProject?.dealId,
    onOpenBilling: openProjectBilling,
  });

  const openHubProposal = useCallback(() => {
    if (!currentProject?.dealId || hideCommercialSurfaces) return;
    if (!(isCurrentUserSalesRep || isCurrentUserProjectManager)) return;
    navigate(`/proposal-builder/${currentProject.dealId}`);
  }, [currentProject?.dealId, hideCommercialSurfaces, isCurrentUserSalesRep, isCurrentUserProjectManager, navigate]);

  const openHubInspiration = useCallback(() => {
    if (!currentDeal?.leadId || !currentDeal?.id || supplierHubMode) return;
    if (!canAccessInspirationBoard(hubSurfaceRole, hubSurfaceDesignations)) return;
    const boardPath = `/client/${currentDeal.leadId}/project/${currentDeal.id}/board`;
    navigate(architectViewAsSuffix ? `${boardPath}${architectViewAsSuffix}` : boardPath, {
      state: {
        from: selectedProjectId
          ? `/project-hub/${selectedProjectId}${architectViewAsSuffix}`
          : `/project-hub${architectViewAsSuffix}`,
      },
    });
  }, [currentDeal?.leadId, currentDeal?.id, supplierHubMode, hubSurfaceRole, hubSurfaceDesignations, architectViewAsSuffix, navigate]);

  const openHubClientPortal = useCallback(() => {
    if (!currentDeal?.leadId || hideCommercialSurfaces) return;
    if (!canAccessProjectHubClientPortalControls(hubSurfaceRole, hubSurfaceDesignations)) return;
    const dealQ = currentDeal.id ? `?deal=${encodeURIComponent(currentDeal.id)}` : '';
    navigate(`/client/${currentDeal.leadId}${dealQ}`);
  }, [currentDeal?.leadId, currentDeal?.id, hideCommercialSurfaces, hubSurfaceRole, hubSurfaceDesignations, navigate]);

  useProjectHubExternalNavHotkeys({
    enabled:
      !!selectedProjectId &&
      !supplierHubMode &&
      !showQuickSearch &&
      !showWorkQueueModal &&
      !showTimeReport,
    onProposal:
      !hideCommercialSurfaces && (isCurrentUserSalesRep || isCurrentUserProjectManager)
        ? openHubProposal
        : undefined,
    onInspiration: canAccessInspirationBoard(hubSurfaceRole, hubSurfaceDesignations) ? openHubInspiration : undefined,
  });

  const currentPM = useMemo(() =>
    currentProject?.projectManager ? crmUsers.find(u => u.uid === currentProject.projectManager) : null,
    [currentProject?.projectManager, crmUsers]
  );

  const assignedProjectManagerUid = useCallback((projectId?: string) => {
    const pid = (projectId || selectedProjectId || '').trim();
    if (!pid) return undefined;
    return timberProjects.find((p) => p.id === pid)?.projectManager?.trim() || undefined;
  }, [selectedProjectId, timberProjects]);

  const canEditDealMeta = canAccessProjectHubClientPortalControls(hubSurfaceRole, hubSurfaceDesignations) && !hideCommercialSurfaces;
  const designersList = useMemo(() => listDesigners(crmUsers), [crmUsers]);
  const architectsList = useMemo(() => listArchitects(crmUsers), [crmUsers]);
  const assignableArchitects = useMemo(
    () => listAssignableArchitects(crmUsers, architectInvites),
    [crmUsers, architectInvites],
  );

  const externalArchitectName = useMemo(
    () => getExternalArchitectName(currentDeal, crmUsers),
    [currentDeal, crmUsers],
  );

  const assignedDesigner = useMemo(
    () => getAssignedDesigner(currentDeal, crmUsers),
    [currentDeal, crmUsers],
  );

  const headerClient = useMemo(() => {
    if (!currentProject) return '';
    return (
      currentDeal?.clientLabel
      || currentProject.clientName
      || currentDeal?.contactName
      || ''
    ).trim();
  }, [currentDeal, currentProject]);

  const headerSiteAddress = useMemo(() => {
    if (!currentProject) return '';
    const loc = currentProject.location?.trim();
    if (loc) return loc;
    if (currentProject.address) {
      const fromFields = formatLocation(currentProject.address).trim();
      if (fromFields) return fromFields;
    }
    const dealLoc = currentDeal?.location?.trim();
    if (dealLoc) return dealLoc;
    if (currentDeal?.address) {
      return formatLocation(currentDeal.address).trim();
    }
    return '';
  }, [currentProject, currentDeal]);

  const metaHoverMoveRaf = useRef<number | null>(null);
  const metaHoverTrackedElRef = useRef<HTMLDivElement | null>(null);
  const metaHoverDocCleanupRef = useRef<(() => void) | null>(null);
  const metaHoverPinnedRef = useRef(false);
  const metaHoverHideTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [projectMetaHover, setProjectMetaHover] = useState<{ x: number; y: number } | null>(null);

  const stopProjectMetaHoverTracking = useCallback(() => {
    if (metaHoverMoveRaf.current != null) {
      cancelAnimationFrame(metaHoverMoveRaf.current);
      metaHoverMoveRaf.current = null;
    }
    metaHoverDocCleanupRef.current?.();
    metaHoverDocCleanupRef.current = null;
    metaHoverTrackedElRef.current = null;
  }, []);

  const hideProjectMetaHover = useCallback(() => {
    if (metaHoverHideTimeout.current) {
      clearTimeout(metaHoverHideTimeout.current);
      metaHoverHideTimeout.current = undefined;
    }
    stopProjectMetaHoverTracking();
    metaHoverPinnedRef.current = false;
    setProjectMetaHover(null);
  }, [stopProjectMetaHoverTracking]);

  useEffect(() => () => hideProjectMetaHover(), [hideProjectMetaHover]);

  const handleProjectTitleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (metaHoverHideTimeout.current) {
        clearTimeout(metaHoverHideTimeout.current);
        metaHoverHideTimeout.current = undefined;
      }
      stopProjectMetaHoverTracking();
      metaHoverPinnedRef.current = false;

      const el = e.currentTarget;
      metaHoverTrackedElRef.current = el;

      let pendingX = e.clientX;
      let pendingY = e.clientY;

      const onDocMouseMove = (ev: MouseEvent) => {
        if (metaHoverPinnedRef.current) return;
        if (!metaHoverTrackedElRef.current) return;

        pendingX = ev.clientX;
        pendingY = ev.clientY;
        if (metaHoverMoveRaf.current != null) return;
        metaHoverMoveRaf.current = requestAnimationFrame(() => {
          metaHoverMoveRaf.current = null;
          if (!metaHoverPinnedRef.current) {
            setProjectMetaHover({ x: pendingX, y: pendingY });
          }
        });
      };

      document.addEventListener('mousemove', onDocMouseMove, { passive: true });
      metaHoverDocCleanupRef.current = () => {
        document.removeEventListener('mousemove', onDocMouseMove);
      };

      setProjectMetaHover({ x: e.clientX, y: e.clientY });
    },
    [stopProjectMetaHoverTracking],
  );

  const handleProjectTitleMouseLeave = useCallback(() => {
    metaHoverHideTimeout.current = setTimeout(() => {
      if (!metaHoverPinnedRef.current) hideProjectMetaHover();
    }, 120);
  }, [hideProjectMetaHover]);

  const handleMetaFlyoutMouseEnter = useCallback(() => {
    if (metaHoverHideTimeout.current) {
      clearTimeout(metaHoverHideTimeout.current);
      metaHoverHideTimeout.current = undefined;
    }
    metaHoverPinnedRef.current = true;
    stopProjectMetaHoverTracking();
  }, [stopProjectMetaHoverTracking]);

  const handleMetaFlyoutMouseLeave = useCallback(() => {
    hideProjectMetaHover();
  }, [hideProjectMetaHover]);

  const taskDetailPanelProjectContext = useMemo(
    () => getTaskDetailPanelProjectContext(currentProject, currentDeal, crmUsers),
    [currentProject, currentDeal, crmUsers]
  );

  const contractedAfterPrecon = useMemo(() => {
    if (!currentProject?.id || isEarlyFunnelProjectPhase(currentProject.phase) || !currentProject.dealId) {
      return false;
    }
    return timberProjects.some(
      (p) =>
        p.id !== currentProject.id &&
        p.dealId === currentProject.dealId &&
        isEarlyFunnelProjectPhase(p.phase),
    );
  }, [currentProject, timberProjects]);

  /** Engagement letter signature (concept / retainer path). Match by dealId or by lead when letter predates deal link. */
  const proposalSignedFromLetter = useMemo(() => {
    if (!currentProject?.dealId) return null;
    const dealLeadId = enhancedDeals.find((d) => d.id === currentProject.dealId)?.leadId;
    const letters = engagementLetters.filter((l) => {
      if (l.status === 'voided') return false;
      const matchesDeal = l.dealId === currentProject.dealId;
      const matchesLead = dealLeadId && l.leadId === dealLeadId;
      if (!matchesDeal && !matchesLead) return false;
      return !!(l.signature?.signedAt || l.signedAt);
    });
    if (letters.length === 0) return null;
    let earliest: Date | null = null;
    for (const l of letters) {
      const d = l.signature?.signedAt || l.signedAt;
      if (!d) continue;
      if (!earliest || d.getTime() < earliest.getTime()) earliest = d;
    }
    return earliest;
  }, [currentProject?.dealId, engagementLetters, enhancedDeals]);

  /** Timber proposal (sales order) sign date — primary for full proposals. */
  const [proposalSignedFromSalesOrder, setProposalSignedFromSalesOrder] = useState<Date | null>(null);
  const [hubProgressInvoices, setHubProgressInvoices] = useState<Array<{
    installmentId?: string;
    amount?: number;
    status?: string;
    partialId?: string;
    structureIds?: string[];
    closesMilestone?: boolean;
  }>>([]);
  useEffect(() => {
    if (!currentProject?.dealId) {
      setProposalSignedFromSalesOrder(null);
      setHubProgressInvoices([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const soq = await getDocs(
          query(collection(db, 'salesOrders'), where('dealId', '==', currentProject.dealId))
        );
        let best: Date | null = null;
        let invoices: typeof hubProgressInvoices = [];
        soq.forEach((docSnap) => {
          const data = docSnap.data();
          const s = data.signedAt as { toDate?: () => Date } | undefined;
          const dt = s && typeof s.toDate === 'function' ? s.toDate() : s ? new Date(s as unknown as string) : null;
          if (dt && !isNaN(dt.getTime()) && (!best || dt.getTime() < best.getTime())) best = dt;
          if (data.billingStyle === 'progress' && !data.changeOrderId) {
            const list = Array.isArray(data.installmentInvoices) ? data.installmentInvoices : [];
            if (docSnap.id.startsWith('so-base-') || invoices.length === 0) {
              invoices = list.map((entry: Record<string, unknown>) => ({
                installmentId: typeof entry.installmentId === 'string' ? entry.installmentId : undefined,
                amount: Number(entry.amount) || 0,
                status: typeof entry.status === 'string' ? entry.status : undefined,
                partialId: typeof entry.partialId === 'string' ? entry.partialId : undefined,
                structureIds: Array.isArray(entry.structureIds) ? entry.structureIds.filter((id) => typeof id === 'string') : undefined,
                closesMilestone: entry.closesMilestone === true,
              }));
            }
          }
        });
        if (!cancelled) {
          setProposalSignedFromSalesOrder(best);
          setHubProgressInvoices(invoices);
        }
      } catch {
        if (!cancelled) {
          setProposalSignedFromSalesOrder(null);
          setHubProgressInvoices([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentProject?.dealId]);

  const proposalSignedAt = useMemo(
    () => proposalSignedFromSalesOrder || proposalSignedFromLetter,
    [proposalSignedFromSalesOrder, proposalSignedFromLetter]
  );

  // ── Payment-due forecast flags for the Gantt (signed contracts only) ──
  const [dealSignedBase, setDealSignedBase] = useState<number | null>(null);
  const [dealChangeOrders, setDealChangeOrders] = useState<ChangeOrder[]>([]);
  const { lagSettings: paymentLagSettings } = usePaymentLagSettings();

  useEffect(() => {
    const dealId = currentProject?.dealId;
    setDealSignedBase(null);
    setDealChangeOrders([]);
    if (!dealId || viewMode !== 'gantt') return;
    let cancelled = false;
    getDoc(doc(db, 'proposalBuilders', dealId))
      .then(snap => {
        if (cancelled || !snap.exists()) return;
        setDealSignedBase(getSignedCommercialListTotal(snap.data() as ProposalBuilderData));
      })
      .catch(() => undefined);
    const unsub = subscribeChangeOrdersForDeal(dealId, orders => {
      if (!cancelled) setDealChangeOrders(orders);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [currentProject?.dealId, viewMode]);

  const filteredTasks = useMemo(() => {
    return selectedProjectId ? projectTasks.filter(t => t.projectId === selectedProjectId) : projectTasks;
  }, [projectTasks, selectedProjectId]);

  const filteredGroups = useMemo(() => {
    if (selectedProjectId) return taskGroups.filter(g => g.projectId === selectedProjectId);
    return taskGroups;
  }, [taskGroups, selectedProjectId]);

  /** Open a representative group's thread for the "comments moved" walkthrough (no read side-effects). */
  const openGroupCommentsForTour = useCallback((): boolean => {
    const group = filteredGroups[0];
    if (!group) return false;
    const currentProj = timberProjects.find((p) => p.id === group.projectId);
    const ctx = collectGroupCommentContext(
      group,
      currentProj,
      timberProjects,
      taskGroups,
      projectTasks,
      projectOnboardings,
    );
    openGroupCommentsForContext(ctx, null, { markAsRead: false });
    return true;
  }, [filteredGroups, timberProjects, taskGroups, projectTasks, projectOnboardings, openGroupCommentsForContext]);

  // First-visit "comments moved" walkthrough — runs once the task table (with group lines) is on screen.
  const { startTour: startCommentsTour } = useCommentsMovedTour({
    enabled: !loading && !!user?.id && !architectHubMode && viewMode === 'table' && !!selectedProjectId && filteredGroups.length > 0,
    surface: 'group',
    openDemo: openGroupCommentsForTour,
    closeDemo: handleCloseGroupComments,
  });

  const handleSelectProject = useCallback((id: string | null) => {
    setSelectedProjectId(id);
    setSelectedTask(null);
    setPanelParentTask(null);
    setStructureFilter(null);
    // Keep `?viewAs=` so the supplier preview survives hub navigation.
    const suffix = architectViewAsSuffix || supplierViewAsSuffix;
    if (id) navigate(`/project-hub/${id}${suffix}`, { replace: true });
    else navigate(`/project-hub${suffix}`, { replace: true });
  }, [navigate, supplierViewAsSuffix, architectViewAsSuffix]);

  const getProjectQuickSearchResults = useCallback(
    (query: string) =>
      searchProjectHub({
        query,
        timberProjects,
        projectTasks,
        taskGroups,
        taskComments,
        enhancedDeals,
        crmUsers,
        currentProjectId: selectedProjectId,
      }),
    [timberProjects, projectTasks, taskGroups, taskComments, enhancedDeals, crmUsers, selectedProjectId],
  );

  const handleProjectQuickSearchSelect = useCallback(
    (result: HubSearchResult) => {
      if (result.kind === 'project') {
        const project = result.payload as TimberProject;
        if (project.id) handleSelectProject(project.id);
        return;
      }
      if (result.kind === 'task') {
        const task = result.payload as ProjectTask;
        if (!task.id) return;
        if (task.projectId !== selectedProjectId) {
          navigate(`/project-hub/${task.projectId}?task=${encodeURIComponent(task.id)}`);
        } else {
          setViewMode('table');
          setSelectedTask(task);
        }
        return;
      }
      if (result.kind === 'comment') {
        const comment = result.payload as TaskComment;
        const taskId = comment.taskId;
        const projectId =
          comment.projectId ||
          projectTasks.find(t => t.id === taskId)?.projectId;
        if (projectId && taskId && comment.id) {
          const params = new URLSearchParams();
          params.set('task', taskId);
          params.set('taskComment', comment.id);
          navigate(`/project-hub/${projectId}?${params.toString()}`);
        }
        return;
      }
      if (result.kind === 'group') {
        const group = result.payload as TaskGroup;
        const proj = timberProjects.find(p => p.id === group.projectId);
        const base = proj?.dealId?.trim() || proj?.id || group.projectId;
        const key = buildGroupCommentKey(base, group.name);
        if (group.projectId) {
          navigate(`/project-hub/${group.projectId}?group=${encodeURIComponent(key)}`);
        }
      }
    },
    [handleSelectProject, selectedProjectId, navigate, projectTasks, timberProjects],
  );

  useHubQuickSearchHotkey({
    onOpen: () => setShowQuickSearch(true),
    blocked: showQuickSearch || !!taskPdfViewer || !!taskImageViewer || !!taskCsvViewer || !!deleteConfirm,
  });

  const handleCloseProject = useCallback(async (id: string) => {
    const proj = timberProjects.find(p => p.id === id);
    const priorStatus = proj?.status && proj.status !== 'completed' ? proj.status : undefined;
    await updateTimberProject(id, {
      status: 'completed',
      ...(priorStatus ? { statusBeforeClose: priorStatus } : {}),
    });
    if (proj?.dealId) {
      await closeRfqProjectsForDeal(proj.dealId, rfqProjects, updateRfqProject);
    }
    if (selectedProjectId === id) {
      setSelectedProjectId(null);
      navigate('/project-hub', { replace: true });
    }
  }, [updateTimberProject, timberProjects, rfqProjects, updateRfqProject, selectedProjectId, navigate]);

  const handleReopenProject = useCallback(async (id: string) => {
    const proj = timberProjects.find(p => p.id === id);
    if (!proj || proj.status !== 'completed') return;

    const restoreStatus: ProjectStatus =
      proj.statusBeforeClose
      || (isEarlyFunnelProjectPhase(proj.phase) ? 'design' : 'installation');

    await updateDoc(doc(db, 'timberProjects', id), {
      status: restoreStatus,
      statusBeforeClose: deleteField(),
      updatedAt: Timestamp.fromDate(new Date()),
    } as UpdateData<DocumentData>);

    if (proj.dealId) {
      await reopenRfqProjectsForDeal(proj.dealId, rfqProjects, rfqSubmissions, updateRfqProject);
    }

    handleSelectProject(id);
  }, [timberProjects, rfqProjects, rfqSubmissions, updateRfqProject, handleSelectProject]);

  const handleDeleteProject = useCallback(async (id: string) => {
    await deleteTimberProject(id);
    setDeleteConfirm(null);
    if (selectedProjectId === id) {
      setSelectedProjectId(null);
      navigate('/project-hub', { replace: true });
    }
  }, [deleteTimberProject, selectedProjectId, navigate]);

  const handleRequestDelete = useCallback((id: string, name: string) => {
    setDeleteConfirm({ id, name });
  }, []);

  const handleCreateTask = useCallback(async (taskData: Partial<ProjectTask> & { projectId: string; groupId: string }) => {
    if (isOnboardingVirtualGroupId(taskData.groupId)) return;
    const g = taskGroups.find((x) => x.id === taskData.groupId && x.projectId === taskData.projectId);
    const deptFromGroup = g ? resolveDepartmentFromTaskGroupName(g.name) : undefined;
    await createProjectTask({
      projectId: taskData.projectId,
      groupId: taskData.groupId,
      title: taskData.title || 'New Task',
      description: '',
      department: deptFromGroup ?? 'design',
      assignedTo: taskData.assignedTo || [],
      status: taskData.status || 'pending',
      priority: taskData.priority || 'medium',
      startDate: taskData.startDate || new Date(),
      endDate: taskData.endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      dependencies: [],
      progress: 0,
      position: taskData.position || 1.0,
    } as any);
  }, [createProjectTask, taskGroups]);

  const handleCreateGroup = useCallback(async (projectId: string) => {
    const existingGroups = taskGroups.filter(g => g.projectId === projectId);
    const maxPos = existingGroups.reduce((max, g) => Math.max(max, g.position || 0), 0);
    await createTaskGroup({
      projectId,
      name: 'New Group',
      color: '#6B7280',
      position: maxPos + 1.0,
    });
  }, [taskGroups, createTaskGroup]);

  const handleDeleteTaskSafe = useCallback(
    (id: string) => {
      id = unwrapSupplierDisplayTaskId(id) ?? id;
      if (isOnboardingBridgeTaskId(id)) {
        if (!selectedProjectId) return;
        const itemId = onboardingTaskIdToItemId(id);
        void deleteOnboardingItem(selectedProjectId, itemId).catch((err) => {
          console.error('Delete onboarding checklist item failed:', err);
          window.alert('Could not delete this checklist item. Try again or refresh the page.');
        });
        return;
      }
      const deleted = projectTasks.find((t) => t.id === id);
      const parentId = deleted?.parentTaskId;
      void deleteProjectTask(id)
        .then(async () => {
          await cancelPendingTaskRemindersForTask(id);
          if (!parentId) return;
          const patch = parentRollupPatch(
            parentId,
            [...mirrorAllOnboardingTasks(projectOnboardings, taskGroups), ...projectTasks],
            id,
          );
          if (patch) {
            if (isOnboardingBridgeTaskId(patch.parentId)) {
              const parsed = parseOnboardingBridgeTaskId(patch.parentId);
              const projectId = parsed?.projectId || deleted?.projectId || selectedProjectId;
              if (projectId && user) {
                await updateOnboardingItem(
                  projectId,
                  parsed?.itemId || onboardingTaskIdToItemId(patch.parentId),
                  { startDate: patch.startDate, endDate: patch.endDate },
                  user.name || user.email || 'Unknown',
                );
              }
            } else {
              await updateProjectTask(patch.parentId, {
                startDate: patch.startDate,
                endDate: patch.endDate,
              });
            }
          }
        })
        .catch((err) => {
          console.error('Delete task failed:', err);
          window.alert('Could not delete this task.');
        });
    },
    [
      deleteProjectTask,
      deleteOnboardingItem,
      selectedProjectId,
      projectTasks,
      projectOnboardings,
      taskGroups,
      user,
      updateOnboardingItem,
      updateProjectTask,
    ],
  );

  const handleUpdateGroupSafe = useCallback(
    (id: string, updates: Partial<TaskGroup>) => {
      if (isOnboardingVirtualGroupId(id)) return;
      updateTaskGroup(id, updates);
    },
    [updateTaskGroup]
  );

  const projectEorGroup = useMemo(() => {
    const withEor = filteredGroups.find(g => g.eorRecord);
    if (withEor) return withEor;
    const designGroup = filteredGroups.find(g => /^design$/i.test(g.name.trim()));
    if (designGroup) return designGroup;
    return filteredGroups.find(g => /engineer/i.test(g.name)) ?? filteredGroups[0];
  }, [filteredGroups]);

  const projectEor = projectEorGroup?.eorRecord ?? null;

  const canEditProjectEngineer = !isLimitedProjectViewer(user?.role, userDesignations)
    && !!projectEorGroup?.id
    && !isOnboardingVirtualGroupId(projectEorGroup.id);

  const projectEorDisplay = projectEor
    ? `${projectEor.engineerName} (${projectEor.firmName})`
    : '';

  const saveDealTeamFields = useCallback(
    async (team: ProjectDealTeamFieldsValue, baseline: ProjectDealTeamFieldsValue) => {
      if (canEditDealMeta && currentDeal?.id) {
        const patch: UpdateData<DocumentData> = { updatedAt: Timestamp.now() };
        let changed = false;

        if (team.designerUid !== baseline.designerUid) {
          const name = designersList.find((d) => d.uid === team.designerUid)?.displayName || '';
          patch.assignedDesigner = team.designerUid ? team.designerUid : deleteField();
          patch.assignedDesignerName = name ? name : deleteField();
          if (team.designerUid) {
            patch.assignedArchitect = deleteField();
          }
          changed = true;
        }

        const trimmedArchitect = team.architectName.trim();
        if (trimmedArchitect !== baseline.architectName.trim()) {
          patch.assignedArchitectName = trimmedArchitect ? trimmedArchitect : deleteField();
          changed = true;
        }

        const nextUids = [...team.architectUids].sort();
        const prevUids = [...baseline.architectUids].sort();
        if (nextUids.join('\0') !== prevUids.join('\0')) {
          patch.assignedArchitectUids = nextUids;
          patch.assignedArchitect = nextUids[0] ? nextUids[0] : deleteField();
          changed = true;
        }

        if (changed) {
          await updateDoc(doc(db, 'enhancedDeals', currentDeal.id), patch);
          if (currentProject?.id) {
            await updateDoc(doc(db, 'timberProjects', currentProject.id), {
              architectUids: nextUids,
            });
          }
        }
      }

      if (!canEditProjectEngineer || !projectEorGroup?.id) return;

      const engineerChanged =
        team.engineerFirmId !== baseline.engineerFirmId
        || team.engineerId !== baseline.engineerId;
      if (!engineerChanged) return;

      if (!team.engineerFirmId || !team.engineerId) {
        await updateDoc(doc(db, 'taskGroups', projectEorGroup.id), { eorRecord: deleteField() });
        return;
      }

      const firm = eorFirmOptions.find(f => f.id === team.engineerFirmId);
      const eng = firm?.engineers.find(e => e.id === team.engineerId);
      if (!firm || !eng) return;

      handleUpdateGroupSafe(projectEorGroup.id, {
        eorRecord: {
          firmId: firm.id,
          firmName: firm.name,
          engineerId: eng.id,
          engineerName: eng.name,
        },
      });
    },
    [
      canEditDealMeta,
      canEditProjectEngineer,
      currentDeal?.id,
      currentProject?.id,
      designersList,
      eorFirmOptions,
      handleUpdateGroupSafe,
      projectEorGroup?.id,
    ],
  );

  useEffect(() => {
    renameAddressDraftRef.current = renameAddressDraft;
  }, [renameAddressDraft]);

  useEffect(() => {
    if (!renamingProject) {
      renameModalInitializedRef.current = false;
      return;
    }
    // Only seed drafts when the modal opens — not when live project/deal snapshots
    // refresh while the user is editing (that was wiping address changes before save).
    if (renameModalInitializedRef.current) return;
    renameModalInitializedRef.current = true;

    const baseline: ProjectDealTeamFieldsValue = {
      designerUid: assignedDesigner.uid,
      architectName: externalArchitectName,
      architectUids: getAssignedArchitectUids(currentDeal, currentProject),
      engineerFirmId: projectEor?.firmId ?? '',
      engineerId: projectEor?.engineerId ?? '',
    };
    renameTeamBaselineRef.current = baseline;
    setRenameTeamDraft(baseline);

    const addressBaseline = resolveProjectSiteAddressDraft(currentProject, currentDeal);
    renameAddressBaselineRef.current = addressBaseline;
    setRenameAddressDraft(addressBaseline);
  }, [
    renamingProject,
    assignedDesigner.uid,
    externalArchitectName,
    projectEor?.firmId,
    projectEor?.engineerId,
    currentProject,
    currentDeal,
  ]);

  const projectMissingSiteAddress = useMemo(
    () =>
      !!currentProject &&
      !hasRecordedProjectSiteAddress(currentProject) &&
      !hasRecordedProjectSiteAddress({
        location: currentDeal?.location,
        address: currentDeal?.address,
      }),
    [currentProject, currentDeal],
  );

  const handleDeleteGroupSafe = useCallback(
    (id: string) => {
      if (isOnboardingVirtualGroupId(id) || isSimulatedUcGroupId(id)) return;
      deleteTaskGroup(id);
    },
    [deleteTaskGroup]
  );

  const handleLogTime = useCallback(async (taskId: string, minutes: number, note: string, date?: Date) => {
    if (!user?.id || !selectedProjectId) return;
    const proj = timberProjects.find(p => p.id === selectedProjectId);
    const entryDate = date || new Date();
    await createProjectTimeEntry({
      projectId: selectedProjectId,
      taskId,
      userId: user.id,
      userName: user.name || 'Unknown',
      type: 'explicit',
      minutes,
      date: entryDate,
      startTime: entryDate,
      endTime: new Date(entryDate.getTime() + minutes * 60 * 1000),
      ...(String(note ?? '').trim() ? { note: String(note).trim() } : {}),
      phase: isEarlyFunnelProjectPhase(proj?.phase) ? 'preconstruction' : 'contracted',
    });
  }, [createProjectTimeEntry, user, selectedProjectId, timberProjects]);

  const currentOnboarding = useMemo(() => {
    if (!selectedProjectId) return undefined;
    return getOnboardingForProject(selectedProjectId);
  }, [selectedProjectId, getOnboardingForProject]);

  const onboardingTasksMirror = useMemo((): ProjectTask[] => {
    if (architectHubMode) return [];
    if (!selectedProjectId || !currentOnboarding?.items?.length) return [];
    return currentOnboarding.items.map((it) =>
      onboardingItemToProjectTask(it, selectedProjectId, filteredGroups),
    );
  }, [architectHubMode, selectedProjectId, currentOnboarding, filteredGroups]);

  /** Checklist rows use real project task groups only (no duplicate virtual sections). */
  const mergedGroupsForViews = useMemo(
    () => sortTaskGroupsForProject(filteredGroups, currentProject ?? null),
    [filteredGroups, currentProject],
  );

  const tasksForViewsBase = useMemo(
    () => [...onboardingTasksMirror, ...filteredTasks],
    [onboardingTasksMirror, filteredTasks],
  );

  // Payment flags must see the same task list the Gantt renders — including
  // onboarding-checklist mirror tasks, which can be the only tasks in the
  // Production group and carry the 40% anchor.
  const ganttPaymentFlags = useMemo(() => {
    // Commercial amounts — hidden from suppliers, designers, engineers, and architects.
    if (hidePaymentFlags) return [];
    if (!currentProject?.id || dealSignedBase == null || dealSignedBase <= 0) return [];
    const projectMilestones = milestones.filter(m => m.projectId === currentProject.id);
    const projectDates = {
      contractSignedAt: currentProject.contractedAt ?? proposalSignedAt ?? undefined,
      startDate: currentProject.startDate,
      targetEndDate: currentProject.targetEndDate,
    };
    const taskAnchors = resolvePaymentDueTaskAnchors(
      currentProject.id,
      tasksForViewsBase,
      taskGroups,
    );
    const lastProductionTaskEnd = taskAnchors.production?.date ?? null;
    const schedule = buildPaymentScheduleBreakdown(dealSignedBase, dealChangeOrders, {
      passedInstallmentIds: computePassedInstallments(
        projectDates,
        projectMilestones,
        undefined,
        lastProductionTaskEnd,
        taskAnchors.install?.date ?? taskAnchors.namedFinal?.date ?? null,
      ),
    });
    const scheduleForFlags = {
      ...schedule,
      rows: schedule.rows.map((row) => {
        if (row.kind !== 'installment' || (row.id !== 'pre_shipment' && row.id !== 'final')) return row;
        const mine = hubProgressInvoices.filter((entry) => (
          entry.installmentId === row.id
          && entry.status !== 'voided'
          && entry.status !== 'removed_in_qb'
        ));
        const openPartial = mine.some((entry) => (
          !invoiceClosesMilestone(entry)
          && (!!entry.partialId || (entry.structureIds?.length ?? 0) > 0)
        ));
        if (!openPartial) return row;
        const billed = mine.reduce((sum, entry) => sum + Math.round(Number(entry.amount) || 0), 0);
        return { ...row, amount: Math.max(0, Math.round(row.amount) - billed) };
      }),
    };
    const approvedAtById: Record<string, Date | undefined> = {};
    schedule.rows.forEach(row => {
      if (row.kind !== 'change_order') return;
      const co = dealChangeOrders.find(c => `co-${c.id || c.number}` === row.id);
      if (co?.approvedAt) {
        const raw = co.approvedAt as Date | { toDate?: () => Date };
        approvedAtById[row.id] =
          raw instanceof Date ? raw : raw.toDate ? raw.toDate() : undefined;
      }
    });
    return computePaymentDueForecast(
      projectDates,
      projectMilestones,
      scheduleForFlags,
      approvedAtById,
      lastProductionTaskEnd,
      paymentLagSettings,
      taskAnchors.project?.date ?? null,
      {
        production: taskAnchors.production?.taskTitle,
        project: taskAnchors.project?.taskTitle,
        install: taskAnchors.install?.taskTitle,
      },
      taskAnchors,
    );
  }, [hidePaymentFlags, currentProject, dealSignedBase, dealChangeOrders, milestones, proposalSignedAt, tasksForViewsBase, taskGroups, paymentLagSettings, hubProgressInvoices]);

  const tasksForViewsBaseRef = useRef(tasksForViewsBase);
  tasksForViewsBaseRef.current = tasksForViewsBase;
  const milestonesRef = useRef(milestones);
  milestonesRef.current = milestones;

  /** After Firestore writes, block auto-clear of gantt overrides briefly (avoids snap-back on stale snapshots). */
  const ganttOverrideHoldUntilRef = useRef<Record<string, number>>({});
  const ganttOverrideSyncTimerRef = useRef<number | null>(null);

  const [ganttDateOverrides, setGanttDateOverrides] = useState<
    Record<string, { startDate: Date; endDate: Date }>
  >({});
  /** Awaiting user choice: forward ripple vs shift whole dependency component */
  const [dependencyDatePrompt, setDependencyDatePrompt] = useState<null | {
    taskId: string;
    start: Date;
    end: Date;
  }>(null);

  /** Pending mark-complete prompt: ask the PM for actual start/end dates and auto-lock. */
  const [pendingCompletionTask, setPendingCompletionTask] = useState<null | {
    taskId: string;
    title: string;
    scheduledStart: Date;
    scheduledEnd: Date;
    projectId: string;
    isOnboarding: boolean;
  }>(null);

  /** PM toasts (non-blocking lock-violation warnings, etc.) */
  const [pmToasts, setPmToasts] = useState<PmToastItem[]>([]);
  const pushPmToast = useCallback((kind: PmToastKind, title: string, body?: React.ReactNode) => {
    const id = `pm-toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setPmToasts((cur) => [...cur, { id, kind, title, body }]);
  }, []);
  const dismissPmToast = useCallback((id: string) => {
    setPmToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const filesCommentDeepLinkActive = React.useMemo(() => {
    if (searchParams.get('tab') !== 'files') return false;
    return !!(
      searchParams.get('file')?.trim() &&
      searchParams.get('comment')?.trim()
    );
  }, [searchParams]);

  const filesTabDeepLinkActive = React.useMemo(
    () => searchParams.get('tab') === 'files',
    [searchParams],
  );

  const taskCommentDeepLinkId = React.useMemo(() => {
    const fromUrl = searchParams.get('taskComment')?.trim();
    if (fromUrl) return fromUrl;
    const st = location.state as { highlightTaskCommentId?: string } | null;
    return st?.highlightTaskCommentId?.trim() || '';
  }, [searchParams, location.state]);

  const archAccessDeniedRef = useRef<string | null>(null);
  useEffect(() => {
    archAccessDeniedRef.current = null;
  }, [urlProjectId]);

  useEffect(() => {
    if (loading || !orgWideDataReady) return;
    const role = user?.role;
    if (!isDesignerRole(role) && role !== 'engineer') return;
    if (!urlProjectId) return;

    const denyAccess = (body?: string) => {
      if (archAccessDeniedRef.current === urlProjectId) return;
      archAccessDeniedRef.current = urlProjectId;
      pushPmToast(
        'error',
        "You don't have access to this project",
        body ??
          (filesCommentDeepLinkActive
            ? 'This file comment link is for a project you are not assigned to.'
            : 'Choose a project from your list to continue.'),
      );
      setSelectedProjectId(null);
      // Keep deep-link query params so a corrected access rule or refresh can still open the target.
      if (!filesCommentDeepLinkActive && !taskCommentDeepLinkId) {
        navigate('/project-hub', { replace: true });
      }
    };

    const grantAccess = () => {
      archAccessDeniedRef.current = null;
      if (selectedProjectId !== urlProjectId) setSelectedProjectId(urlProjectId);
    };

    const projectRow = timberProjects.find((p) => p.id === urlProjectId);
    const userId = user?.id;
    const deal = projectRow?.dealId
      ? enhancedDeals.find((d) => d.id === projectRow.dealId)
      : undefined;
    const mentionableUsers = crmUsers.map((u) => ({ uid: u.uid, displayName: u.displayName }));

    const hasFileCommentInvolvement =
      !!projectRow &&
      !!userId &&
      (isDesignerRole(role)
        ? architectIsInvolvedInProjectForFileComment(
            projectRow,
            deal,
            userId,
            projectTasks,
            taskGroups,
            projectOnboardings,
            crmUsers,
          )
        : engineerIsInvolvedInProjectForFileComment(
            projectRow,
            userId,
            projectTasks,
            taskGroups,
            projectOnboardings,
            { crmUsers, engineeringFirms: engineeringFirms ?? [] },
          ));

    const hasHubInvolvement =
      !!projectRow &&
      !!userId &&
      (isDesignerRole(role)
        ? architectIsInvolvedInProject(
            projectRow,
            deal,
            userId,
            projectTasks,
            taskGroups,
            projectOnboardings,
            crmUsers,
          )
        : engineerIsInvolvedInProject(
            projectRow,
            userId,
            projectTasks,
            taskGroups,
            projectOnboardings,
            { crmUsers, engineeringFirms: engineeringFirms ?? [] },
          ));

    const grantAccessForDeepLink = (projectId?: string) => {
      const pid = (projectId || urlProjectId || '').trim();
      if (pid) {
        setDeepLinkGrantedProjectIds((prev) => {
          if (prev.has(pid)) return prev;
          const next = new Set(prev);
          next.add(pid);
          return next;
        });
      }
      grantAccess();
    };

    if (filesCommentDeepLinkActive && hasFileCommentInvolvement) {
      grantAccessForDeepLink();
      return;
    }

    if (hubVisibleProjects.some((p) => p.id === urlProjectId)) {
      grantAccess();
      return;
    }

    if (hasHubInvolvement) {
      grantAccess();
      return;
    }

    if (filesTabDeepLinkActive && !filesCommentDeepLinkActive) {
      grantAccessForDeepLink();
      return;
    }

    const tryGrantTaskCommentRecipient = (
      shape: {
        authorId?: string;
        mentions?: TaskComment['mentions'];
        notifyUserIds?: string[] | null;
        taskId?: string;
        projectId?: string | null;
      },
    ): boolean => {
      if (!userId) return false;
      if (
        !userIsTaskCommentNotifyTarget(
          {
            authorId: shape.authorId || '',
            mentions: shape.mentions,
            notifyUserIds: shape.notifyUserIds,
            taskId: shape.taskId || '',
          },
          userId,
          projectTasks,
        )
      ) {
        return false;
      }
      const commentProjectId =
        (typeof shape.projectId === 'string' && shape.projectId.trim()) ||
        projectTasks.find((t) => t.id === shape.taskId)?.projectId ||
        urlProjectId;
      if (commentProjectId && commentProjectId !== urlProjectId) {
        navigate(
          buildProjectHubCommentDeepLinkPath(commentProjectId, {
            taskId: shape.taskId,
            taskCommentId: taskCommentDeepLinkId,
            groupKey: searchParams.get('group')?.trim(),
            discussion: searchParams.get('discussion')?.trim(),
          }),
          { replace: true },
        );
        return true;
      }
      grantAccessForDeepLink(commentProjectId || urlProjectId);
      return true;
    };

    // Architect email → designer alias (`?discussion=architect&taskComment=`).
    // The comment is often addressed only to the external architect; designers who
    // can already see the project should open Design discussion without a leftover
    // "not addressed to your account" toast from the notify-target check below.
    const discussionParam = searchParams.get('discussion')?.trim();
    if (discussionParam === 'architect' && projectRow) {
      grantAccessForDeepLink();
      return;
    }

    if (taskCommentDeepLinkId && userId) {
      const cached = taskComments.find((c) => c.id === taskCommentDeepLinkId);
      if (cached) {
        if (tryGrantTaskCommentRecipient(cached)) return;
        denyAccess('This comment notification is not addressed to your account.');
        return;
      }

      let cancelled = false;
      void getDoc(doc(db, 'taskComments', taskCommentDeepLinkId)).then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          denyAccess();
          return;
        }
        const data = snap.data();
        if (
          tryGrantTaskCommentRecipient({
            authorId: typeof data.authorId === 'string' ? data.authorId : '',
            mentions: data.mentions as TaskComment['mentions'],
            notifyUserIds: Array.isArray(data.notifyUserIds) ? data.notifyUserIds : null,
            taskId: typeof data.taskId === 'string' ? data.taskId : '',
            projectId: typeof data.projectId === 'string' ? data.projectId : null,
          })
        ) {
          return;
        }
        denyAccess('This comment notification is not addressed to your account.');
      });
      return () => {
        cancelled = true;
      };
    }

    const commentId = searchParams.get('comment')?.trim();
    if (commentId && userId) {
      let cancelled = false;
      void getDoc(doc(db, 'pdfAnnotations', commentId)).then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          denyAccess();
          return;
        }
        const data = snap.data();
        const annProjectId = typeof data.projectId === 'string' ? data.projectId.trim() : '';
        if (annProjectId && annProjectId !== urlProjectId) {
          denyAccess();
          return;
        }
        const mentioned = pdfAnnotationMentionsUserDeep(
          {
            mentions: data.mentions as PdfAnnotation['mentions'],
            replies: data.replies as PdfAnnotation['replies'],
            content: typeof data.content === 'string' ? data.content : '',
          },
          userId,
          mentionableUsers,
        );
        const involvedNow =
          !!projectRow &&
          (isDesignerRole(role)
            ? architectIsInvolvedInProjectForFileComment(
                projectRow,
                deal,
                userId,
                projectTasks,
                taskGroups,
                projectOnboardings,
                crmUsers,
              )
            : engineerIsInvolvedInProjectForFileComment(
                projectRow,
                userId,
                projectTasks,
                taskGroups,
                projectOnboardings,
                { crmUsers, engineeringFirms: engineeringFirms ?? [] },
              ));
        if (mentioned || involvedNow) {
          grantAccess();
          return;
        }
        denyAccess();
      });
      return () => {
        cancelled = true;
      };
    }

    if (filesCommentDeepLinkActive) {
      // Hub data may still be hydrating task involvement — avoid denying email deep links too early.
      return;
    }

    if (taskCommentDeepLinkId) {
      return;
    }

    if (!pdfAnnotationsHydrated && searchParams.get('tab') === 'files') {
      return;
    }

    denyAccess();
  }, [
    loading,
    orgWideDataReady,
    user?.role,
    user?.id,
    urlProjectId,
    hubVisibleProjects,
    timberProjects,
    enhancedDeals,
    projectTasks,
    taskGroups,
    taskComments,
    projectOnboardings,
    crmUsers,
    engineeringFirms,
    selectedProjectId,
    navigate,
    searchParams,
    location.state,
    pushPmToast,
    pdfAnnotationsHydrated,
    filesCommentDeepLinkActive,
    filesTabDeepLinkActive,
    taskCommentDeepLinkId,
  ]);

  /** Supplier mode deep-link guard: only awarded projects are reachable. */
  const supplierAccessDeniedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!supplierHubMode || loading) return;
    if (!urlProjectId) {
      supplierAccessDeniedRef.current = null;
      return;
    }
    // Wait for the awarded-project list (callable hydration / CRM scoping).
    if (timberProjects.length === 0) return;
    if (timberProjects.some((p) => p.id === urlProjectId)) {
      supplierAccessDeniedRef.current = null;
      return;
    }
    if (supplierAccessDeniedRef.current === urlProjectId) return;
    supplierAccessDeniedRef.current = urlProjectId;
    pushPmToast(
      'error',
      "You don't have access to this project",
      'Only projects with awarded work for your organization are available.',
    );
    setSelectedProjectId(null);
    navigate(`/project-hub${supplierViewAsSuffix}`, { replace: true });
  }, [supplierHubMode, loading, urlProjectId, timberProjects, navigate, supplierViewAsSuffix, pushPmToast]);

  const persistArchEngineerWorkQueue = useCallback(
    async (subjectUserId: string, projectIds: string[]) => {
      const seen = new Set<string>();
      const deduped: string[] = [];
      for (const id of projectIds) {
        if (!id || seen.has(id)) continue;
        seen.add(id);
        deduped.push(id);
      }
      try {
        await setDoc(
          doc(db, 'settings', ARCH_ENGINEER_WORK_QUEUE_DOC_ID),
          {
            queuesByUserId: {
              ...aeWorkQueuesByUser,
              [subjectUserId]: deduped,
            },
            updatedAt: Timestamp.now(),
            updatedByUserId: user?.id || '',
          },
          { merge: true },
        );
        pushPmToast('success', 'Work queue saved');
      } catch (e) {
        pushPmToast('error', 'Could not save work queue', e instanceof Error ? e.message : String(e));
        throw e;
      }
    },
    [user?.id, pushPmToast, aeWorkQueuesByUser],
  );

  const pruneMatchingGanttOverrides = useCallback(() => {
    setGanttDateOverrides((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const base = tasksForViewsBaseRef.current;
      const ms = milestonesRef.current;
      const next = { ...prev };
      let changed = false;
      const clearedIds: string[] = [];
      const now = Date.now();
      for (const id of Object.keys(next)) {
        if (now < (ganttOverrideHoldUntilRef.current[id] ?? 0)) continue;
        const o = next[id];
        if (isMilestoneBridgeId(id)) {
          const mid = parseMilestoneBridgeId(id);
          const m = ms.find((x) => x.id === mid);
          if (m && sameLocalCalendarDay(new Date(m.targetDate), o.endDate)) {
            delete next[id];
            delete ganttOverrideHoldUntilRef.current[id];
            clearedIds.push(id);
            changed = true;
          }
        } else {
          const t = base.find((x) => x.id === id);
          if (
            t &&
            sameLocalCalendarDay(new Date(t.startDate), o.startDate) &&
            sameLocalCalendarDay(new Date(t.endDate), o.endDate)
          ) {
            delete next[id];
            delete ganttOverrideHoldUntilRef.current[id];
            clearedIds.push(id);
            changed = true;
          }
        }
      }
      if (changed && clearedIds.length && isGanttScheduleDebugEnabled()) {
        queueMicrotask(() => {
          ganttScheduleDebugLog(
            'ganttDateOverrides cleared (OK) — saved task dates now match what the bar showed; in-memory overlay removed',
            clearedIds.map((id) => formatGanttTaskIdForLog(id)),
          );
        });
      }
      return changed ? next : prev;
    });
  }, []);

  const tasksForViews = useMemo(
    () =>
      tasksForViewsBase.map((t) => {
        if (!t.id) return t;
        let next = t;
        const dates = ganttDateOverrides[t.id];
        if (dates) {
          next = { ...next, startDate: dates.startDate, endDate: dates.endDate };
        }
        return mergeTaskFieldOverride(next, taskFieldOverrides[t.id]);
      }),
    [tasksForViewsBase, ganttDateOverrides, taskFieldOverrides],
  );

  const groupTimelineProjectLabels = useMemo(() => {
    const map = new Map<string, GroupTimelineProjectLabel>();
    const dealsById = new Map(enhancedDeals.filter((d) => d.id).map((d) => [d.id!, d] as const));
    for (const p of hubVisibleProjects) {
      if (!p.id) continue;
      const deal = p.dealId ? dealsById.get(p.dealId) : undefined;
      const descriptor = (
        (deal?.descriptor || '').trim()
        || parseLegacyTitle(deal?.title || '').descriptor
        || p.name
        || ''
      ).trim();
      map.set(p.id, {
        projectNumber: (deal?.projectNumber || '').trim(),
        descriptor,
      });
    }
    return map;
  }, [hubVisibleProjects, enhancedDeals]);

  const groupTimelineSourceTasks = useMemo(() => {
    const base = [...mirrorAllOnboardingTasks(projectOnboardings, taskGroups), ...projectTasks];
    if (!Object.keys(taskSupplierOverrides).length && !selectedTask?.id) return base;
    const selectedId = selectedTask?.id
      ? (unwrapSupplierDisplayTaskId(selectedTask.id) ?? selectedTask.id)
      : null;
    return base.map((t) => {
      const tid = unwrapSupplierDisplayTaskId(t.id) ?? t.id;
      if (!tid) return t;
      const override = taskSupplierOverrides[tid];
      if (override) return { ...t, ...override };
      if (tid === selectedId) {
        return {
          ...t,
          supplierOrgIds: selectedTask?.supplierOrgIds ?? t.supplierOrgIds,
          supplierManual: selectedTask?.supplierManual ?? t.supplierManual,
        };
      }
      return t;
    });
  }, [projectOnboardings, taskGroups, projectTasks, selectedTask, taskSupplierOverrides]);

  const groupTimelineSupplierIndex = useMemo(
    () =>
      buildGroupTimelineSupplierIndex({
        family: groupTimelineFamily,
        projects: hubVisibleProjects,
        tasks: groupTimelineSourceTasks,
        rfqProjects,
        rfqPackages,
        rfqSubmissions,
        supplierOrgs,
      }),
    [
      groupTimelineFamily,
      hubVisibleProjects,
      groupTimelineSourceTasks,
      rfqProjects,
      rfqPackages,
      rfqSubmissions,
      supplierOrgs,
    ],
  );

  const supplierTimelineTasks = useMemo(() => {
    if (!supplierHubMode || !supplierViewerOrgId) return null;
    return filterTasksVisibleToSupplierOrg({
      tasks: groupTimelineSourceTasks,
      byTaskId: groupTimelineSupplierIndex.byTaskId,
      viewerOrgId: supplierViewerOrgId,
      projects: hubVisibleProjects,
      rfqProjects,
      rfqPackages,
    });
  }, [
    supplierHubMode,
    supplierViewerOrgId,
    groupTimelineSourceTasks,
    groupTimelineSupplierIndex.byTaskId,
    hubVisibleProjects,
    rfqProjects,
    rfqPackages,
  ]);

  const supplierVisibleTaskIds = useMemo(() => {
    if (!supplierTimelineTasks) return null;
    return new Set(supplierTimelineTasks.map((t) => t.id).filter((id): id is string => !!id));
  }, [supplierTimelineTasks]);

  const groupTimelineFilterOptions = useMemo(
    () =>
      buildGroupTimelineFilterOptions({
        projects: hubVisibleProjects,
        groups: taskGroups,
        tasks: groupTimelineSourceTasks,
        projectLabels: groupTimelineProjectLabels,
        supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
        supplierNameByOrgId: groupTimelineSupplierIndex.orgNameById,
      }),
    [
      hubVisibleProjects,
      taskGroups,
      groupTimelineSourceTasks,
      groupTimelineProjectLabels,
      groupTimelineSupplierIndex,
    ],
  );

  const groupTimelineScopedInputs = useMemo(() => {
    if (groupTimelineFamily !== 'all' || countActiveGroupTimelineFilters(groupTimelineFilter) === 0) {
      return {
        projects: hubVisibleProjects,
        groups: taskGroups,
        tasks: supplierTimelineTasks ?? groupTimelineSourceTasks,
      };
    }
    return applyGroupTimelineDetailFilter({
      filter: groupTimelineFilter,
      projects: hubVisibleProjects,
      groups: taskGroups,
      tasks: groupTimelineSourceTasks,
      supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
      simulatedProjectIds: simulateUcProjectIds,
    });
  }, [
    groupTimelineFamily,
    groupTimelineFilter,
    hubVisibleProjects,
    taskGroups,
    groupTimelineSourceTasks,
    supplierTimelineTasks,
    groupTimelineSupplierIndex.byTaskId,
    simulateUcProjectIds,
  ]);

  const groupTimelineBuilt = useMemo(
    () =>
      buildGroupTimelineView({
        family: groupTimelineFamily,
        projects: groupTimelineScopedInputs.projects,
        groups: groupTimelineScopedInputs.groups,
        tasks: groupTimelineScopedInputs.tasks,
        projectLabels: groupTimelineProjectLabels,
        layout: groupTimelineLayout,
        sortMode: groupTimelineSort,
        supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
        supplierNameByOrgId: groupTimelineSupplierIndex.orgNameById,
        // Supplier hub shows the whole Production group, not the staff
        // "Cut Timber Package" supplier-focus subset.
        extraTaskIds: supplierHubMode
          ? groupTimelineScopedInputs.tasks.map((t) => t.id).filter((id): id is string => !!id)
          : groupTimelineExtraTasks[groupTimelineFamily],
      }),
    [
      groupTimelineFamily,
      groupTimelineScopedInputs,
      groupTimelineProjectLabels,
      groupTimelineLayout,
      groupTimelineSort,
      groupTimelineSupplierIndex,
      groupTimelineExtraTasks,
      supplierHubMode,
    ],
  );

  const ganttProposalDealKey = useMemo(() => {
    if (selectedProjectId || supplierHubMode) return '';
    const ids = new Set<string>();
    for (const project of groupTimelineScopedInputs.projects) {
      const id = project.dealId?.trim();
      if (id) ids.add(id);
    }
    return Array.from(ids).sort().join('|');
  }, [selectedProjectId, supplierHubMode, groupTimelineScopedInputs.projects]);

  /** Deal id + hub project id so proposal structures drop untagged pre-split defaults. */
  const ganttProposalStructureLoadKey = useMemo(() => {
    if (selectedProjectId || supplierHubMode) return '';
    const parts: string[] = [];
    for (const project of groupTimelineScopedInputs.projects) {
      const dealId = project.dealId?.trim();
      if (!dealId) continue;
      parts.push(`${dealId}:${project.id || ''}`);
    }
    return parts.sort().join('|');
  }, [selectedProjectId, supplierHubMode, groupTimelineScopedInputs.projects]);

  const [proposalStructuresByDealId, setProposalStructuresByDealId] = useState<Map<string, ProposalStructureRef[]>>(
    () => new Map(),
  );

  useEffect(() => {
    if (!ganttProposalStructureLoadKey) {
      setProposalStructuresByDealId(new Map());
      return;
    }
    let cancelled = false;
    const projects = groupTimelineScopedInputs.projects;
    void Promise.all(
      ganttProposalStructureLoadKey.split('|').map(async (pair) => {
        const [dealId, hubProjectId] = pair.split(':');
        return [
          dealId,
          await loadDealProposalStructures(dealId, { hubProjectId: hubProjectId || null }),
        ] as const;
      }),
    ).then((rows) => {
      if (!cancelled) setProposalStructuresByDealId(new Map(rows));
    });
    return () => {
      cancelled = true;
    };
  }, [ganttProposalStructureLoadKey, groupTimelineScopedInputs.projects]);

  const groupTimelineConflictBoard = useMemo(() => {
    if (selectedProjectId || supplierHubMode) {
      return emptyConflictBoard();
    }
    return buildGroupTimelineConflictBoard({
      projects: groupTimelineScopedInputs.projects,
      groups: groupTimelineScopedInputs.groups,
      tasks: groupTimelineScopedInputs.tasks,
      proposalStructuresByDealId,
      procurementSuppliers: buildGroupTimelineSupplierIndex({
        family: 'procurement',
        projects: groupTimelineScopedInputs.projects,
        tasks: groupTimelineScopedInputs.tasks,
        rfqProjects,
        rfqPackages,
        rfqSubmissions,
        supplierOrgs,
      }),
      productionSuppliers: buildGroupTimelineSupplierIndex({
        family: 'production',
        projects: groupTimelineScopedInputs.projects,
        tasks: groupTimelineScopedInputs.tasks,
        rfqProjects,
        rfqPackages,
        rfqSubmissions,
        supplierOrgs,
      }),
      projectLabels: groupTimelineProjectLabels,
    });
  }, [
    selectedProjectId,
    supplierHubMode,
    groupTimelineScopedInputs,
    groupTimelineProjectLabels,
    proposalStructuresByDealId,
    rfqProjects,
    rfqPackages,
    rfqSubmissions,
    supplierOrgs,
  ]);

  const showConflictTasks = useCallback((issues: GroupTimelineConflictIssues) => {
    const next = detailFilterFromConflictIssues(issues);
    if (!next.taskIds.length && !next.projectIds.length) return;
    setGroupTimelineFilter(next);
    storeGroupTimelineFilter(next);
    selectAllProjectsTimelineFamily('all');
    if (ganttSplitOpen && ganttSplitSync) {
      setGanttSplitFilter(next);
      setGanttSplitFamily('all');
    }
  }, [selectAllProjectsTimelineFamily, ganttSplitOpen, ganttSplitSync]);

  const ganttCollabEnabled =
    viewMode === 'gantt' && !architectHubMode && !supplierHubMode && !!user?.id;

  const ganttCollabMode = useMemo((): GanttTimelineCollaborationMode | null => {
    if (!ganttCollabEnabled) return null;
    if (selectedProjectId) {
      return {
        kind: 'single',
        projectId: selectedProjectId,
        structureFilter,
      };
    }
    return {
      kind: 'all',
      family: groupTimelineFamily,
      colorMode: groupTimelineColorMode,
      layout: groupTimelineLayout,
      sort: groupTimelineSort,
      filter: groupTimelineFilter,
      extraTasks: groupTimelineExtraTasks,
    };
  }, [
    ganttCollabEnabled,
    selectedProjectId,
    structureFilter,
    groupTimelineFamily,
    groupTimelineColorMode,
    groupTimelineLayout,
    groupTimelineSort,
    groupTimelineFilter,
    groupTimelineExtraTasks,
  ]);

  const applyGroupTimelineFamilyFromSync = useCallback((family: GroupTimelineFamily) => {
    setGroupTimelineFamily(family);
    storeGroupTimelineFamily(family);
    setGroupTimelineSearchParams(family);
  }, [setGroupTimelineSearchParams]);

  const applyGroupTimelineColorFromSync = useCallback((mode: GroupTimelineColorMode) => {
    setGroupTimelineColorMode(mode);
    storeGroupTimelineColorMode(mode);
  }, []);

  const applyGroupTimelineLayoutFromSync = useCallback((mode: GroupTimelineLayoutMode) => {
    setGroupTimelineLayout(mode);
    storeGroupTimelineLayout(mode);
  }, []);

  const applyGroupTimelineSortFromSync = useCallback((mode: GroupTimelineSortMode) => {
    setGroupTimelineSort(mode);
    storeGroupTimelineSort(mode);
  }, []);

  const applyGroupTimelineFilterFromSync = useCallback((filter: GroupTimelineDetailFilter) => {
    setGroupTimelineFilter(filter);
    storeGroupTimelineFilter(filter);
  }, []);

  const applyGroupTimelineExtraTasksFromSync = useCallback((map: GroupTimelineExtraTasksByFamily) => {
    setGroupTimelineExtraTasks(map);
    storeGroupTimelineExtraTasks(map);
  }, []);

  useEffect(() => subscribeGroupTimelineColors(doc => setCanonicalColorEntries(doc.entries)), []);

  useEffect(() => {
    if (!user?.id) {
      setSavedTimelineViews([]);
      return undefined;
    }
    return subscribeGroupTimelineSavedViewsForUser(user, setSavedTimelineViews);
  }, [user?.id, user?.role, user?.designations]);

  const captureAllProjectsSavedViewOutline = useCallback(() => {
    if (selectedProjectId) return null;
    const gantt = ganttOutlineApiRef.current?.getOutlineState() ?? {
      collapsedGroups: [],
      collapsedSections: [],
      expandedParentIds: [],
      pinKeys: [],
    };
    return buildSavedViewOutlineFromHubState({
      family: 'all',
      colorMode: groupTimelineColorMode,
      layout: groupTimelineLayout,
      sort: groupTimelineSort,
      filter: groupTimelineFilter,
      extraTasks: groupTimelineExtraTasks,
      gantt,
    });
  }, [
    selectedProjectId,
    groupTimelineColorMode,
    groupTimelineLayout,
    groupTimelineSort,
    groupTimelineFilter,
    groupTimelineExtraTasks,
  ]);

  const applySavedTimelineView = useCallback(
    (view: GroupTimelineSavedView) => {
      selectAllProjectsTimelineFamily('all');
      applySavedViewToHub(view, {
        setGroupTimelineFamily: applyGroupTimelineFamilyFromSync,
        setGroupTimelineColorMode: applyGroupTimelineColorFromSync,
        setGroupTimelineLayout: applyGroupTimelineLayoutFromSync,
        setGroupTimelineSort: applyGroupTimelineSortFromSync,
        setGroupTimelineFilter: applyGroupTimelineFilterFromSync,
        setGroupTimelineExtraTasks: applyGroupTimelineExtraTasksFromSync,
        applyGanttOutline: gantt => ganttOutlineApiRef.current?.applyOutlineState(gantt),
      });
      storeLastAppliedSavedViewId(view.id);
      setActiveSavedTimelineViewId(view.id);
    },
    [
      selectAllProjectsTimelineFamily,
      applyGroupTimelineFamilyFromSync,
      applyGroupTimelineColorFromSync,
      applyGroupTimelineLayoutFromSync,
      applyGroupTimelineSortFromSync,
      applyGroupTimelineFilterFromSync,
      applyGroupTimelineExtraTasksFromSync,
    ],
  );

  const ganttTimelineCollab = useGanttTimelineCollaboration({
    enabled: ganttCollabEnabled && ganttCollabMode != null,
    user: user ?? null,
    mode: ganttCollabMode ?? { kind: 'single', projectId: '_', structureFilter: null },
    ganttOutlineApiRef: ganttOutlineApiRef,
    setStructureFilter,
    setGroupTimelineFamily: applyGroupTimelineFamilyFromSync,
    setGroupTimelineColorMode: applyGroupTimelineColorFromSync,
    setGroupTimelineLayout: applyGroupTimelineLayoutFromSync,
    setGroupTimelineSort: applyGroupTimelineSortFromSync,
    setGroupTimelineFilter: applyGroupTimelineFilterFromSync,
    setGroupTimelineExtraTasks: applyGroupTimelineExtraTasksFromSync,
    onSynced: (peerName) => {
      pushPmToast(
        'success',
        'Following',
        `Your timeline is following ${peerName}, including zoom and pan.`,
      );
    },
  });

  useEffect(() => {
    if (viewMode !== 'gantt' || architectHubMode || supplierHubMode) return;
    if (!ganttTimelineCollab.showCollaborationUi) return;
    const onKey = (e: KeyboardEvent) => {
      if (!shouldHandleProjectHubLetterHotkey(e)) return;
      if (showWorkQueueModal || showQuickSearch || showTimeReport) return;
      if (e.key.toLowerCase() !== 'u') return;
      e.preventDefault();
      ganttUserSyncChipRef.current?.toggleUserSync();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    viewMode,
    architectHubMode,
    supplierHubMode,
    ganttTimelineCollab.showCollaborationUi,
    showWorkQueueModal,
    showQuickSearch,
    showTimeReport,
  ]);

  const conflictTaskFilterKey = groupTimelineFilter.taskIds.join('|');
  useEffect(() => {
    if (selectedProjectId || !conflictTaskFilterKey) return;
    ganttOutlineApiRef.current?.expandAll();
  }, [selectedProjectId, conflictTaskFilterKey]);

  const groupTimelineFamilyParents = useMemo(
    () =>
      listGroupTimelineFamilyParentTasks({
        family: groupTimelineFamily,
        projects: hubVisibleProjects,
        groups: taskGroups,
        tasks: groupTimelineSourceTasks,
      }),
    [groupTimelineFamily, hubVisibleProjects, taskGroups, groupTimelineSourceTasks],
  );

  const groupTimelineFocusOptions = useMemo(() => {
    const visibleIds = new Set(
      groupTimelineBuilt.tasks.filter((t) => t.id && !t.parentTaskId).map((t) => t.id!),
    );
    const extraIds = new Set(groupTimelineExtraTasks[groupTimelineFamily] ?? []);
    const optionFor = (t: ProjectTask) => {
      const label = groupTimelineProjectLabels.get(t.projectId);
      const project = hubVisibleProjects.find((p) => p.id === t.projectId);
      const projectLabel = formatGroupTimelineSectionLabel({
        projectNumber: label?.projectNumber,
        descriptor: label?.descriptor,
        projectName: project?.name,
      });
      return { id: t.id!, label: `${projectLabel} · ${t.title}` };
    };
    const extraTasks = groupTimelineFamilyParents
      .filter((t) => t.id && extraIds.has(t.id) && visibleIds.has(t.id))
      .map(optionFor);
    return { extraTasks };
  }, [
    groupTimelineBuilt.tasks,
    groupTimelineExtraTasks,
    groupTimelineFamily,
    groupTimelineFamilyParents,
    groupTimelineProjectLabels,
    hubVisibleProjects,
  ]);

  const removeGroupTimelineExtraTask = useCallback((taskId: string) => {
    setGroupTimelineExtraTasks((prev) => {
      const current = prev[groupTimelineFamily] ?? [];
      const nextIds = current.filter((id) => id !== taskId);
      const next = { ...prev, [groupTimelineFamily]: nextIds };
      storeGroupTimelineExtraTasks(next);
      return next;
    });
  }, [groupTimelineFamily]);

  const groupTimelineTasks = useMemo(
    () =>
      groupTimelineBuilt.tasks.map((t) => {
        if (!t.id) return t;
        let next = t;
        const dates = ganttDateOverrides[t.id];
        if (dates) {
          next = { ...next, startDate: dates.startDate, endDate: dates.endDate };
        }
        return mergeTaskFieldOverride(next, taskFieldOverrides[t.id]);
      }),
    [groupTimelineBuilt.tasks, ganttDateOverrides, taskFieldOverrides],
  );
  const groupTimelineGroups = useMemo(
    () =>
      applyGroupTimelineGroupColors(groupTimelineBuilt.groups, groupTimelineColorMode, {
        tasks: groupTimelineBuilt.tasks,
        supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
        canonicalColorEntries,
      }),
    [
      groupTimelineBuilt.groups,
      groupTimelineBuilt.tasks,
      groupTimelineColorMode,
      groupTimelineSupplierIndex.byTaskId,
      canonicalColorEntries,
    ],
  );
  const ganttSplitBuilt = useMemo(() => {
    if (selectedProjectId || !ganttSplitOpen) return null;
    const baseTasks = supplierTimelineTasks ?? groupTimelineSourceTasks;
    const scoped = ganttSplitFamily === 'all' && countActiveGroupTimelineFilters(ganttSplitFilter) > 0
      ? applyGroupTimelineDetailFilter({
          filter: ganttSplitFilter,
          projects: hubVisibleProjects,
          groups: taskGroups,
          tasks: groupTimelineSourceTasks,
          supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
          simulatedProjectIds: simulateUcProjectIds,
        })
      : { projects: hubVisibleProjects, groups: taskGroups, tasks: baseTasks };
    return buildGroupTimelineView({
      family: ganttSplitFamily,
      projects: scoped.projects,
      groups: scoped.groups,
      tasks: scoped.tasks,
      projectLabels: groupTimelineProjectLabels,
      layout: ganttSplitLayout,
      sortMode: ganttSplitSort,
      supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
      supplierNameByOrgId: groupTimelineSupplierIndex.orgNameById,
      extraTaskIds: ganttSplitExtraTasks[ganttSplitFamily],
    });
  }, [
    selectedProjectId,
    ganttSplitOpen,
    ganttSplitFamily,
    ganttSplitFilter,
    ganttSplitLayout,
    ganttSplitSort,
    ganttSplitExtraTasks,
    hubVisibleProjects,
    taskGroups,
    supplierTimelineTasks,
    groupTimelineSourceTasks,
    groupTimelineProjectLabels,
    groupTimelineSupplierIndex,
    simulateUcProjectIds,
  ]);
  const ganttSplitTasks = useMemo(
    () =>
      (ganttSplitBuilt?.tasks ?? []).map((t) => {
        if (!t.id) return t;
        const o = ganttDateOverrides[t.id];
        if (!o) return t;
        return { ...t, startDate: o.startDate, endDate: o.endDate };
      }),
    [ganttSplitBuilt, ganttDateOverrides],
  );
  const ganttSplitGroups = useMemo(
    () =>
      ganttSplitBuilt
        ? applyGroupTimelineGroupColors(ganttSplitBuilt.groups, ganttSplitColorMode, {
            tasks: ganttSplitBuilt.tasks,
            supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
            canonicalColorEntries,
          })
        : [],
    [ganttSplitBuilt, ganttSplitColorMode, groupTimelineSupplierIndex.byTaskId, canonicalColorEntries],
  );

  const pushSplitViewport = useCallback((source: 'left' | 'right', viewport: GanttSyncedViewport) => {
    if (!ganttSplitOpen || !ganttSplitSync) return;
    if (splitLeaderRef.current && splitLeaderRef.current !== source) return;
    splitLeaderRef.current = source;
    pendingSplitViewportRef.current = viewport;
    if (splitFrameRef.current != null) return;
    splitFrameRef.current = window.requestAnimationFrame(() => {
      splitFrameRef.current = null;
      const next = pendingSplitViewportRef.current;
      pendingSplitViewportRef.current = null;
      const leader = splitLeaderRef.current;
      if (!next || !leader) return;
      const target = leader === 'left' ? ganttSplitApiRef : ganttOutlineApiRef;
      target.current?.applyViewport(next);
      if (splitApplyTimerRef.current != null) window.clearTimeout(splitApplyTimerRef.current);
      splitApplyTimerRef.current = window.setTimeout(() => {
        splitApplyTimerRef.current = null;
        if (splitLeaderRef.current === leader) splitLeaderRef.current = null;
      }, 500);
    });
  }, [ganttSplitOpen, ganttSplitSync]);

  useEffect(() => {
    if (!ganttSplitOpen) splitOutlineSeededRef.current = false;
  }, [ganttSplitOpen]);

  useEffect(() => {
    if (!ganttSplitOpen || !ganttSplitSync || selectedProjectId) return;
    splitOutlineSeededRef.current = false;
    let frames = 0;
    let raf = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      frames += 1;
      const viewport = ganttOutlineApiRef.current?.getViewport();
      const api = ganttSplitApiRef.current;
      if (!viewport || !api) {
        if (frames < 45) raf = window.requestAnimationFrame(tick);
        return;
      }
      if (!splitOutlineSeededRef.current) {
        const outline = ganttOutlineApiRef.current?.getOutlineState();
        if (outline) {
          matchingOutlineRef.current = true;
          api.applyOutlineState(outline);
          splitOutlineSeededRef.current = true;
          window.setTimeout(() => {
            matchingOutlineRef.current = false;
          }, 200);
        }
      }
      splitLeaderRef.current = 'left';
      api.applyViewport(viewport);
      const applied = api.getViewport();
      const aligned = applied.zoom === viewport.zoom
        && Math.abs(applied.anchorMs - viewport.anchorMs) < 12 * 60 * 60 * 1000
        && Math.abs(applied.scrollTop - viewport.scrollTop) < 4
        && (
          !viewport.columnWidth
          || !applied.columnWidth
          || Math.abs(applied.columnWidth - viewport.columnWidth) < 1
        );
      if (!aligned && frames < 45) {
        raf = window.requestAnimationFrame(tick);
        return;
      }
      if (splitApplyTimerRef.current != null) window.clearTimeout(splitApplyTimerRef.current);
      splitApplyTimerRef.current = window.setTimeout(() => {
        splitApplyTimerRef.current = null;
        if (splitLeaderRef.current === 'left') splitLeaderRef.current = null;
      }, 500);
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
    };
  }, [
    ganttSplitOpen,
    ganttSplitSync,
    ganttSplitFamily,
    ganttSplitLayout,
    ganttSplitSort,
    ganttSplitColorMode,
    ganttSplitFilter,
    ganttSplitSeedZoom,
    selectedProjectId,
  ]);

  const openGanttSplit = useCallback(() => {
    const viewport = ganttOutlineApiRef.current?.getViewport();
    if (viewport) setGanttSplitSeedZoom(viewport.zoom);
    setGanttSplitFamily(groupTimelineFamily);
    setGanttSplitLayout(groupTimelineLayout);
    setGanttSplitSort(groupTimelineSort);
    setGanttSplitColorMode(groupTimelineColorMode);
    setGanttSplitFilter(groupTimelineFilter);
    setGanttSplitExtraTasks(groupTimelineExtraTasks);
    setGanttSplitPinnedCount(0);
    setGanttSplitSync(true);
    setGanttSplitOpen(true);
  }, [
    groupTimelineFamily,
    groupTimelineLayout,
    groupTimelineSort,
    groupTimelineColorMode,
    groupTimelineFilter,
    groupTimelineExtraTasks,
  ]);

  toggleSplitRef.current = () => {
    if (ganttSplitOpen) setGanttSplitOpen(false);
    else openGanttSplit();
  };
  toggleMatchRef.current = () => {
    if (!ganttSplitOpen) return;
    if (ganttSplitSync) {
      setGanttSplitSync(false);
      return;
    }
    setGanttSplitFamily(groupTimelineFamily);
    setGanttSplitLayout(groupTimelineLayout);
    setGanttSplitSort(groupTimelineSort);
    setGanttSplitColorMode(groupTimelineColorMode);
    setGanttSplitFilter(groupTimelineFilter);
    setGanttSplitExtraTasks(groupTimelineExtraTasks);
    setGanttSplitSync(true);
    const outline = ganttOutlineApiRef.current?.getOutlineState();
    if (outline) {
      matchingOutlineRef.current = true;
      ganttSplitApiRef.current?.applyOutlineState(outline);
      window.setTimeout(() => {
        matchingOutlineRef.current = false;
      }, 200);
    }
  };

  useEffect(() => {
    if (selectedProjectId) setGanttSplitOpen(false);
  }, [selectedProjectId]);

  const groupTimelineProjectIds = useMemo(
    () => new Set(groupTimelineGroups.map((g) => g.projectId)),
    [groupTimelineGroups],
  );
  const groupTimelinePhaseById = useMemo(() => {
    const map = new Map<string, ProjectPhase | undefined>();
    for (const p of hubVisibleProjects) {
      if (p.id) map.set(p.id, p.phase);
    }
    return map;
  }, [hubVisibleProjects]);
  const groupTimelineColorCtx = useMemo(
    () => ({
      groups: groupTimelineGroups,
      projectPhaseById: groupTimelinePhaseById,
      supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
      supplierNameByOrgId: groupTimelineSupplierIndex.orgNameById,
      canonicalColorEntries,
    }),
    [groupTimelineGroups, groupTimelinePhaseById, groupTimelineSupplierIndex, canonicalColorEntries],
  );
  const groupTimelineLegend = useMemo(
    () => buildGroupTimelineColorLegend(groupTimelineColorMode, groupTimelineTasks, groupTimelineColorCtx),
    [groupTimelineColorMode, groupTimelineTasks, groupTimelineColorCtx],
  );
  const resolveGroupTimelineTaskBarColor = useCallback(
    (task: ProjectTask) => resolveGroupTimelineBarColor(task, groupTimelineColorMode, groupTimelineColorCtx),
    [groupTimelineColorMode, groupTimelineColorCtx],
  );
  const ganttSplitColorCtx = useMemo(
    () => ({
      groups: ganttSplitGroups,
      projectPhaseById: groupTimelinePhaseById,
      supplierByTaskId: groupTimelineSupplierIndex.byTaskId,
      supplierNameByOrgId: groupTimelineSupplierIndex.orgNameById,
      canonicalColorEntries,
    }),
    [ganttSplitGroups, groupTimelinePhaseById, groupTimelineSupplierIndex, canonicalColorEntries],
  );
  const ganttSplitLegend = useMemo(
    () => buildGroupTimelineColorLegend(ganttSplitColorMode, ganttSplitTasks, ganttSplitColorCtx),
    [ganttSplitColorMode, ganttSplitTasks, ganttSplitColorCtx],
  );
  const resolveSplitTaskBarColor = useCallback(
    (task: ProjectTask) => resolveGroupTimelineBarColor(task, ganttSplitColorMode, ganttSplitColorCtx),
    [ganttSplitColorMode, ganttSplitColorCtx],
  );
  const ganttSplitFamilyParents = useMemo(
    () =>
      listGroupTimelineFamilyParentTasks({
        family: ganttSplitFamily,
        projects: hubVisibleProjects,
        groups: taskGroups,
        tasks: groupTimelineSourceTasks,
      }),
    [ganttSplitFamily, hubVisibleProjects, taskGroups, groupTimelineSourceTasks],
  );
  const ganttSplitFocusTasks = useMemo(() => {
    if (!ganttSplitBuilt) return [];
    const visibleIds = new Set(
      ganttSplitBuilt.tasks.filter((t) => t.id && !t.parentTaskId).map((t) => t.id!),
    );
    const extraIds = new Set(ganttSplitExtraTasks[ganttSplitFamily] ?? []);
    return ganttSplitFamilyParents
      .filter((t) => t.id && extraIds.has(t.id) && visibleIds.has(t.id))
      .map((t) => {
        const label = groupTimelineProjectLabels.get(t.projectId);
        const project = hubVisibleProjects.find((p) => p.id === t.projectId);
        const projectLabel = formatGroupTimelineSectionLabel({
          projectNumber: label?.projectNumber,
          descriptor: label?.descriptor,
          projectName: project?.name,
        });
        return { id: t.id!, label: `${projectLabel} · ${t.title}` };
      });
  }, [
    ganttSplitBuilt,
    ganttSplitExtraTasks,
    ganttSplitFamily,
    ganttSplitFamilyParents,
    groupTimelineProjectLabels,
    hubVisibleProjects,
  ]);
  const showSplitConflictTasks = useCallback((issues: GroupTimelineConflictIssues) => {
    const next = detailFilterFromConflictIssues(issues);
    if (!next.taskIds.length && !next.projectIds.length) return;
    setGanttSplitFilter(next);
    setGanttSplitFamily('all');
    if (ganttSplitSync) {
      setGroupTimelineFilter(next);
      storeGroupTimelineFilter(next);
      selectAllProjectsTimelineFamily('all');
    }
  }, [ganttSplitSync, selectAllProjectsTimelineFamily]);
  const removeSplitExtraTask = useCallback((taskId: string) => {
    setGanttSplitExtraTasks((prev) => {
      const current = prev[ganttSplitFamily] ?? [];
      return { ...prev, [ganttSplitFamily]: current.filter((id) => id !== taskId) };
    });
  }, [ganttSplitFamily]);

  const taskPanelDealId = useMemo(() => {
    if (currentProject?.dealId) return currentProject.dealId;
    const projectId = selectedTask?.projectId;
    if (!projectId) return null;
    return timberProjects.find((p) => p.id === projectId)?.dealId?.trim() || null;
  }, [currentProject?.dealId, selectedTask?.projectId, timberProjects]);

  const taskPanelParentTask = useMemo(() => {
    const parentId = selectedTask?.parentTaskId;
    if (!parentId) return panelParentTask;
    if (panelParentTask?.id === parentId) return panelParentTask;
    return projectTasks.find((t) => t.id === parentId)
      ?? groupTimelineSourceTasks.find((t) => t.id === parentId)
      ?? panelParentTask;
  }, [selectedTask?.parentTaskId, panelParentTask, projectTasks, groupTimelineSourceTasks]);

  const taskPanelSupplierCapability =
    supplierCapabilityForTaskTitle(selectedTask?.title)
    || supplierCapabilityForTaskTitle(taskPanelParentTask?.title);

  const taskPanelShowSupplier = !!(
    selectedTask
    && (
      isGroupTimelineSupplierAssignableTitle(selectedTask.title)
      || isGroupTimelineSupplierAssignableTitle(taskPanelParentTask?.title)
    )
  );

  const taskPanelSupplierOptions = useMemo(
    () => {
      const parentId = selectedTask?.parentTaskId || selectedTask?.id;
      const childOrgIds = parentId
        ? groupTimelineSourceTasks
          .filter((t) => t.parentTaskId === parentId)
          .flatMap((t) => t.supplierOrgIds ?? [])
        : [];
      return supplierOptionsForTaskPanel({
        dealId: taskPanelDealId,
        rfqProjects,
        supplierOrgs,
        capability: taskPanelSupplierCapability,
        extraOrgIds: [
          ...(selectedTask?.supplierOrgIds ?? []),
          ...(taskPanelParentTask?.supplierOrgIds ?? []),
          ...childOrgIds,
        ],
        includeAllOrgs: taskPanelShowSupplier,
      });
    },
    [
      taskPanelDealId,
      rfqProjects,
      supplierOrgs,
      taskPanelSupplierCapability,
      selectedTask?.id,
      selectedTask?.parentTaskId,
      selectedTask?.supplierOrgIds,
      taskPanelParentTask?.supplierOrgIds,
      groupTimelineSourceTasks,
      taskPanelShowSupplier,
    ],
  );

  const taskPanelTeamMembers = useMemo(() => {
    const base = buildProjectHubTeamMembers({
      crmUsers,
      dealId: taskPanelDealId || scopedDealId,
      productionCommentAccess,
      rfqProjects,
      rfqSubmissions,
      timberProjects,
      supplierOrgs,
    });
    const extraIds = [
      ...(selectedTask?.assignedTo ?? []),
      selectedTask?.actionRequired || '',
    ].filter(Boolean);
    if (extraIds.length === 0) return base;
    const seen = new Set(base.map((u) => u.uid));
    const extra = extraIds
      .map((id) => crmUsers.find((u) => u.uid === id || (u.email || '').trim() === id))
      .filter((u): u is typeof crmUsers[number] => !!u && !seen.has(u.uid));
    return extra.length ? [...base, ...extra] : base;
  }, [
    crmUsers,
    taskPanelDealId,
    scopedDealId,
    productionCommentAccess,
    rfqProjects,
    rfqSubmissions,
    timberProjects,
    supplierOrgs,
    selectedTask?.assignedTo,
    selectedTask?.actionRequired,
  ]);

  const derivedSupplierLabelForTask = useCallback((task: ProjectTask) => {
    const quoted = quoteDerivedSupplierLabel(task, groupTimelineSupplierIndex);
    if (!quoted) return null;
    const assigned = (task.supplierOrgIds ?? []).filter(Boolean);
    if (assigned.length) {
      const assignedNames = assigned
        .map((id) => groupTimelineSupplierIndex.orgNameById.get(id) || id)
        .join(', ');
      if (assignedNames === quoted) return null;
    }
    return quoted;
  }, [groupTimelineSupplierIndex]);

  const taskPanelDerivedSupplierLabel = selectedTask
    ? derivedSupplierLabelForTask(selectedTask)
    : null;

  const resolveTaskProjectId = useCallback((taskId: string | undefined): string | undefined => {
    if (selectedProjectId) return selectedProjectId;
    taskId = unwrapSupplierDisplayTaskId(taskId) ?? taskId;
    if (!taskId) return undefined;
    const parsed = parseOnboardingBridgeTaskId(taskId);
    if (parsed?.projectId) return parsed.projectId;
    const fromReal = projectTasks.find((t) => t.id === taskId);
    if (fromReal?.projectId) return fromReal.projectId;
    const fromSource = groupTimelineSourceTasks.find((t) => t.id === taskId);
    if (fromSource?.projectId) return fromSource.projectId;
    return tasksForViewsBaseRef.current.find((t) => t.id === taskId)?.projectId;
  }, [selectedProjectId, projectTasks, groupTimelineSourceTasks]);

  const childTasksByParent = useMemo(
    () =>
      buildChildTasksByParent(
        supplierVisibleTaskIds
          ? tasksForViews.filter((t) => !t.id || supplierVisibleTaskIds.has(t.id))
          : tasksForViews,
      ),
    [tasksForViews, supplierVisibleTaskIds],
  );

  const syncParentRollupForTask = useCallback(
    async (
      taskId: string,
      dateOverride?: { startDate: Date; endDate: Date },
    ) => {
      const rollupSource = [
        ...mirrorAllOnboardingTasks(projectOnboardings, taskGroups),
        ...projectTasks,
      ];
      const patch = parentRollupPatchAfterChildChange(taskId, rollupSource, dateOverride);
      if (!patch) return;
      if (isOnboardingBridgeTaskId(patch.parentId)) {
        const parsed = parseOnboardingBridgeTaskId(patch.parentId);
        const projectId =
          parsed?.projectId
          || projectTasks.find((t) => t.id === taskId)?.projectId
          || selectedProjectId;
        if (!projectId || !user) return;
        await updateOnboardingItem(
          projectId,
          parsed?.itemId || onboardingTaskIdToItemId(patch.parentId),
          { startDate: patch.startDate, endDate: patch.endDate },
          user.name || user.email || 'Unknown',
        );
        return;
      }
      await updateProjectTask(patch.parentId, {
        startDate: patch.startDate,
        endDate: patch.endDate,
      });
    },
    [
      projectOnboardings,
      taskGroups,
      projectTasks,
      selectedProjectId,
      user,
      updateOnboardingItem,
      updateProjectTask,
    ],
  );

  const tableTasksForViews = useMemo(
    () =>
      tasksForViews.filter(
        (t) => !t.parentTaskId && (!supplierVisibleTaskIds || !t.id || supplierVisibleTaskIds.has(t.id)),
      ),
    [tasksForViews, supplierVisibleTaskIds],
  );

  useEffect(() => {
    const dealId = currentProject?.dealId;
    if (!dealId || supplierHubMode) {
      setDealStructures([]);
      return;
    }
    let cancelled = false;
    loadProposalStructures(dealId)
      .then((structures) => {
        if (!cancelled) setDealStructures(structures);
      })
      .catch(() => {
        if (!cancelled) setDealStructures([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentProject?.dealId, supplierHubMode]);

  useEffect(() => {
    if (!selectedProjectId || supplierHubMode) return;
    if (subtaskMigrationRanRef.current.has(selectedProjectId)) return;
    const needs = tasksNeedingSubtaskMigration(filteredTasks);
    if (needs.length === 0) {
      subtaskMigrationRanRef.current.add(selectedProjectId);
      return;
    }
    subtaskMigrationRanRef.current.add(selectedProjectId);
    void (async () => {
      for (const parent of needs) {
        try {
          await migrateLegacySubtasksForParent(parent, createProjectTask, updateProjectTask);
        } catch (err) {
          console.error('Subtask migration failed for', parent.id, err);
        }
      }
    })();
  }, [selectedProjectId, supplierHubMode, filteredTasks, createProjectTask, updateProjectTask]);

  const taskDeepLinkWarnedRef = useRef(false);
  useEffect(() => {
    taskDeepLinkWarnedRef.current = false;
  }, [urlProjectId]);

  /** Slack / bookmark / Forecast URL: ?task=&taskComment= — open task panel (see TaskComments). */
  useEffect(() => {
    const tid = searchParams.get('task')?.trim();
    if (!tid || !selectedProjectId || selectedProjectId !== urlProjectId) return;

    // Forecast install/job-cost anchors and Slack links land here before scoped
    // hub listeners have delivered their first snapshot. Opening as soon as the
    // task appears; only toast "not found" after hydration so we don't flash a
    // false "Could not open this task" (CRM feedback #52).
    const task = findProjectTaskForDeepLink(tid, selectedProjectId, tasksForViews);
    if (!task) {
      if (scopedActive && !scopedData?.ready) return;
      if (!taskDeepLinkWarnedRef.current) {
        taskDeepLinkWarnedRef.current = true;
        pushPmToast(
          'error',
          'Could not open this task',
          'The comment is on a Project Hub task. Find it in the task list for this project, or use the notification bell.',
        );
      }
      return;
    }
    taskDeepLinkWarnedRef.current = false;

    const cid = searchParams.get('taskComment')?.trim();
    setViewMode('table');
    if (user?.id && task.id) markTaskAsRead(user.id, task.id);
    // Comments now live on the group line — route legacy task-comment links into
    // the consolidated group thread (it aggregates this task's existing comments).
    if (cid) {
      const opened = openGroupCommentsForTask(task, cid);
      if (!opened) setSelectedTask(task);
    } else {
      setSelectedTask(task);
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('task');
        next.delete('taskComment');
        return next;
      },
      { replace: true },
    );
  }, [
    searchParams,
    selectedProjectId,
    urlProjectId,
    tasksForViews,
    scopedActive,
    scopedData?.ready,
    setSearchParams,
    user?.id,
    pushPmToast,
    openGroupCommentsForTask,
  ]);

  /** Group-comment deep link: ?group={key}&taskComment={id}&discussion= — open the matching tab. */
  useEffect(() => {
    const gkey = searchParams.get('group')?.trim();
    if (!gkey || !selectedProjectId || selectedProjectId !== urlProjectId) return;

    const cid = searchParams.get('taskComment')?.trim() || '';
    const planReviewId = searchParams.get('planReview')?.trim();
    const discussion = searchParams.get('discussion')?.trim() || '';

    const clearDeepLink = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('group');
          next.delete('taskComment');
          next.delete('planReview');
          next.delete('discussion');
          return next;
        },
        { replace: true },
      );
    };

    const tabForAudience = (audience?: unknown): GroupCommentsTab =>
      resolveGroupCommentsTab({
        discussion,
        commentAudience: audience,
        groupCommentKey: gkey,
      });

    // Supplier Production comments use the deal-scoped carve-out — do not require a
    // matching TaskGroup row (schedule may only mirror Production after hydration).
    if (supplierHubMode && isProductionCommentKey(gkey)) {
      const project = timberProjects.find((p) => p.id === selectedProjectId);
      const dealId = project?.dealId?.trim() || dealIdFromProductionCommentKey(gkey);
      if (!dealId) return;
      // Wait until the awarded project list is hydrated.
      if (!timberProjects.some((p) => p.id === selectedProjectId)) return;
      setViewMode('table');
      openGroupCommentsForContext(
        buildSupplierProductionGroupContext({ dealId, hubProjectId: selectedProjectId }),
        cid || null,
        { planReviewId: planReviewId || null, initialTab: tabForAudience() },
      );
      clearDeepLink();
      return;
    }

    const sepIdx = gkey.indexOf('::');
    const wantedName = sepIdx >= 0 ? gkey.slice(sepIdx + 2) : '';
    const group =
      taskGroups.find(
        (g) => g.projectId === selectedProjectId && normalizeGroupName(g.name) === wantedName,
      ) ||
      taskGroups.find((g) => {
        const proj = timberProjects.find((p) => p.id === g.projectId);
        const base = proj?.dealId?.trim() || proj?.id || g.projectId;
        return buildGroupCommentKey(base, g.name) === gkey;
      });
    if (!group) return;

    const currentProj = timberProjects.find((p) => p.id === group.projectId);
    const ctx = collectGroupCommentContext(
      group,
      currentProj,
      timberProjects,
      taskGroups,
      projectTasks,
      projectOnboardings,
    );

    const openResolved = (audience?: unknown) => {
      setViewMode('table');
      openGroupCommentsForContext(ctx, cid || null, {
        planReviewId: planReviewId || null,
        initialTab: tabForAudience(audience),
      });
      clearDeepLink();
    };

    if (discussion || !cid) {
      openResolved(cid ? taskComments.find((c) => c.id === cid)?.audience : undefined);
      return;
    }

    const cached = taskComments.find((c) => c.id === cid);
    if (cached) {
      openResolved(cached.audience);
      return;
    }

    let cancelled = false;
    void getDoc(doc(db, 'taskComments', cid)).then((snap) => {
      if (cancelled) return;
      openResolved(snap.data()?.audience);
    });
    return () => {
      cancelled = true;
    };
  }, [
    searchParams,
    selectedProjectId,
    urlProjectId,
    supplierHubMode,
    taskGroups,
    timberProjects,
    projectTasks,
    projectOnboardings,
    taskComments,
    openGroupCommentsForContext,
    setSearchParams,
  ]);

  /**
   * Architect email / designer alias deep link: `/project-hub/:id?discussion=architect&taskComment=`
   * without `group=` — open the Design group Architect discussion (same as portal popout).
   */
  useEffect(() => {
    if (searchParams.get('group')?.trim()) return;
    const discussion = searchParams.get('discussion')?.trim();
    if (discussion !== 'architect') return;
    if (!selectedProjectId || selectedProjectId !== urlProjectId) return;

    const cid = searchParams.get('taskComment')?.trim() || '';
    const designGroup = taskGroups.find(
      (g) => g.projectId === selectedProjectId && isDesignGroupName(g.name),
    );
    if (!designGroup) return;

    const currentProj = timberProjects.find((p) => p.id === selectedProjectId);
    const ctx = collectGroupCommentContext(
      designGroup,
      currentProj,
      timberProjects,
      taskGroups,
      projectTasks,
      projectOnboardings,
    );

    setViewMode('table');
    openGroupCommentsForContext(ctx, cid || null, { initialTab: 'architect' });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('discussion');
        next.delete('taskComment');
        return next;
      },
      { replace: true },
    );
  }, [
    searchParams,
    selectedProjectId,
    urlProjectId,
    taskGroups,
    timberProjects,
    projectTasks,
    projectOnboardings,
    openGroupCommentsForContext,
    setSearchParams,
  ]);

  /** Plan review summary deep link: ?planReview={id} (optional ?group=) — Design group thread + summary modal. */
  useEffect(() => {
    const planReviewId = searchParams.get('planReview')?.trim();
    if (!planReviewId || searchParams.get('group')?.trim()) return;
    if (!selectedProjectId || selectedProjectId !== urlProjectId) return;

    const currentProj = timberProjects.find((p) => p.id === selectedProjectId);
    const keyBase = currentProj?.dealId?.trim() || currentProj?.id || selectedProjectId;
    const gkey = buildGroupCommentKey(keyBase, 'design');
    const wantedName = 'design';
    const group =
      taskGroups.find(
        (g) => g.projectId === selectedProjectId && normalizeGroupName(g.name) === wantedName,
      ) ||
      taskGroups.find((g) => {
        const proj = timberProjects.find((p) => p.id === g.projectId);
        const base = proj?.dealId?.trim() || proj?.id || g.projectId;
        return buildGroupCommentKey(base, g.name) === gkey;
      });
    if (!group) return;

    const ctx = collectGroupCommentContext(
      group,
      currentProj,
      timberProjects,
      taskGroups,
      projectTasks,
      projectOnboardings,
    );
    setViewMode('table');
    openGroupCommentsForContext(ctx, null, { planReviewId });

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('planReview');
        return next;
      },
      { replace: true },
    );
  }, [
    searchParams,
    selectedProjectId,
    urlProjectId,
    taskGroups,
    timberProjects,
    projectTasks,
    projectOnboardings,
    openGroupCommentsForContext,
    setSearchParams,
  ]);

  // Open task when navigating from notification (task_assigned, task_comment, etc.)
  useEffect(() => {
    const st = location.state as {
      openTaskId?: string;
      highlightTaskCommentId?: string;
      openTaskCommentsPopout?: boolean;
    } | null;
    const openTaskId = st?.openTaskId;
    if (!openTaskId || !selectedProjectId) return;
    const task = findProjectTaskForDeepLink(openTaskId, selectedProjectId, tasksForViews);
    if (!task) return;

    setViewMode('table');
    if (user?.id && task.id) markTaskAsRead(user.id, task.id);
    // Comment notifications now open the consolidated group thread.
    if (st?.openTaskCommentsPopout || st?.highlightTaskCommentId) {
      const opened = openGroupCommentsForTask(task, st?.highlightTaskCommentId ?? null);
      if (!opened) setSelectedTask(task);
    } else {
      setSelectedTask(task);
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, selectedProjectId, tasksForViews, navigate, user?.id, openGroupCommentsForTask]);

  /**
   * Direct-open the PDF / image viewer when arriving from the All-Projects unread
   * comments modal (`location.state.openFileViewer`). Bypasses the Files tab so
   * the user lands on the actual annotation pin regardless of how the
   * annotation's `documentId` was stored historically.
   *
   * Always re-resolve via `projectFiles` — annotation `documentUrl` can be a
   * stale Firebase download token, which pdf.js surfaces as "Invalid PDF".
   */
  useEffect(() => {
    const st = location.state as {
      openFileViewer?: {
        kind: 'pdf' | 'image' | 'csv';
        url: string;
        title: string;
        documentId: string;
        annotationId: string;
        pageNumber?: number;
      };
    } | null;
    const fv = st?.openFileViewer;
    if (!fv || !selectedProjectId) return;

    let cancelled = false;
    void (async () => {
      const resolved = await resolveAnnotationViewerOpenTarget({
        documentId: fv.documentId,
        documentUrl: fv.url,
        title: fv.title,
      });
      if (cancelled) return;
      if (!resolved) {
        pushPmToast(
          'error',
          'Could not open this file comment',
          'The file may have been removed or you may not have access. Try opening it from the Files tab.',
        );
        navigate(location.pathname, { replace: true, state: {} });
        return;
      }

      if (fv.kind === 'image') {
        if (!currentDeal?.leadId) return;
        setTaskImageViewer({
          url: resolved.url,
          title: resolved.title || fv.title,
          documentId: resolved.documentId || fv.documentId,
          mimeType: resolved.mimeType,
          annotationId: fv.annotationId,
        });
      } else if (fv.kind === 'csv') {
        setTaskCsvViewer({
          url: resolved.url,
          title: resolved.title || fv.title,
          documentId: resolved.documentId || fv.documentId,
          annotationId: fv.annotationId,
        });
      } else {
        if (!currentDeal?.leadId) return;
        setTaskPdfViewer({
          url: resolved.url,
          title: resolved.title || fv.title,
          documentId: resolved.documentId || fv.documentId,
          annotationId: fv.annotationId,
          pageNumber: fv.pageNumber,
        });
      }

      navigate(location.pathname, { replace: true, state: {} });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    location.state,
    location.pathname,
    selectedProjectId,
    currentDeal?.leadId,
    navigate,
    pushPmToast,
  ]);

  const projectSchedulingNodes = useMemo((): ProjectTask[] => {
    if (selectedProjectId) {
      const tasks = tasksForViews.filter((t) => t.projectId === selectedProjectId && t.id);
      const ms = milestones.filter((m) => m.projectId === selectedProjectId && m.id);
      const pseudo = ms.map((m) => {
        const p = milestoneToPseudoTask(m);
        const o = p.id ? ganttDateOverrides[p.id] : undefined;
        if (!o) return p;
        return { ...p, startDate: o.startDate, endDate: o.endDate };
      });
      return [...tasks, ...pseudo];
    }
    if (viewMode !== 'gantt') return [];
    return groupTimelineSourceTasks
      .filter((t) => t.id && groupTimelineProjectIds.has(t.projectId))
      .map((t) => {
        const o = t.id ? ganttDateOverrides[t.id] : undefined;
        if (!o) return t;
        return { ...t, startDate: o.startDate, endDate: o.endDate };
      });
  }, [tasksForViews, milestones, selectedProjectId, ganttDateOverrides, viewMode, groupTimelineSourceTasks, groupTimelineProjectIds]);

  /** Firestore-backed dates only (no `ganttDateOverrides`). Used when diffing schedules so a pending dependency prompt overlay cannot mask unsaved calendar state. */
  const schedulingBaselineNodes = useMemo((): ProjectTask[] => {
    if (selectedProjectId) {
      const tasks = tasksForViewsBase.filter((t) => t.projectId === selectedProjectId && t.id);
      const ms = milestones.filter((m) => m.projectId === selectedProjectId && m.id);
      const pseudo = ms.map((m) => milestoneToPseudoTask(m));
      return [...tasks, ...pseudo];
    }
    if (viewMode !== 'gantt') return [];
    return groupTimelineSourceTasks.filter((t) => t.id && groupTimelineProjectIds.has(t.projectId));
  }, [tasksForViewsBase, milestones, selectedProjectId, viewMode, groupTimelineSourceTasks, groupTimelineProjectIds]);

  const schedulingBaselineNodesRef = useRef(schedulingBaselineNodes);
  schedulingBaselineNodesRef.current = schedulingBaselineNodes;

  const installWeatherDateYmds = useMemo(() => {
    if (!selectedProjectId || viewMode !== 'gantt') return [];
    const ymds: string[] = [];
    for (const t of tasksForViews) {
      if (t.projectId !== selectedProjectId || t.department !== 'installation' || !t.startDate || !t.endDate) {
        continue;
      }
      ymds.push(...listHorizonYmdsInTaskRange(new Date(t.startDate), new Date(t.endDate)));
    }
    return ymds;
  }, [tasksForViews, selectedProjectId, viewMode]);

  const installWeatherSites = useMemo(() => {
    if (!currentProject?.id) return [];
    return [
      {
        projectId: currentProject.id,
        coordinates: currentProject.coordinates,
        location: currentProject.location,
        address: currentProject.address,
      },
    ];
  }, [currentProject]);

  const installWeather = useInstallWeatherForecast({
    sites: installWeatherSites,
    dateYmds: installWeatherDateYmds,
    enabled: viewMode === 'gantt' && !!currentProject?.id && installWeatherDateYmds.length > 0,
  });

  const getInstallTaskRangeRainyDays = useCallback(
    (start: Date, end: Date) =>
      installWeather.getTaskRangeRainyDays(start, end, currentProject?.id),
    [installWeather, currentProject?.id],
  );

  const [timelineUndoStack, setTimelineUndoStack] = useState<TimelineUndoSnapshot[]>([]);
  const [timelineUndoBusy, setTimelineUndoBusy] = useState(false);

  useEffect(() => {
    setTimelineUndoStack([]);
  }, [selectedProjectId]);

  const pushTimelineUndo = useCallback((snap: TimelineUndoSnapshot | null) => {
    if (!snap?.patches?.length) return;
    setTimelineUndoStack((prev) => [...prev.slice(-(TIMELINE_UNDO_MAX_STACK - 1)), snap]);
  }, []);

  /**
   * After enforceAllFsDependencies, re-infer FS/SS lags from final dates (same as bar drag ripple)
   * so stored edges match the bars. Otherwise the UI can show an old lag while the schedule moved.
   *
   * For **updateDependencyLag**, do not reinfer that successor’s edges: with multiple predecessors,
   * calendar-implied lags can disagree with stored lags on non-binding edges; reinfer would rewrite
   * every edge from dates and appear to change deps the user did not edit.
   */
  const persistScheduleAfterEnforce = useCallback(
    async (
      mergedTasks: ProjectTask[],
      dates: Map<string, TaskDateState>,
      successorId: string,
      debugSource?: string,
    ): Promise<{ changes: ReturnType<typeof diffDateChanges>; lagPatches: Map<string, TaskDependencyEdge[]> } | undefined> => {
      const changes = diffDateChanges(schedulingBaselineNodes, dates);
      let lagPatches = reinferFsLagsForDates(mergedTasks, dates);
      if (debugSource === 'updateDependencyLag') {
        lagPatches = new Map(lagPatches);
        lagPatches.delete(successorId);
      }
      if (debugSource && isGanttScheduleDebugEnabled()) {
        logPersistScheduleSnapshot(debugSource, successorId, schedulingBaselineNodes, dates, changes, lagPatches);
      }
      const mergedSucc = mergedTasks.find((t) => t.id === successorId);
      if (!mergedSucc?.id) return undefined;
      const succEdgesResolved = lagPatches.get(successorId) ?? getDependencyEdges(mergedSucc);
      const actor = user?.name || user?.email || 'Unknown';

      setGanttDateOverrides((prev) => {
        const next = { ...prev };
        for (const c of changes) {
          next[c.id] = { startDate: c.startDate, endDate: c.endDate };
        }
        return next;
      });

      const changeById = new Map(changes.map((c) => [c.id, c]));
      const allIds = new Set<string>([...changes.map((c) => c.id), ...Array.from(lagPatches.keys()), successorId]);

      const undoProjectId = selectedProjectId || resolveTaskProjectId(successorId);
      const undoSnap =
        undoProjectId && allIds.size > 0
          ? captureTimelineUndoSnapshot(schedulingBaselineNodesRef.current, undoProjectId, allIds)
          : null;

      for (const tid of Array.from(allIds)) {
        const c = changeById.get(tid);
        const edgeList = tid === successorId ? succEdgesResolved : lagPatches.get(tid);
        if (!c && !edgeList) continue;

        if (isOnboardingBridgeTaskId(tid)) {
          const onboardingProjectId = resolveTaskProjectId(tid);
          if (!onboardingProjectId || !user) continue;
          const itemId = onboardingTaskIdToItemId(tid);
          const patch: Partial<OnboardingItem> = {};
          if (c) {
            patch.startDate = c.startDate;
            patch.endDate = c.endDate;
          }
          if (edgeList) patch.dependencyEdges = edgeList;
          await updateOnboardingItem(onboardingProjectId, itemId, patch, actor);
        } else if (isMilestoneBridgeId(tid)) {
          const patch: Partial<Milestone> = {};
          if (c) patch.targetDate = c.endDate;
          if (edgeList) patch.dependencyEdges = edgeList;
          await updateMilestone(parseMilestoneBridgeId(tid), patch);
        } else {
          const patch: Partial<ProjectTask> = {};
          if (c) {
            patch.startDate = c.startDate;
            patch.endDate = c.endDate;
          }
          if (edgeList) Object.assign(patch, dependencyPayload(edgeList));
          await updateProjectTask(tid, patch);
        }
      }
      const holdUntil = Date.now() + 1200;
      for (const tid of Array.from(allIds)) {
        ganttOverrideHoldUntilRef.current[tid] = holdUntil;
      }
      window.setTimeout(() => {
        pruneMatchingGanttOverrides();
      }, 1350);
      pushTimelineUndo(undoSnap);
      return { changes, lagPatches };
    },
    [
      schedulingBaselineNodes,
      selectedProjectId,
      resolveTaskProjectId,
      user,
      setGanttDateOverrides,
      updateProjectTask,
      updateOnboardingItem,
      updateMilestone,
      pruneMatchingGanttOverrides,
      pushTimelineUndo,
    ],
  );

  const handleUpdateTask = useCallback(async (id: string, updates: Partial<ProjectTask>) => {
    id = unwrapSupplierDisplayTaskId(id) ?? id;
    setSelectedTask((prev) => {
      if (!prev) return prev;
      const prevId = unwrapSupplierDisplayTaskId(prev.id) ?? prev.id;
      if (prevId !== id) return prev;
      return { ...prev, ...updates };
    });
    if (Object.prototype.hasOwnProperty.call(updates, 'supplierOrgIds')) {
      const orgIds = (updates.supplierOrgIds ?? []).filter((x): x is string => !!x);
      const supplierPatch = { supplierOrgIds: orgIds, supplierManual: true as const };
      setTaskSupplierOverrides((prev) => ({ ...prev, [id]: supplierPatch }));
      try {
        if (isOnboardingBridgeTaskId(id)) {
          const onboardingProjectId = resolveTaskProjectId(id);
          if (!onboardingProjectId) {
            throw new Error('Could not find the project for this task.');
          }
          await updateOnboardingItem(
            onboardingProjectId,
            onboardingTaskIdToItemId(id),
            supplierPatch,
            user?.name || user?.email || 'Unknown',
          );
        } else {
          await updateProjectTask(id, supplierPatch);
        }
      } catch (err) {
        setTaskSupplierOverrides((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        pushPmToast(
          'error',
          'Could not save supplier',
          err instanceof Error ? err.message : 'Please try again.',
        );
        return;
      }
      const supplierProjectId = isOnboardingBridgeTaskId(id)
        ? resolveTaskProjectId(id)
        : (projectTasks.find((t) => t.id === id)?.projectId ?? selectedProjectId);
      const supplierDealId = supplierProjectId
        ? timberProjects.find((p) => p.id === supplierProjectId)?.dealId?.trim()
        : '';
      if (supplierDealId) {
        if (isOnboardingBridgeTaskId(id) && supplierProjectId) {
          const itemId = onboardingTaskIdToItemId(id);
          void persistProductionCommentAccessForDeal(supplierDealId, {
            accessProjectOnboardings: projectOnboardings.map((ob) => {
              if (ob.projectId !== supplierProjectId) return ob;
              return {
                ...ob,
                items: (ob.items ?? []).map((it) =>
                  it.id === itemId ? { ...it, ...supplierPatch } : it,
                ),
              };
            }),
          });
        } else {
          void persistProductionCommentAccessForDeal(supplierDealId, {
            accessProjectTasks: projectTasks.map((t) =>
              t.id === id ? { ...t, ...supplierPatch } : t,
            ),
          });
        }
      }
      const onlySupplier = Object.keys(updates).every(
        (k) => k === 'supplierOrgIds' || k === 'supplierManual',
      );
      if (onlySupplier) return;
    }
    const completionPrompt = resolveTaskCompletionPrompt(id, updates, {
      projectTasks,
      taskGroups: filteredGroups,
      projectOnboardings,
      getOnboardingForProject,
      fallbackProjectId: selectedProjectId || undefined,
    });
    if (completionPrompt) {
      setPendingCompletionTask(completionPrompt);
      return;
    }

    if (
      updates.status !== undefined
      || updates.progress !== undefined
      || updates.priority !== undefined
    ) {
      const fieldPatch: TaskFieldOverride = {};
      if (updates.status !== undefined) fieldPatch.status = updates.status;
      if (updates.progress !== undefined) fieldPatch.progress = updates.progress;
      if (updates.priority !== undefined) fieldPatch.priority = updates.priority;
      setTaskFieldOverrides((prev) => ({
        ...prev,
        [id]: { ...prev[id], ...fieldPatch },
      }));
    }

    const onboardingProjectId = isOnboardingBridgeTaskId(id) ? resolveTaskProjectId(id) : undefined;
    const onboardingProjectGroups = onboardingProjectId
      ? taskGroups.filter((g) => g.projectId === onboardingProjectId)
      : filteredGroups;
    if (isOnboardingBridgeTaskId(id) && onboardingProjectId) {
      const itemId = onboardingTaskIdToItemId(id);
      const actor = user?.name || user?.email || 'Unknown';
      const { groupId: movedToGroupId, ...effective } = updates;
      const obItem = getOnboardingForProject(onboardingProjectId)?.items?.find((it) => it.id === itemId);
      const beforeOnboarding =
        obItem
          ? onboardingItemToProjectTask(obItem, onboardingProjectId, onboardingProjectGroups)
          : undefined;
      const onboardingTask = beforeOnboarding;
      const previouslyAssigned = onboardingTask?.assignedTo ?? [];

      let eff = { ...effective };
      if (onboardingTask) {
        eff = applyActionRequiredSideEffects(onboardingTask, eff);
      }

      const patches: Partial<OnboardingItem> = {};
      if (movedToGroupId !== undefined) {
        patches.taskGroupId = movedToGroupId || undefined;
      }
      if (eff.status !== undefined) {
        patches.completed = eff.status === 'completed';
        patches.taskStatus = eff.status;
      } else if (eff.progress !== undefined) {
        patches.completed = (eff.progress ?? 0) >= 100;
        if ((eff.progress ?? 0) >= 100) patches.taskStatus = 'completed';
      }
      if (eff.title !== undefined) patches.title = eff.title;
      if (eff.description !== undefined) patches.description = eff.description;
      if (eff.assignedTo !== undefined) {
        patches.assignedTo = eff.assignedTo;
        const uid = eff.assignedTo[0];
        if (uid) {
          patches.assigneeId = uid;
          patches.assigneeName = crmUsers.find((c) => c.uid === uid)?.displayName;
        } else {
          patches.assigneeId = undefined;
          patches.assigneeName = undefined;
        }
      }
      if (eff.progress !== undefined) patches.progress = eff.progress;
      if (eff.priority !== undefined) patches.priority = eff.priority;
      if (eff.startDate !== undefined) patches.startDate = eff.startDate;
      if (eff.endDate !== undefined) patches.endDate = eff.endDate;
      if (eff.dependencyEdges !== undefined) patches.dependencyEdges = eff.dependencyEdges;
      if (eff.subtasks !== undefined) patches.subtasks = eff.subtasks;
      if (eff.supplierOrgIds !== undefined) patches.supplierOrgIds = eff.supplierOrgIds;
      if (eff.supplierManual !== undefined) patches.supplierManual = eff.supplierManual;
      if ('actionRequired' in eff) patches.actionRequired = eff.actionRequired;
      if ('actionRequiredNote' in eff) patches.actionRequiredNote = eff.actionRequiredNote;
      if (eff.onCriticalPath !== undefined) patches.onCriticalPath = eff.onCriticalPath;
      if (eff.department !== undefined) {
        patches.department = eff.department;
        if (movedToGroupId === undefined && onboardingProjectId) {
          const gid = resolveTaskGroupIdForDepartment(
            normalizeOnboardingDepartment(eff.department),
            onboardingProjectId,
            onboardingProjectGroups,
          );
          if (gid) patches.taskGroupId = gid;
        }
      }
      if (
        movedToGroupId !== undefined &&
        movedToGroupId &&
        eff.department === undefined &&
        onboardingProjectId
      ) {
        const g = onboardingProjectGroups.find((x) => x.id === movedToGroupId);
        const dept = g ? resolveDepartmentFromTaskGroupName(g.name) : undefined;
        if (dept) patches.department = dept;
      }
      if (Object.keys(patches).length === 0) return;
      const onboardingPatches = mergePmDateReviewIntoOnboardingPatch(
        patches,
        assignedProjectManagerUid(onboardingProjectId),
        user?.id,
      );
      await updateOnboardingItem(onboardingProjectId, itemId, onboardingPatches, actor);

      if (beforeOnboarding && user?.id) {
        const logUpdates: Partial<ProjectTask> = {};
        if (eff.status !== undefined) logUpdates.status = eff.status;
        if (eff.title !== undefined) logUpdates.title = eff.title;
        if (eff.description !== undefined) logUpdates.description = eff.description;
        if (eff.assignedTo !== undefined) logUpdates.assignedTo = eff.assignedTo;
        if (eff.progress !== undefined) logUpdates.progress = eff.progress;
        if (eff.priority !== undefined) logUpdates.priority = eff.priority;
        if (eff.startDate !== undefined) logUpdates.startDate = eff.startDate;
        if (eff.endDate !== undefined) logUpdates.endDate = eff.endDate;
        if (eff.dependencyEdges !== undefined) logUpdates.dependencyEdges = eff.dependencyEdges;
        if (eff.subtasks !== undefined) logUpdates.subtasks = eff.subtasks;
        if ('actionRequired' in eff) logUpdates.actionRequired = eff.actionRequired;
        if ('actionRequiredNote' in eff) logUpdates.actionRequiredNote = eff.actionRequiredNote;
        if (eff.onCriticalPath !== undefined) logUpdates.onCriticalPath = eff.onCriticalPath;
        if (eff.department !== undefined) logUpdates.department = eff.department;
        if (movedToGroupId !== undefined) logUpdates.groupId = movedToGroupId;
        if (Object.keys(logUpdates).length > 0) {
          recordTaskActivityChanges({
            before: beforeOnboarding,
            updates: logUpdates,
            taskId: id,
            actorId: user.id,
            actorName: user.name || user.email || 'Unknown',
            teamMembers: crmUsers,
            createTaskActivity,
          });
        }
      }

      if (eff.assignedTo && onboardingTask) {
        const newlyAssigned = eff.assignedTo.filter((uid) => !previouslyAssigned.includes(uid));
        const project = timberProjects.find((p) => p.id === onboardingTask.projectId);
        const projectName = project?.name ?? 'Project';
        for (const assigneeId of newlyAssigned) {
          if (assigneeId === user?.id) continue;
          createTaskAssignmentNotification({
            assigneeUserId: assigneeId,
            taskId: id,
            taskTitle: onboardingTask.title,
            projectId: onboardingTask.projectId,
            projectName,
            assignedByName: user?.name ?? 'Someone',
          });
        }
      }
      return;
    }

    const task = projectTasks.find(t => t.id === id);
    const previouslyAssigned = task?.assignedTo ?? [];

    let taskPayload: Partial<ProjectTask> = task
      ? applyActionRequiredSideEffects(task, { ...updates })
      : { ...updates };
    if (updates.department !== undefined && task?.projectId) {
      const gid = resolveTaskGroupIdForDepartment(
        updates.department,
        task.projectId,
        taskGroups.filter((g) => g.projectId === task.projectId),
      );
      if (gid) taskPayload.groupId = gid;
    }
    if (
      updates.groupId !== undefined &&
      task?.projectId &&
      updates.department === undefined
    ) {
      const g = taskGroups.find((x) => x.id === updates.groupId);
      const dept = g ? resolveDepartmentFromTaskGroupName(g.name) : undefined;
      if (dept) taskPayload.department = dept;
    }

    if (
      task?.id &&
      (childTasksByParent.get(task.id)?.length ?? 0) > 0 &&
      (taskPayload.startDate !== undefined || taskPayload.endDate !== undefined)
    ) {
      delete taskPayload.startDate;
      delete taskPayload.endDate;
    }

    /** Enforce schedule + re-infer FS/SS lags (same as Gantt lag popover) so sidebar matches timeline. */
    if (updates.dependencyEdges !== undefined && (selectedProjectId || task?.projectId)) {
      const schedulingTasks = projectSchedulingNodes;
      const succ = schedulingTasks.find((t) => t.id === id);
      if (succ) {
        const depPayload = dependencyPayload(
          getDependencyEdges({ ...succ, ...updates } as ProjectTask),
        );
        const mergedSucc: ProjectTask = { ...succ, ...depPayload };
        const mergedTasks = schedulingTasks.map((t) => (t.id === id ? mergedSucc : t));
        const { dates, error } = enforceAllFsDependencies(mergedTasks);
        if (error) {
          window.alert(error);
          return;
        }
        const persisted = await persistScheduleAfterEnforce(mergedTasks, dates, id, 'sidebarDependencyEdges');
        if (task && user?.id && persisted) {
          const succChange = persisted.changes.find((c) => c.id === id);
          const edgesAfter = persisted.lagPatches.get(id) ?? getDependencyEdges(mergedSucc);
          recordTaskActivityChanges({
            before: task,
            updates: {
              ...taskPayload,
              dependencyEdges: edgesAfter,
              ...(succChange && { startDate: succChange.startDate, endDate: succChange.endDate }),
            },
            taskId: id,
            actorId: user.id,
            actorName: user.name || user.email || 'Unknown',
            teamMembers: crmUsers,
            createTaskActivity,
          });
        }

        if (taskPayload.assignedTo && task) {
          const newlyAssigned = taskPayload.assignedTo.filter((uid) => !previouslyAssigned.includes(uid));
          const project = timberProjects.find((p) => p.id === task.projectId);
          const projectName = project?.name ?? 'Project';

          for (const assigneeId of newlyAssigned) {
            if (assigneeId === user?.id) continue;
            createTaskAssignmentNotification({
              assigneeUserId: assigneeId,
              taskId: id,
              taskTitle: task.title,
              projectId: task.projectId,
              projectName,
              assignedByName: user?.name ?? 'Someone',
            });
          }
        }
        return;
      }
    }

    const stampedTaskPayload = mergePmDateReviewIntoTaskPatch(
      taskPayload,
      assignedProjectManagerUid(task?.projectId || selectedProjectId || undefined),
      user?.id,
    );
    await updateProjectTask(id, stampedTaskPayload);

    if (stampedTaskPayload.status === 'completed') {
      await cancelPendingTaskRemindersForTask(id);
    }

    if (
      task &&
      (stampedTaskPayload.startDate !== undefined || stampedTaskPayload.endDate !== undefined)
    ) {
      await syncParentRollupForTask(id);
    }

    if (task && user?.id) {
      recordTaskActivityChanges({
        before: task,
        updates: stampedTaskPayload,
        taskId: id,
        actorId: user.id,
        actorName: user.name || user.email || 'Unknown',
        teamMembers: crmUsers,
        createTaskActivity,
      });
    }

    if (taskPayload.assignedTo && task) {
      const newlyAssigned = taskPayload.assignedTo.filter((uid) => !previouslyAssigned.includes(uid));
      const project = timberProjects.find((p) => p.id === task.projectId);
      const projectName = project?.name ?? 'Project';

      for (const assigneeId of newlyAssigned) {
        if (assigneeId === user?.id) continue;
        createTaskAssignmentNotification({
          assigneeUserId: assigneeId,
          taskId: id,
          taskTitle: task.title,
          projectId: task.projectId,
          projectName,
          assignedByName: user?.name ?? 'Someone',
        });
      }
    }
  }, [
    updateProjectTask,
    projectTasks,
    projectOnboardings,
    timberProjects,
    user?.id,
    user?.name,
    selectedProjectId,
    resolveTaskProjectId,
    updateOnboardingItem,
    crmUsers,
    user?.email,
    filteredGroups,
    taskGroups,
    getOnboardingForProject,
    createTaskActivity,
    projectSchedulingNodes,
    setGanttDateOverrides,
    updateMilestone,
    persistScheduleAfterEnforce,
    childTasksByParent,
    syncParentRollupForTask,
    pushPmToast,
    persistProductionCommentAccessForDeal,
    assignedProjectManagerUid,
  ]);

  const handleRequestGroupTaskComplete = useCallback(
    (taskId: string) => {
      void handleUpdateTask(taskId, { status: 'completed', progress: 100 });
    },
    [handleUpdateTask],
  );

  /** Drop overrides once Firestore-backed rows match — debounced + post-write hold so we don’t clear on a stale snapshot. */
  useEffect(() => {
    if (ganttOverrideSyncTimerRef.current != null) {
      window.clearTimeout(ganttOverrideSyncTimerRef.current);
    }
    ganttOverrideSyncTimerRef.current = window.setTimeout(() => {
      ganttOverrideSyncTimerRef.current = null;
      pruneMatchingGanttOverrides();
    }, 450);
    return () => {
      if (ganttOverrideSyncTimerRef.current != null) {
        window.clearTimeout(ganttOverrideSyncTimerRef.current);
        ganttOverrideSyncTimerRef.current = null;
      }
    };
  }, [tasksForViewsBase, milestones, pruneMatchingGanttOverrides]);

  const persistScheduleEngineResult = useCallback(
    async (taskIdForLog: string, dates: Map<string, TaskDateState>, debugLabel: string) => {
      const allChanges = diffDateChanges(schedulingBaselineNodes, dates);
      const lockedIds = new Set(
        projectSchedulingNodes.filter((n) => n.id && isTaskLocked(n)).map((n) => n.id!),
      );
      // Defense in depth: drop any date change for a locked task. The scheduler should
      // already skip them, but a buggy upstream caller (or an in-flight unlock race)
      // must not silently rewrite a pinned task's dates.
      const droppedLockedIds: string[] = [];
      const changes = allChanges.filter((c) => {
        if (lockedIds.has(c.id)) {
          droppedLockedIds.push(c.id);
          return false;
        }
        return true;
      });
      if (droppedLockedIds.length && isGanttScheduleDebugEnabled()) {
        ganttScheduleDebugWarn(
          'persistScheduleEngineResult: skipped writes for locked task ids',
          droppedLockedIds.map((id) => formatGanttTaskIdForLog(id)),
        );
      }
      const lagPatches = reinferFsLagsForDates(schedulingBaselineNodes, dates);
      if (isGanttScheduleDebugEnabled()) {
        logPersistScheduleSnapshot(debugLabel, taskIdForLog, schedulingBaselineNodes, dates, changes, lagPatches);
      }
      setGanttDateOverrides((prev) => applySchedulePersistToGanttOverrides(prev, changes, taskIdForLog));

      const changeById = new Map(changes.map((c) => [c.id, c]));
      const allIds = new Set<string>([...changes.map((c) => c.id), ...Array.from(lagPatches.keys())]);
      // Don't write lag patches against a locked task either (its edges + dates stay frozen).
      for (const id of Array.from(allIds)) {
        if (lockedIds.has(id)) allIds.delete(id);
      }

      const undoProjectId = selectedProjectId || resolveTaskProjectId(taskIdForLog);
      const undoSnap =
        undoProjectId && allIds.size > 0
          ? captureTimelineUndoSnapshot(schedulingBaselineNodesRef.current, undoProjectId, allIds)
          : null;

      for (const id of Array.from(allIds)) {
        const c = changeById.get(id);
        const newEdges = lagPatches.get(id);
        if (!c && !newEdges) continue;

        if (isOnboardingBridgeTaskId(id)) {
          const onboardingProjectId = resolveTaskProjectId(id);
          if (!onboardingProjectId || !user) continue;
          const itemId = onboardingTaskIdToItemId(id);
          const actor = user.name || user.email || 'Unknown';
          const patch: Partial<OnboardingItem> = {};
          if (c) {
            patch.startDate = c.startDate;
            patch.endDate = c.endDate;
          }
          if (newEdges) patch.dependencyEdges = newEdges;
          const stampedOnboarding = mergePmDateReviewIntoOnboardingPatch(
            patch,
            assignedProjectManagerUid(onboardingProjectId),
            user?.id,
          );
          await updateOnboardingItem(onboardingProjectId, itemId, stampedOnboarding, actor);
        } else if (isMilestoneBridgeId(id)) {
          const patch: Partial<Milestone> = {};
          if (c) patch.targetDate = c.endDate;
          if (newEdges) patch.dependencyEdges = newEdges;
          await updateMilestone(parseMilestoneBridgeId(id), patch);
        } else {
          const patch: Partial<ProjectTask> = {};
          if (c) {
            patch.startDate = c.startDate;
            patch.endDate = c.endDate;
          }
          if (newEdges) Object.assign(patch, dependencyPayload(newEdges));
          const stamped = mergePmDateReviewIntoTaskPatch(
            patch,
            assignedProjectManagerUid(resolveTaskProjectId(id) || selectedProjectId || undefined),
            user?.id,
          );
          await updateProjectTask(id, stamped);
        }
      }
      for (const c of changes) {
        await syncParentRollupForTask(c.id, { startDate: c.startDate, endDate: c.endDate });
      }
      // Payment forecast audit — a scheduler pass can move anchor milestones too.
      const movedMilestoneId = Array.from(allIds).find(
        (id) => isMilestoneBridgeId(id) && changeById.has(id),
      );
      if (movedMilestoneId && selectedProjectId) {
        void recordPaymentForecastSnapshot(selectedProjectId, 'milestone_moved', {
          byUid: user?.id,
          milestoneId: parseMilestoneBridgeId(movedMilestoneId),
        });
      }

      const holdUntil = Date.now() + 1200;
      for (const id of Array.from(allIds)) {
        ganttOverrideHoldUntilRef.current[id] = holdUntil;
      }
      window.setTimeout(() => {
        pruneMatchingGanttOverrides();
      }, 1350);
      pushTimelineUndo(undoSnap);
    },
    [
      projectSchedulingNodes,
      schedulingBaselineNodes,
      updateProjectTask,
      updateOnboardingItem,
      updateMilestone,
      selectedProjectId,
      resolveTaskProjectId,
      user,
      getOnboardingForProject,
      filteredGroups,
      pruneMatchingGanttOverrides,
      pushTimelineUndo,
      syncParentRollupForTask,
      assignedProjectManagerUid,
    ],
  );

  const reportLockViolations = useCallback(
    (violations: ScheduleLockViolation[] | undefined) => {
      if (!violations || violations.length === 0) return;
      const titleById = new Map<string, string>();
      for (const n of projectSchedulingNodes) {
        if (n.id) titleById.set(n.id, n.title || 'Untitled');
      }
      const fmt = (d: Date) =>
        new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const items = violations.slice(0, 4).map((v) => {
        const pin =
          v.pinnedReason === 'completed'
            ? '(completed)'
            : v.pinnedReason === 'explicit'
              ? '(schedule locked)'
              : '';
        return (
          <li key={v.id}>
            <strong>{titleById.get(v.id) || 'Locked task'}</strong>{' '}
            {pin ? <>{pin}{' — '}</> : null}
            {v.requiredStart !== undefined && v.currentStart !== undefined && (
              <>
                would need to start by {fmt(v.requiredStart)} (stays at {fmt(v.currentStart)})
              </>
            )}
            {v.requiredStart !== undefined && v.currentStart !== undefined && v.requiredEnd !== undefined
              ? '; '
              : null}
            {v.requiredEnd !== undefined && v.currentEnd !== undefined && (
              <>
                finish by {fmt(v.requiredEnd)} (stays ending {fmt(v.currentEnd)})
              </>
            )}
            .
          </li>
        );
      });
      const moreCount = violations.length - items.length;
      pushPmToast(
        'warning',
        `Lock conflict — ${violations.length} task${violations.length === 1 ? '' : 's'} pinned`,
        <ul>
          {items}
          {moreCount > 0 ? <li>+ {moreCount} more</li> : null}
        </ul>,
      );
    },
    [projectSchedulingNodes, pushPmToast],
  );

  const timelineUndoInFlightRef = useRef(false);

  const handleTimelineUndo = useCallback(async () => {
    if (!user) return;
    if (timelineUndoInFlightRef.current) return;

    let snapshotPop: TimelineUndoSnapshot | undefined;
    setTimelineUndoStack((prev) => {
      if (!prev.length) return prev;
      const snap = prev[prev.length - 1];
      if (!snap?.patches?.length) return prev;
      if (selectedProjectId && snap.projectId !== selectedProjectId) return prev;
      snapshotPop = snap;
      return prev.slice(0, -1);
    });
    if (!snapshotPop) return;

    const snapshot = snapshotPop;
    timelineUndoInFlightRef.current = true;
    setTimelineUndoBusy(true);

    const actorName = user.name || user.email || 'Unknown';
    const nextOverrides: Record<string, { startDate: Date; endDate: Date }> = {};
    for (const p of snapshot.patches) {
      if (p.kind === 'task') {
        nextOverrides[p.taskId] = {
          startDate: new Date(p.startMs),
          endDate: new Date(p.endMs),
        };
      } else if (p.kind === 'milestone') {
        const brid = milestoneBridgeId(p.milestoneDocId);
        const d = new Date(p.targetMs);
        nextOverrides[brid] = { startDate: d, endDate: d };
      } else {
        nextOverrides[p.bridgeTaskId] = {
          startDate: new Date(p.startMs),
          endDate: new Date(p.endMs),
        };
      }
    }

    try {
      await restoreTimelineUndoSnapshot(snapshot, {
        projectId: snapshot.projectId,
        actorName,
        updateProjectTask,
        updateMilestone,
        updateOnboardingItem,
      });

      setGanttDateOverrides((prev) => ({ ...prev, ...nextOverrides }));
      const holdUntil = Date.now() + 1200;
      for (const id of Object.keys(nextOverrides)) {
        ganttOverrideHoldUntilRef.current[id] = holdUntil;
      }
      window.setTimeout(() => pruneMatchingGanttOverrides(), 1350);

      pushPmToast('success', 'Undo', 'Restored previous schedule.');
    } catch (e) {
      console.error(e);
      pushTimelineUndo(snapshot);
      pushPmToast('warning', 'Undo failed', "Couldn't revert the timeline change.");
    } finally {
      timelineUndoInFlightRef.current = false;
      setTimelineUndoBusy(false);
    }
  }, [
    selectedProjectId,
    user,
    updateProjectTask,
    updateMilestone,
    updateOnboardingItem,
    pruneMatchingGanttOverrides,
    pushPmToast,
    pushTimelineUndo,
  ]);

  const applyTaskDateCommit = useCallback(
    async (taskId: string, start: Date, end: Date, mode: 'direct' | 'ripple' | 'shiftConnected') => {
      const targetNode = projectSchedulingNodes.find((n) => n.id === taskId);
      if (targetNode && isTaskLocked(targetNode)) {
        // Abort: locked tasks (including completed) are immovable until explicitly unlocked.
        setGanttDateOverrides((prev) => {
          if (!(taskId in prev)) return prev;
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        pushPmToast(
          'warning',
          'Task is locked',
          <span>
            &ldquo;{targetNode.title}&rdquo; is locked. Unlock it on the Gantt to change its dates.
          </span>,
        );
        return false;
      }

      const summaryChildCount = childTasksByParent.get(taskId)?.length ?? 0;
      if (summaryChildCount > 0) {
        setGanttDateOverrides((prev) => {
          if (!(taskId in prev)) return prev;
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        pushPmToast(
          'info',
          'Summary task',
          <span>
            &ldquo;{targetNode?.title ?? 'This task'}&rdquo; spans its subtasks. Edit subtask dates to change the summary range.
          </span>,
        );
        return false;
      }

      if (mode === 'direct') {
        if (isGanttScheduleDebugEnabled()) {
          ganttScheduleDebugLog('commit path: direct (no ripple, no full-graph enforce)');
        }
        if (isMilestoneBridgeId(taskId)) {
          if (!selectedProjectId) return false;
          const snap = captureTimelineUndoSnapshot(
            schedulingBaselineNodesRef.current,
            selectedProjectId,
            [taskId],
          );
          const bridge = taskId;
          setGanttDateOverrides((prev) => ({
            ...prev,
            [bridge]: { startDate: end, endDate: end },
          }));
          ganttOverrideHoldUntilRef.current[bridge] = Date.now() + 1200;
          const milestoneDocId = parseMilestoneBridgeId(taskId);
          await updateMilestone(milestoneDocId, { targetDate: end });
          // Payment forecast audit — anchor milestone moves shift projected due dates.
          void recordPaymentForecastSnapshot(selectedProjectId, 'milestone_moved', {
            byUid: user?.id,
            milestoneId: milestoneDocId,
          });
          pushTimelineUndo(snap);
          window.setTimeout(() => pruneMatchingGanttOverrides(), 1350);
          return true;
        }
        if (isOnboardingBridgeTaskId(taskId)) {
          const onboardingProjectId = resolveTaskProjectId(taskId);
          if (!onboardingProjectId || !user) return false;
          const snap = captureTimelineUndoSnapshot(
            schedulingBaselineNodesRef.current,
            onboardingProjectId,
            [taskId],
          );
          setGanttDateOverrides((prev) => ({
            ...prev,
            [taskId]: { startDate: start, endDate: end },
          }));
          ganttOverrideHoldUntilRef.current[taskId] = Date.now() + 1200;
          const itemId = onboardingTaskIdToItemId(taskId);
          const actor = user.name || user.email || 'Unknown';
          const groupsForOb = taskGroups.filter((g) => g.projectId === onboardingProjectId);
          const obItem = getOnboardingForProject(onboardingProjectId)?.items?.find((it) => it.id === itemId);
          const beforeOb =
            obItem
              ? onboardingItemToProjectTask(obItem, onboardingProjectId, groupsForOb)
              : undefined;
          await updateOnboardingItem(
            onboardingProjectId,
            itemId,
            mergePmDateReviewIntoOnboardingPatch(
              { startDate: start, endDate: end },
              assignedProjectManagerUid(onboardingProjectId),
              user?.id,
            ),
            actor,
          );
          if (beforeOb) {
            recordTaskActivityChanges({
              before: beforeOb,
              updates: { startDate: start, endDate: end },
              taskId,
              actorId: user.id,
              actorName: actor,
              teamMembers: crmUsers,
              extras: { projectId: onboardingProjectId },
              createTaskActivity,
            });
          }
          pushTimelineUndo(snap);
          window.setTimeout(() => pruneMatchingGanttOverrides(), 1350);
          return true;
        }
        const tBefore = projectTasks.find((x) => x.id === taskId);
        const snap = selectedProjectId
          ? captureTimelineUndoSnapshot(schedulingBaselineNodesRef.current, selectedProjectId, [taskId])
          : null;
        setGanttDateOverrides((prev) => ({
          ...prev,
          [taskId]: { startDate: start, endDate: end },
        }));
        ganttOverrideHoldUntilRef.current[taskId] = Date.now() + 1200;
        await updateProjectTask(
          taskId,
          mergePmDateReviewIntoTaskPatch(
            { startDate: start, endDate: end },
            assignedProjectManagerUid(
              projectTasks.find((x) => x.id === taskId)?.projectId || selectedProjectId || undefined,
            ),
            user?.id,
          ),
        );
        if (tBefore && user) {
          recordTaskActivityChanges({
            before: tBefore,
            updates: { startDate: start, endDate: end },
            taskId,
            actorId: user.id,
            actorName: user.name || user.email || 'Unknown',
            teamMembers: crmUsers,
            extras: { projectId: tBefore.projectId || selectedProjectId || undefined },
            createTaskActivity,
          });
        }
        await syncParentRollupForTask(taskId, { startDate: start, endDate: end });
        pushTimelineUndo(snap);
        window.setTimeout(() => pruneMatchingGanttOverrides(), 1350);
        return true;
      }

      const result =
        mode === 'ripple'
          ? scheduleAfterMove(schedulingBaselineNodes, taskId, start, end)
          : scheduleShiftConnectedComponent(schedulingBaselineNodes, taskId, start, end);
      const { dates, error, violations } = result;
      if (error) {
        pushPmToast('error', 'Could not move task', error);
        setGanttDateOverrides((prev) => {
          if (!(taskId in prev)) return prev;
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        return false;
      }
      if (mode === 'ripple') {
        if (isGanttScheduleDebugEnabled()) {
          ganttScheduleDebugLog('commit path: ripple (scheduleAfterMove + lag reinfer)');
        }
        const finalMoved = dates.get(taskId);
        if (
          finalMoved &&
          isGanttScheduleDebugEnabled() &&
          (!sameLocalCalendarDay(finalMoved.startDate, start) || !sameLocalCalendarDay(finalMoved.endDate, end))
        ) {
          ganttScheduleDebugWarn(
            'timeline ripple: bar snapped to feasible schedule (FS/SS predecessors) — drag further left than allowed',
            {
              task: formatGanttTaskIdForLog(taskId),
              requested: { start: fmtDay(start), end: fmtDay(end) },
              applied: { start: fmtDay(finalMoved.startDate), end: fmtDay(finalMoved.endDate) },
            },
          );
        }
        await persistScheduleEngineResult(taskId, dates, 'timelineRippleCommit');
      } else {
        if (isGanttScheduleDebugEnabled()) {
          ganttScheduleDebugLog('commit path: shift connected component (uniform calendar delta + lag reinfer)');
        }
        await persistScheduleEngineResult(taskId, dates, 'timelineShiftConnectedCommit');
      }
      reportLockViolations(violations);
      return true;
    },
    [
      projectSchedulingNodes,
      schedulingBaselineNodes,
      projectTasks,
      persistScheduleEngineResult,
      updateProjectTask,
      updateMilestone,
      updateOnboardingItem,
      getOnboardingForProject,
      resolveTaskProjectId,
      taskGroups,
      selectedProjectId,
      user,
      crmUsers,
      createTaskActivity,
      pushPmToast,
      reportLockViolations,
      captureTimelineUndoSnapshot,
      pushTimelineUndo,
      schedulingBaselineNodesRef,
      childTasksByParent,
      syncParentRollupForTask,
      pruneMatchingGanttOverrides,
      assignedProjectManagerUid,
    ],
  );

  const offerScheduleMoveReason = useCallback(
    async (before: ProjectTask, start: Date, end: Date) => {
      if (!user?.id || !before.id) return;
      const pool = [...tasksForViews, ...groupTimelineTasks, ...projectTasks];
      const byId = new Map(pool.filter((t) => t.id).map((t) => [t.id!, t] as const));
      if (!isTimelineDriftFocusTask(before, byId)) return;
      if (
        sameLocalCalendarDay(start, new Date(before.startDate))
        && sameLocalCalendarDay(end, new Date(before.endDate))
      ) {
        return;
      }
      try {
        const activityId = await createTaskActivity(
          buildScheduleMoveActivityEntry({
            taskId: before.id,
            projectId: before.projectId || selectedProjectId || undefined,
            actorId: user.id,
            actorName: user.name || user.email || 'Unknown',
            before,
            start,
            end,
          }),
        );
        setMoveReasonPrompt({ activityId, taskTitle: before.title || 'Task' });
      } catch (err) {
        console.error(err);
      }
    },
    [user, tasksForViews, groupTimelineTasks, projectTasks, selectedProjectId, createTaskActivity],
  );

  const handleCommitTaskDates = useCallback(
    async (taskId: string, start: Date, end: Date, hint: CommitTaskDatesHint) => {
      taskId = unwrapSupplierDisplayTaskId(taskId) ?? taskId;
      const opt = normalizeCommitTaskDatesHint(hint);
      if (isGanttScheduleDebugEnabled()) {
        ganttScheduleDebugGroup(`commitTaskDates · ${taskId.slice(0, 12)}…`, () => {
          ganttScheduleDebugLog('input', {
            hint: opt,
            start: fmtDay(start),
            end: fmtDay(end),
            onboardingBridge: isOnboardingBridgeTaskId(taskId),
            milestoneBridge: isMilestoneBridgeId(taskId),
          });
        });
      }

      const beforeTask = schedulingBaselineNodes.find((t) => t.id === taskId)
        ?? tasksForViewsBase.find((t) => t.id === taskId)
        ?? projectTasks.find((t) => t.id === taskId)
        ?? null;

      if (opt.commitMode) {
        const persisted = await applyTaskDateCommit(taskId, start, end, opt.commitMode);
        if (persisted && beforeTask) await offerScheduleMoveReason(beforeTask, start, end);
        return;
      }

      const task = beforeTask;
      const datesChanged =
        !!task &&
        (!sameLocalCalendarDay(start, new Date(task.startDate)) ||
          !sameLocalCalendarDay(end, new Date(task.endDate)));
      const taskProjectId = selectedProjectId || task?.projectId;
      const hasDeps =
        !!taskProjectId &&
        taskParticipatesInDependencyGraph(projectSchedulingNodes, taskProjectId, taskId);
      const preferRipple = opt.preferRipple ?? false;
      const fromPanel = opt.fromTaskPanel ?? false;

      // Gantt-only “don’t ask again”. Panel/table date fields always prompt when linked
      // (see decideTaskDateCommit) so a remembered cascade cannot silently clamp edits (CRM #40).
      let remembered: DependencyCommitMode | null = null;
      if (taskProjectId && datesChanged && hasDeps && !fromPanel) {
        try {
          const raw = window.localStorage.getItem(`pm.gantt.depPrompt.${taskProjectId}`);
          if (raw === 'direct' || raw === 'ripple' || raw === 'shiftConnected') {
            remembered = raw;
          }
        } catch {
          /* ignore localStorage errors */
        }
      }

      const decision = decideTaskDateCommit({
        datesChanged,
        hasDeps,
        hasProjectId: !!taskProjectId,
        preferRipple,
        fromTaskPanel: fromPanel,
        remembered,
      });

      if (decision.action === 'prompt') {
        setDependencyDatePrompt({ taskId, start, end });
        setGanttDateOverrides((prev) => ({
          ...prev,
          [taskId]: { startDate: start, endDate: end },
        }));
        return;
      }

      const persisted = await applyTaskDateCommit(taskId, start, end, decision.mode);
      if (persisted && beforeTask) await offerScheduleMoveReason(beforeTask, start, end);
    },
    [applyTaskDateCommit, schedulingBaselineNodes, tasksForViewsBase, projectTasks, projectSchedulingNodes, selectedProjectId, offerScheduleMoveReason],
  );

  const resolveDependencyDatePrompt = useCallback(
    (mode: DependencyCommitMode, remember = false) => {
      setDependencyDatePrompt((cur) => {
        if (cur) {
          const { taskId, start, end } = cur;
          const rememberProjectId = selectedProjectId || resolveTaskProjectId(taskId);
          if (remember && rememberProjectId) {
            try {
              window.localStorage.setItem(`pm.gantt.depPrompt.${rememberProjectId}`, mode);
            } catch {
              /* ignore localStorage errors */
            }
          }
          const beforeTask = schedulingBaselineNodes.find((t) => t.id === taskId)
            ?? tasksForViewsBase.find((t) => t.id === taskId)
            ?? projectTasks.find((t) => t.id === taskId)
            ?? null;
          queueMicrotask(() => {
            void applyTaskDateCommit(taskId, start, end, mode).then((persisted) => {
              if (persisted && beforeTask) void offerScheduleMoveReason(beforeTask, start, end);
            });
          });
        }
        return null;
      });
    },
    [applyTaskDateCommit, selectedProjectId, resolveTaskProjectId, schedulingBaselineNodes, tasksForViewsBase, projectTasks, offerScheduleMoveReason],
  );

  const cancelDependencyDatePrompt = useCallback(() => {
    setDependencyDatePrompt((cur) => {
      if (cur) {
        const tid = cur.taskId;
        setGanttDateOverrides((prev) => {
          const next = { ...prev };
          delete next[tid];
          return next;
        });
      }
      return null;
    });
  }, []);

  const timelineDriftSourceTasks = useMemo(
    () => (selectedProjectId ? tasksForViews : [...groupTimelineTasks, ...ganttSplitTasks]),
    [selectedProjectId, tasksForViews, groupTimelineTasks, ganttSplitTasks],
  );

  const timelineDriftCanonicalTasks = useMemo(
    () => canonicalizeTasksForTimelineDrift(timelineDriftSourceTasks),
    [timelineDriftSourceTasks],
  );

  const timelineDriftFocusIdKey = useMemo(() => {
    if (!showTimelineDrift || viewMode !== 'gantt') return '';
    const byId = new Map(
      timelineDriftCanonicalTasks.filter((t) => t.id).map((t) => [t.id!, t] as const),
    );
    return timelineDriftCanonicalTasks
      .filter((t) => t.id && isTimelineDriftFocusTask(t, byId))
      .map((t) => t.id!)
      .sort()
      .join(',');
  }, [showTimelineDrift, viewMode, timelineDriftCanonicalTasks]);

  useEffect(() => {
    if (!showTimelineDrift || viewMode !== 'gantt' || !timelineDriftFocusIdKey) {
      setTimelineDriftLogs([]);
      return;
    }
    const ids = timelineDriftFocusIdKey.split(',').filter(Boolean);
    const logMap = new Map<string, TaskActivityLog>();
    const unsubs: Array<() => void> = [];
    const flush = () => setTimelineDriftLogs(Array.from(logMap.values()));
    for (let i = 0; i < ids.length; i += 10) {
      const chunk = ids.slice(i, i + 10);
      const q = query(collection(db, 'taskActivity'), where('taskId', 'in', chunk));
      unsubs.push(
        onSnapshot(q, (snap) => {
          const present = new Set(snap.docs.map((d) => d.id));
          Array.from(logMap.entries()).forEach(([id, log]) => {
            if (chunk.includes(log.taskId) && !present.has(id)) logMap.delete(id);
          });
          for (const d of snap.docs) {
            const data = d.data();
            const createdAt =
              typeof data.createdAt?.toDate === 'function'
                ? data.createdAt.toDate()
                : data.createdAt instanceof Date
                  ? data.createdAt
                  : new Date();
            logMap.set(d.id, { id: d.id, ...data, createdAt } as TaskActivityLog);
          }
          flush();
        }),
      );
    }
    return () => unsubs.forEach((u) => u());
  }, [showTimelineDrift, viewMode, timelineDriftFocusIdKey]);

  const timelineDriftGhosts = useMemo(() => {
    if (!showTimelineDrift || viewMode !== 'gantt') return [];
    const ghosts = mapTimelineDriftGhostsToRows(
      buildTimelineDriftGhosts(timelineDriftCanonicalTasks, timelineDriftLogs),
      timelineDriftSourceTasks,
    );
    if (!timelineDriftCauseFilter || timelineDriftCauseFilter.size === 0) return ghosts;
    return ghosts.filter((g) => timelineDriftCauseFilter.has(g.cause));
  }, [
    showTimelineDrift,
    viewMode,
    timelineDriftCanonicalTasks,
    timelineDriftSourceTasks,
    timelineDriftLogs,
    timelineDriftCauseFilter,
  ]);

  const timelineDriftFilterCauses = useMemo((): TimelineDriftCause[] => {
    const seen: TimelineDriftCause[] = [];
    buildTimelineDriftGhosts(timelineDriftCanonicalTasks, timelineDriftLogs).forEach((g) => {
      if (seen.indexOf(g.cause) === -1) seen.push(g.cause);
    });
    return seen;
  }, [timelineDriftCanonicalTasks, timelineDriftLogs]);

  /**
   * Apply a completion: write actuals, set scheduleLocked, replace startDate/endDate with the
   * actual dates so the bar shows where the work really happened. Auto-locks the task so the
   * scheduler will never move it again.
   */
  const applyTaskCompletion = useCallback(
    async (
      ctx: { taskId: string; isOnboarding: boolean; projectId: string; title: string },
      actualStart: Date,
      actualEnd: Date,
    ) => {
      const actor = user?.name || user?.email || 'Unknown';
      try {
        if (ctx.isOnboarding) {
          const itemId = onboardingTaskIdToItemId(ctx.taskId);
          setTaskFieldOverrides((prev) => ({
            ...prev,
            [ctx.taskId]: { ...prev[ctx.taskId], status: 'completed', progress: 100 },
          }));
          await updateOnboardingItem(
            ctx.projectId,
            itemId,
            {
              completed: true,
              taskStatus: 'completed',
              progress: 100,
              startDate: actualStart,
              endDate: actualEnd,
              actualStartDate: actualStart,
              actualEndDate: actualEnd,
              completedAt: new Date(),
              scheduleLocked: true,
              lockedAt: new Date(),
              lockedBy: actor,
            },
            actor,
          );
        } else {
          setTaskFieldOverrides((prev) => ({
            ...prev,
            [ctx.taskId]: { ...prev[ctx.taskId], status: 'completed', progress: 100 },
          }));
          await updateProjectTask(ctx.taskId, {
            status: 'completed',
            progress: 100,
            startDate: actualStart,
            endDate: actualEnd,
            actualStartDate: actualStart,
            actualEndDate: actualEnd,
            completedAt: new Date(),
            scheduleLocked: true,
            lockedAt: new Date(),
            lockedBy: actor,
          });
        }
        pushPmToast(
          'success',
          'Task complete & locked',
          <span>
            &ldquo;{ctx.title}&rdquo; is locked at {actualStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            {' '}&ndash;{' '}
            {actualEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}.
          </span>,
        );
      } catch (err) {
        console.error('applyTaskCompletion failed', err);
        pushPmToast('error', 'Could not mark complete', err instanceof Error ? err.message : String(err));
      }
    },
    [user, updateProjectTask, updateOnboardingItem, pushPmToast],
  );

  /**
   * Toggle a single task's lock. Uses `updateProjectTask` for tasks, `updateOnboardingItem`
   * for onboarding bridges, and `updateMilestone` for milestone bridges.
   */
  const handleToggleTaskLock = useCallback(
    async (taskId: string, next: boolean) => {
      taskId = unwrapSupplierDisplayTaskId(taskId) ?? taskId;
      const actor = user?.name || user?.email || 'Unknown';
      try {
        if (isOnboardingBridgeTaskId(taskId)) {
          const onboardingProjectId = resolveTaskProjectId(taskId);
          if (!onboardingProjectId) return;
          const itemId = onboardingTaskIdToItemId(taskId);
          await updateOnboardingItem(
            onboardingProjectId,
            itemId,
            {
              scheduleLocked: next,
              lockedAt: next ? new Date() : undefined,
              lockedBy: next ? actor : undefined,
            },
            actor,
          );
        } else if (isMilestoneBridgeId(taskId)) {
          await updateMilestone(parseMilestoneBridgeId(taskId), {
            scheduleLocked: next,
            lockedAt: next ? new Date() : undefined,
            lockedBy: next ? actor : undefined,
          });
        } else {
          await updateProjectTask(taskId, {
            scheduleLocked: next,
            lockedAt: next ? new Date() : undefined,
            lockedBy: next ? actor : undefined,
          });
        }
        const node = projectSchedulingNodes.find((n) => n.id === taskId);
        const title = node?.title || 'Task';
        pushPmToast(
          next ? 'info' : 'success',
          next ? 'Task locked' : 'Task unlocked',
          <span>&ldquo;{title}&rdquo; {next ? 'is pinned to its current dates.' : 'can move with the scheduler again.'}</span>,
        );
      } catch (err) {
        console.error('handleToggleTaskLock failed', err);
        pushPmToast('error', 'Lock toggle failed', err instanceof Error ? err.message : String(err));
      }
    },
    [
      user,
      selectedProjectId,
      resolveTaskProjectId,
      updateProjectTask,
      updateOnboardingItem,
      updateMilestone,
      projectSchedulingNodes,
      pushPmToast,
    ],
  );

  /**
   * Lock or unlock every schedulable task in the project. Skips items that already match the
   * desired state. Completed tasks are not unlocked here — they remain locked by virtue of
   * status === 'completed' (their actuals are authoritative).
   */
  const handleLockAllTasks = useCallback(
    async (next: boolean) => {
      if (!selectedProjectId) return;
      const actor = user?.name || user?.email || 'Unknown';
      const targets = projectSchedulingNodes.filter((n) => {
        if (!n.id) return false;
        if (n.status === 'completed') return false; // Completed tasks: don't toggle their flag.
        return n.scheduleLocked !== next;
      });
      if (targets.length === 0) {
        pushPmToast('info', next ? 'Already locked' : 'Already unlocked', 'No tasks needed updating.');
        return;
      }
      const lockedAt = next ? new Date() : undefined;
      const lockedBy = next ? actor : undefined;
      let successes = 0;
      const failures: string[] = [];
      for (const t of targets) {
        const tid = t.id!;
        try {
          if (isOnboardingBridgeTaskId(tid)) {
            const itemId = onboardingTaskIdToItemId(tid);
            await updateOnboardingItem(
              selectedProjectId,
              itemId,
              { scheduleLocked: next, lockedAt, lockedBy },
              actor,
            );
          } else if (isMilestoneBridgeId(tid)) {
            await updateMilestone(parseMilestoneBridgeId(tid), {
              scheduleLocked: next,
              lockedAt,
              lockedBy,
            });
          } else {
            await updateProjectTask(tid, { scheduleLocked: next, lockedAt, lockedBy });
          }
          successes += 1;
        } catch (err) {
          console.error('handleLockAllTasks failed for', tid, err);
          failures.push(t.title || tid);
        }
      }
      pushPmToast(
        failures.length ? 'warning' : 'success',
        next ? `Locked ${successes} task${successes === 1 ? '' : 's'}` : `Unlocked ${successes} task${successes === 1 ? '' : 's'}`,
        failures.length ? `${failures.length} failed: ${failures.slice(0, 3).join(', ')}${failures.length > 3 ? '…' : ''}` : undefined,
      );
    },
    [
      selectedProjectId,
      projectSchedulingNodes,
      user,
      updateProjectTask,
      updateOnboardingItem,
      updateMilestone,
      pushPmToast,
    ],
  );

  const handleCreateFsDependency = useCallback(
    async (predecessorId: string, successorId: string, linkType: DependencyEdgeType = 'FS') => {
      predecessorId = unwrapSupplierDisplayTaskId(predecessorId) ?? predecessorId;
      successorId = unwrapSupplierDisplayTaskId(successorId) ?? successorId;
      ganttScheduleDebugLog('createDependency invoked', {
        predecessor: formatGanttTaskIdForLog(predecessorId),
        successor: formatGanttTaskIdForLog(successorId),
        linkType,
        selectedProjectId,
      });
      if (predecessorId === successorId) {
        ganttScheduleDebugWarn('createDependency skipped: predecessor === successor');
        return;
      }

      const schedulingTasks = projectSchedulingNodes;
      const succ = schedulingTasks.find((t) => t.id === successorId);
      const pred = schedulingTasks.find((t) => t.id === predecessorId);
      if (!succ || !pred || succ.projectId !== pred.projectId) {
        ganttScheduleDebugWarn('createDependency skipped: task not in scheduling graph or project mismatch', {
          hasSucc: !!succ,
          hasPred: !!pred,
          succProject: succ?.projectId,
          predProject: pred?.projectId,
        });
        return;
      }

      const existing = getDependencyEdges(succ);
      if (existing.some((e) => e.predecessorId === predecessorId)) {
        ganttScheduleDebugWarn('createDependency skipped: edge already exists');
        return;
      }
      if (wouldAddingEdgeCreateCycle(schedulingTasks, successorId, predecessorId, succ.projectId)) {
        ganttScheduleDebugWarn('createDependency blocked: would cycle');
        window.alert('That link would create a circular dependency.');
        return;
      }

      const edgeType: DependencyEdgeType =
        linkType === 'SS' || linkType === 'FF' || linkType === 'SF' || linkType === 'FS' ? linkType : 'FS';
      const impliedLag = impliedLagForNewDependencyEdge(pred, succ, edgeType);

      if (impliedLag < CREATE_DEP_IMPLIED_LAG_MOVE_THRESHOLD) {
        const newEdges: TaskDependencyEdge[] = [
          ...existing,
          { predecessorId, type: edgeType, lagBusinessDays: impliedLag },
        ];
        const depPayload = dependencyPayload(newEdges);
        const actor = user?.name || user?.email || 'Unknown';
        if (isOnboardingBridgeTaskId(successorId)) {
          const onboardingProjectId = resolveTaskProjectId(successorId);
          if (!onboardingProjectId || !user) return;
          await updateOnboardingItem(
            onboardingProjectId,
            onboardingTaskIdToItemId(successorId),
            { dependencyEdges: depPayload.dependencyEdges },
            actor,
          );
        } else if (isMilestoneBridgeId(successorId)) {
          await updateMilestone(parseMilestoneBridgeId(successorId), {
            dependencyEdges: depPayload.dependencyEdges,
          });
        } else {
          await updateProjectTask(successorId, depPayload);
        }
        ganttScheduleDebugLog('createDependency: keep dates; store implied lag', {
          impliedLag,
          threshold: CREATE_DEP_IMPLIED_LAG_MOVE_THRESHOLD,
          successorTitle: succ.title,
        });
        ganttScheduleDebugLog('createDependency: persist finished');
        return;
      }

      const newEdges: TaskDependencyEdge[] = [
        ...existing,
        { predecessorId, type: edgeType, lagBusinessDays: CREATE_DEP_LARGE_GAP_LAG_BUSINESS_DAYS },
      ];
      const depPayload = dependencyPayload(newEdges);
      const mergedSucc: ProjectTask = { ...succ, ...depPayload };
      const mergedTasks = schedulingTasks.map((t) => (t.id === successorId ? mergedSucc : t));
      const { dates, error } = enforceAllFsDependencies(mergedTasks);
      if (error) {
        ganttScheduleDebugWarn('createDependency: enforceAllFsDependencies error', error);
        window.alert(error);
        return;
      }
      const previewChanges = diffDateChanges(schedulingBaselineNodes, dates);
      ganttScheduleDebugLog('createDependency: enforce result (large implied gap → lag 1)', {
        successorTitle: succ.title,
        impliedLag,
        storedLag: CREATE_DEP_LARGE_GAP_LAG_BUSINESS_DAYS,
        dateChangeCount: previewChanges.length,
        changedTaskIds: previewChanges.map((c) => c.id),
        note:
          previewChanges.length === 0
            ? 'No bar dates moved — successor already satisfied FS/SS constraints; only dependency edges will persist.'
            : undefined,
      });
      await persistScheduleAfterEnforce(mergedTasks, dates, successorId, 'createDependency');
      ganttScheduleDebugLog('createDependency: persist finished');
    },
    [
      projectSchedulingNodes,
      schedulingBaselineNodes,
      selectedProjectId,
      resolveTaskProjectId,
      persistScheduleAfterEnforce,
      user,
      updateProjectTask,
      updateOnboardingItem,
      updateMilestone,
    ],
  );

  const handleUpdateDependencyLag = useCallback(
    async (successorId: string, predecessorId: string, payload: UpdateDependencyPayload) => {
      successorId = unwrapSupplierDisplayTaskId(successorId) ?? successorId;
      predecessorId = unwrapSupplierDisplayTaskId(predecessorId) ?? predecessorId;
      const schedulingTasks = projectSchedulingNodes;
      const succ = schedulingTasks.find((t) => t.id === successorId);
      if (!succ) return;
      const lag = Math.max(-500, Math.min(500, Math.floor(Number(payload.lagBusinessDays) || 0)));
      const type: DependencyEdgeType =
        payload.type === 'SS' || payload.type === 'FF' || payload.type === 'SF' || payload.type === 'FS'
          ? payload.type
          : 'FS';
      const edges = getDependencyEdges(succ).map((e) =>
        e.predecessorId === predecessorId ? { ...e, lagBusinessDays: lag, type } : e,
      );
      if (!edges.some((e) => e.predecessorId === predecessorId)) return;
      const depPayload = dependencyPayload(edges);
      const mergedSucc: ProjectTask = { ...succ, ...depPayload };
      const mergedTasks = schedulingTasks.map((t) => (t.id === successorId ? mergedSucc : t));
      const { dates, error } = enforceAllFsDependencies(mergedTasks);
      if (error) {
        window.alert(error);
        return;
      }
      await persistScheduleAfterEnforce(mergedTasks, dates, successorId, 'updateDependencyLag');
    },
    [projectSchedulingNodes, persistScheduleAfterEnforce],
  );

  const handleRemoveDependency = useCallback(
    async (successorId: string, predecessorId: string) => {
      successorId = unwrapSupplierDisplayTaskId(successorId) ?? successorId;
      predecessorId = unwrapSupplierDisplayTaskId(predecessorId) ?? predecessorId;
      const schedulingTasks = projectSchedulingNodes;
      const succ = schedulingTasks.find((t) => t.id === successorId);
      if (!succ) return;
      const before = getDependencyEdges(succ);
      const edges = before.filter((e) => e.predecessorId !== predecessorId);
      if (edges.length === before.length) return;
      const depPayload = dependencyPayload(edges);
      const mergedSucc: ProjectTask = { ...succ, ...depPayload };
      const mergedTasks = schedulingTasks.map((t) => (t.id === successorId ? mergedSucc : t));
      const { dates, error } = enforceAllFsDependencies(mergedTasks);
      if (error) {
        window.alert(error);
        return;
      }
      await persistScheduleAfterEnforce(mergedTasks, dates, successorId, 'removeDependency');
    },
    [projectSchedulingNodes, persistScheduleAfterEnforce],
  );

  const detailTaskForPanel = useMemo(() => {
    if (!selectedTask) return null;
    const fromViews = tasksForViews.find((t) => t.id === selectedTask.id)
      ?? groupTimelineTasks.find((t) => t.id === selectedTask.id);
    const live = fromViews || projectTasks.find((t) => t.id === selectedTask.id) || selectedTask;
    const overrideId = unwrapSupplierDisplayTaskId(selectedTask.id) ?? selectedTask.id;
    const override = overrideId ? taskSupplierOverrides[overrideId] : undefined;
    return {
      ...live,
      supplierOrgIds: override?.supplierOrgIds ?? selectedTask.supplierOrgIds ?? live.supplierOrgIds,
      supplierManual: override?.supplierManual ?? selectedTask.supplierManual ?? live.supplierManual,
    };
  }, [selectedTask, tasksForViews, groupTimelineTasks, projectTasks, taskSupplierOverrides]);

  const panelChildTasks = useMemo(() => {
    if (!detailTaskForPanel?.id || detailTaskForPanel.parentTaskId) return [];
    const kids = childTasksByParent.get(detailTaskForPanel.id) ?? [];
    if (!Object.keys(taskSupplierOverrides).length) return kids;
    return kids.map((t) => {
      const tid = unwrapSupplierDisplayTaskId(t.id) ?? t.id;
      const override = tid ? taskSupplierOverrides[tid] : undefined;
      return override ? { ...t, ...override } : t;
    });
  }, [detailTaskForPanel, childTasksByParent, taskSupplierOverrides]);

  useEffect(() => {
    const taskId = detailTaskForPanel?.id;
    if (!taskId || !user?.id || supplierHubMode) {
      setPanelTaskReminders([]);
      return;
    }
    return subscribeTaskReminders(taskId, user.id, setPanelTaskReminders);
  }, [detailTaskForPanel?.id, user?.id, supplierHubMode]);

  const handleCreateTaskReminder = useCallback(
    async (
      remindAt: Date,
      message?: string,
      opts?: { kind?: 'once' | 'recurring'; intervalMinutes?: number },
    ) => {
      if (!detailTaskForPanel?.id || !user?.id) return;
      await createTaskReminder({
        taskId: detailTaskForPanel.id,
        projectId: detailTaskForPanel.projectId,
        userId: user.id,
        remindAt,
        message,
        kind: opts?.kind ?? 'once',
        intervalMinutes: opts?.intervalMinutes,
      });
      scheduleTaskReminderFlush(remindAt);
    },
    [detailTaskForPanel, user?.id],
  );

  const handleCancelTaskReminder = useCallback(async (reminderId: string) => {
    await cancelTaskReminder(reminderId);
  }, []);

  const handleCreateSubtask = useCallback(
    async (
      parentId: string,
      title: string,
      opts?: { structures?: { id: string; name: string }[] },
    ) => {
      const parent =
        tasksForViews.find((t) => t.id === parentId)
        ?? projectTasks.find((t) => t.id === parentId)
        ?? groupTimelineSourceTasks.find((t) => t.id === parentId);
      if (!parent?.id) {
        pushPmToast(
          'error',
          'Could not create subtask',
          'Parent task was not found on this timeline. Refresh and try again.',
        );
        throw new Error('Parent task not found');
      }
      try {
        const siblings = childTasksByParent.get(parentId) ?? [];
        const maxPos = siblings.reduce((m, t) => Math.max(m, t.position || 0), 0);
        const structures = opts?.structures ?? [];
        const childDates = defaultSubtaskDateRange(parent);
        await createProjectTask({
          projectId: parent.projectId,
          groupId: parent.groupId,
          parentTaskId: parentId,
          title: title.trim(),
          department: parent.department,
          assignedTo: [...(parent.assignedTo || [])],
          status: 'pending',
          priority: parent.priority,
          ...childDates,
          dependencies: [],
          progress: 0,
          position: maxPos + 1,
          ...subtaskStructurePayload(structures),
        });
        const rollup = rollupDatesFromChildren([
          ...siblings,
          {
            id: '__new__',
            parentTaskId: parentId,
            startDate: childDates.startDate,
            endDate: childDates.endDate,
          } as ProjectTask,
        ]);
        if (rollup && !sameRollupDates(parent, rollup)) {
          if (isOnboardingBridgeTaskId(parentId)) {
            if (user) {
              await updateOnboardingItem(
                parent.projectId,
                onboardingTaskIdToItemId(parentId),
                { startDate: rollup.startDate, endDate: rollup.endDate },
                user.name || user.email || 'Unknown',
              );
            }
          } else {
            await updateProjectTask(parentId, rollup);
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message !== 'Parent task not found') {
          pushPmToast('error', 'Could not create subtask', message);
        }
        throw err;
      }
    },
    [
      tasksForViews,
      projectTasks,
      groupTimelineSourceTasks,
      childTasksByParent,
      createProjectTask,
      updateProjectTask,
      updateOnboardingItem,
      user,
      pushPmToast,
    ],
  );

  const handleSelectSubtask = useCallback(
    (child: ProjectTask) => {
      if (detailTaskForPanel) setPanelParentTask(detailTaskForPanel);
      setSelectedTask(child);
    },
    [detailTaskForPanel],
  );

  const handleBackToParentTask = useCallback(() => {
    if (panelParentTask) {
      setSelectedTask(panelParentTask);
      return;
    }
    const parentId = detailTaskForPanel?.parentTaskId;
    if (!parentId) return;
    const parent =
      projectTasks.find((t) => t.id === parentId)
      ?? tasksForViews.find((t) => t.id === parentId)
      ?? null;
    if (parent) setSelectedTask(parent);
  }, [panelParentTask, detailTaskForPanel?.parentTaskId, projectTasks, tasksForViews]);

  const handleOnboardingToggle = useCallback(async (itemId: string, completed: boolean) => {
    if (!selectedProjectId || !user || !currentOnboarding) return;
    const userName = user.name || user.email || 'Unknown';

    const ob = currentOnboarding;
    const sortedItems = [...ob.items].sort((a, b) => a.order - b.order);
    const completedItem = sortedItems.find(i => i.id === itemId);
    const nextItem = sortedItems.find(i => i.id !== itemId && !i.completed);

    // When marking an onboarding item complete, route through the actuals modal first
    // so we capture the real start/end and auto-lock the bridge task.
    if (completed && completedItem && !completedItem.completed) {
      const sched = onboardingItemToProjectTask(completedItem, selectedProjectId, filteredGroups);
      const bridgeId = `onboarding-task:${selectedProjectId}:${completedItem.id}`;
      setPendingCompletionTask({
        taskId: bridgeId,
        title: completedItem.title,
        scheduledStart: sched.startDate,
        scheduledEnd: sched.endDate,
        projectId: selectedProjectId,
        isOnboarding: true,
      });
      return;
    }

    try {
      await updateOnboardingItem(selectedProjectId, itemId, { completed }, userName);
    } catch (err) {
      console.error('Error toggling onboarding item:', err);
      return;
    }

    if (!completed) return;

    if (nextItem?.assigneeId) {
      try {
        await addDoc(collection(db, 'userNotifications'), {
          userId: nextItem.assigneeId,
          type: 'onboarding_action_needed',
          title: 'Your turn on project onboarding',
          message: `"${completedItem?.title}" was completed by ${userName} on ${ob.projectName}. Next up: "${nextItem.title}"`,
          read: false,
          projectId: selectedProjectId,
          createdAt: Timestamp.fromDate(new Date()),
        });
      } catch (err) {
        console.warn('Failed to create onboarding notification:', err);
      }
    }

    const doneCount = sortedItems.filter(i => i.completed || i.id === itemId).length;
    const progress = `${doneCount}/${sortedItems.length}`;
    const functionsBase = process.env.REACT_APP_FUNCTIONS_URL
      || `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'lctf-projects'}.cloudfunctions.net`;
    try {
      const resp = await fetch(`${functionsBase}/sendPortalNotification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'onboarding_step_completed',
          projectName: ob.projectName,
          projectId: selectedProjectId,
          completedItemTitle: completedItem?.title || '',
          completedBy: userName,
          progress,
          nextItemTitle: nextItem?.title || 'None',
          nextAssigneeName: nextItem?.assigneeName || 'Unassigned',
          nextAssigneeEmail: nextItem?.assigneeId
            ? crmUsers.find(u => u.uid === nextItem.assigneeId)?.email || ''
            : '',
        }),
      });
      if (!resp.ok) console.warn('Slack onboarding notification failed:', resp.status);
    } catch (err) {
      console.warn('Slack onboarding notification error:', err);
    }
  }, [selectedProjectId, user, currentOnboarding, updateOnboardingItem, crmUsers]);

  const handleOnboardingAssigneeChange = useCallback(async (itemId: string, assigneeId: string | undefined, assigneeName: string | undefined) => {
    if (!selectedProjectId || !user) return;
    const userName = user.name || user.email || 'Unknown';
    try {
      await updateOnboardingItem(selectedProjectId, itemId, {
        assigneeId,
        assigneeName,
        assignedTo: assigneeId ? [assigneeId] : [],
      }, userName);
    } catch (err) {
      console.error('Error changing onboarding assignee:', err);
    }
  }, [selectedProjectId, user, updateOnboardingItem]);

  const handleOnboardingDepartmentChange = useCallback(
    async (itemId: string, department: ProjectDepartment) => {
      if (!selectedProjectId || !user) return;
      const userName = user.name || user.email || 'Unknown';
      const gid = resolveTaskGroupIdForDepartment(department, selectedProjectId, filteredGroups);
      try {
        await updateOnboardingItem(
          selectedProjectId,
          itemId,
          { department, taskGroupId: gid || undefined },
          userName,
        );
      } catch (err) {
        console.error('Error changing onboarding department:', err);
      }
    },
    [selectedProjectId, user, updateOnboardingItem, filteredGroups],
  );

  const handleOnboardingReorder = useCallback(async (itemId: string, direction: 'up' | 'down') => {
    if (!selectedProjectId || !user || !currentOnboarding) return;
    const items = [...currentOnboarding.items].sort((a, b) => a.order - b.order);
    const idx = items.findIndex(i => i.id === itemId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= items.length) return;

    const updatedItems = [...items];
    [updatedItems[idx], updatedItems[swapIdx]] = [updatedItems[swapIdx], updatedItems[idx]];
    updatedItems.forEach((it, i) => it.order = i);

    const userName = user.name || user.email || 'Unknown';
    try {
      const { updateDoc: fbUpdateDoc, doc: fbDoc, Timestamp: fbTimestamp } = await import('firebase/firestore');
      const { db: fbDb } = await import('../utils/firebase');
      const firestoreItems = updatedItems.map(it => ({
        ...it,
        completedAt: it.completedAt ? fbTimestamp.fromDate(it.completedAt instanceof Date ? it.completedAt : new Date(it.completedAt)) : null,
      }));
      await fbUpdateDoc(fbDoc(fbDb, 'projectOnboarding', selectedProjectId), { items: firestoreItems });
    } catch (err) {
      console.error('Error reordering onboarding items:', err);
    }
  }, [selectedProjectId, user, currentOnboarding]);

  const [generatingChecklist, setGeneratingChecklist] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  const handleGenerateChecklist = useCallback(async () => {
    if (!selectedProjectId || !currentProject) return;
    setGeneratingChecklist(true);
    try {
      const templateSnap = await getDoc(doc(db, 'settings', 'onboardingChecklist'));
      if (!templateSnap.exists()) {
        alert('No onboarding template configured. Go to Settings → Onboarding to create one first.');
        setGeneratingChecklist(false);
        return;
      }
      const template = templateSnap.data();
      if (!template.items?.length) {
        alert('Onboarding template has no items. Go to Settings → Onboarding to add checklist items.');
        setGeneratingChecklist(false);
        return;
      }

      const userCache: Record<string, string> = {};
      for (const item of template.items) {
        if (item.defaultAssigneeId && !userCache[item.defaultAssigneeId]) {
          try {
            const uSnap = await getDoc(doc(db, 'users', item.defaultAssigneeId));
            if (uSnap.exists()) {
              const u = uSnap.data();
              userCache[item.defaultAssigneeId] = u.displayName || u.email || '';
            }
          } catch { /* skip */ }
        }
      }

      const obItems = template.items.map((t: any) => ({
        id: t.id,
        title: t.title,
        description: t.description || '',
        category: t.category,
        department: normalizeOnboardingDepartment(t.department),
        assigneeId: t.defaultAssigneeId || null,
        assigneeName: t.defaultAssigneeId ? (userCache[t.defaultAssigneeId] || null) : null,
        required: t.required ?? true,
        order: t.order,
        onCriticalPath: !!t.onCriticalPath,
        completed: false,
        completedAt: null,
        completedBy: null,
        completedByName: null,
      }));

      await setDoc(doc(db, 'projectOnboarding', selectedProjectId), {
        projectId: selectedProjectId,
        projectName: currentProject.name || 'Project',
        templateVersion: template.updatedAt || Timestamp.fromDate(new Date()),
        status: 'active',
        items: obItems,
        createdAt: Timestamp.fromDate(new Date()),
      });
    } catch (err) {
      console.error('Error generating onboarding checklist:', err);
      alert('Failed to generate checklist. Check console for details.');
    } finally {
      setGeneratingChecklist(false);
    }
  }, [selectedProjectId, currentProject]);

  const handleSyncTemplate = useCallback(async (): Promise<number> => {
    if (!selectedProjectId || !currentOnboarding) return 0;

    const templateSnap = await getDoc(doc(db, 'settings', 'onboardingChecklist'));
    if (!templateSnap.exists()) throw new Error('No template configured');
    const template = templateSnap.data();
    if (!template.items?.length) return 0;

    const existingTitles = new Set(currentOnboarding.items.map(i => i.title.toLowerCase().trim()));
    const newTemplateItems = (template.items as any[]).filter(
      t => !existingTitles.has((t.title || '').toLowerCase().trim())
    );

    if (newTemplateItems.length === 0) return 0;

    const userCache: Record<string, string> = {};
    for (const item of newTemplateItems) {
      if (item.defaultAssigneeId && !userCache[item.defaultAssigneeId]) {
        try {
          const uSnap = await getDoc(doc(db, 'users', item.defaultAssigneeId));
          if (uSnap.exists()) {
            const u = uSnap.data();
            userCache[item.defaultAssigneeId] = u.displayName || u.email || '';
          }
        } catch { /* skip */ }
      }
    }

    const maxOrder = currentOnboarding.items.reduce((max, i) => Math.max(max, i.order), 0);
    const newItems = newTemplateItems.map((t: any, idx: number) => ({
      id: `sync-${Date.now()}-${idx}`,
      title: t.title,
      description: t.description || '',
      category: t.category || '',
      department: normalizeOnboardingDepartment(t.department),
      assigneeId: t.defaultAssigneeId || null,
      assigneeName: t.defaultAssigneeId ? (userCache[t.defaultAssigneeId] || null) : null,
      required: t.required ?? true,
      order: maxOrder + idx + 1,
      onCriticalPath: !!t.onCriticalPath,
      completed: false,
      completedAt: null,
      completedBy: null,
      completedByName: null,
    }));

    const allItems = [...currentOnboarding.items, ...newItems];
    const firestoreItems = allItems.map(it => {
      const clean: Record<string, any> = {};
      for (const [k, v] of Object.entries(it)) {
        if (v === undefined) continue;
        clean[k] = v;
      }
      if (clean.completedAt && clean.completedAt instanceof Date) {
        clean.completedAt = Timestamp.fromDate(clean.completedAt);
      } else if (!clean.completedAt) {
        clean.completedAt = null;
      }
      if (!clean.completedBy) clean.completedBy = null;
      if (!clean.completedByName) clean.completedByName = null;
      return clean;
    });

    await updateDoc(doc(db, 'projectOnboarding', selectedProjectId), {
      items: firestoreItems,
      status: 'active',
    });

    return newItems.length;
  }, [selectedProjectId, currentOnboarding]);

  if (loading) return <LoadingScreen />;

  return (
    <ProjectHubScopedDataProvider value={scopedActive ? scopedData : null}>
    <div className="pm-hub">
      <ProjectUploadModalProvider projectId={selectedProjectId} dealId={currentProject?.dealId}>
      {isDesignerRole(user?.role) ? (
        <ArchitectHeader />
      ) : isArchitectUserRole ? (
        <ArchitectHeader
          homePath="/architect-portal"
          homeLinkLabel="Status"
          showStandup={false}
          showHours={false}
        />
      ) : user?.role === 'engineer' ? (
        <EngineerHeader />
      ) : (
        <AppHeader
          supplierNavPreviewOrgId={supplierPreviewOrgId}
          supplierNavPreviewRole={supplierPreviewOrgId ? supplierPreviewRole : null}
        />
      )}
      {architectPreviewUid && (
        <div className="pm-hub-preview-banner" role="status">
          <StaffPortalViewAs
            designers={designersList.filter((u) => !isCrmTestUser(u))}
            architects={architectsList}
            engineers={crmUsers.filter((u) => u.role === 'engineer' && u.isActive && !isCrmTestUser(u))}
            current={{ kind: 'architect', uid: architectPreviewUid }}
            currentName={crmUsers.find((u) => u.uid === architectPreviewUid)?.displayName}
            onChange={(next) => navigate(staffPortalPreviewHref(next.kind, next.uid))}
            onExit={() => navigate(staffPortalPreviewExitHref())}
          />
        </div>
      )}
      {supplierPreviewOrgId && (
        <div className="pm-hub-preview-banner" role="status">
          <Eye size={14} aria-hidden />
          <span>
            Previewing as{' '}
            <strong>
              {supplierOrgs.find((o) => o.id === supplierPreviewOrgId)?.name || supplierPreviewOrgId}
            </strong>{' '}
            — Project Hub limited to Production on their awarded projects.
          </span>
          <button
            type="button"
            className="pm-hub-preview-banner-exit"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('viewAs');
              next.delete('as');
              setSearchParams(next, { replace: true });
            }}
          >
            <X size={13} aria-hidden /> Exit preview
          </button>
        </div>
      )}
      <div className="pm-hub-layout">
        {projectsSidebarOpen && (
          <aside className="pm-projects-drawer" aria-label="Projects">
            <PMSidebar
              projects={hubVisibleProjects}
              deals={enhancedDeals}
              crmUsers={crmUsers}
              selectedProjectId={selectedProjectId}
              onSelectProject={handleSelectProject}
              onCreateProject={() => setShowCreateModal(true)}
              onCloseProject={partnerHubMode ? undefined : handleCloseProject}
              onReopenProject={partnerHubMode ? undefined : handleReopenProject}
              onDeleteProject={supplierHubMode ? undefined : handleRequestDelete}
              hideCreate={isLimitedProjectViewer(user?.role, userDesignations) || partnerHubMode}
              hideValues={isLimitedProjectViewer(user?.role, userDesignations) || partnerHubMode}
              onRenameProject={isLimitedProjectViewer(user?.role, userDesignations) || partnerHubMode ? undefined : (project) => {
                // Select the project (so `currentProject` and `currentDeal`
                // resolve to it) then open the single-deal rename wizard
                // mounted near the bottom of this file. React 18 batches
                // both updates into one render.
                if (project.id) handleSelectProject(project.id);
                setRenamingProject(true);
              }}
              onCollapse={() => {
                setProjectsSidebarOpen(false);
                try {
                  localStorage.setItem('projectHubSidebarOpen', 'false');
                } catch {
                  /* ignore */
                }
              }}
            />
          </aside>
        )}

        {/* Main content */}
        <div className="pm-hub-main">
          {!projectsSidebarOpen && (
            <button
              type="button"
              className="pm-projects-sidebar-bump"
              onClick={() => {
                setProjectsSidebarOpen(true);
                try {
                  localStorage.setItem('projectHubSidebarOpen', 'true');
                } catch {
                  /* ignore */
                }
              }}
              aria-label="Open projects sidebar"
            >
              <ChevronRight size={20} strokeWidth={2} aria-hidden />
            </button>
          )}

          {/* Toolbar */}
          <div className="pm-hub-toolbar">
            {currentProject ? (
              <>
                {/* ── Row 1: breadcrumb + PM ── */}
                <div className="pm-toolbar-row-1">
                  <div className="pm-toolbar-row-1-start">
                  <nav className="pm-hub-breadcrumb" aria-label="Breadcrumb">
                    <ol className="pm-hub-breadcrumb-list">
                      <li>
                        <button
                          type="button"
                          className="pm-hub-breadcrumb-link"
                          title="All projects (H)"
                          aria-keyshortcuts="H"
                          onClick={() => handleSelectProject(null)}
                        >
                          Projects
                        </button>
                      </li>
                      <li className="pm-hub-breadcrumb-sep" aria-hidden>
                        <ChevronRight size={14} strokeWidth={2} />
                      </li>
                      {contractedAfterPrecon ? (
                        <>
                          <li>
                            <span className="pm-hub-breadcrumb-past">Preconstruction</span>
                          </li>
                          <li className="pm-hub-breadcrumb-sep" aria-hidden>
                            <ChevronRight size={14} strokeWidth={2} />
                          </li>
                          <li>
                            <span className="pm-hub-breadcrumb-current" aria-current="page">
                              Under Contract
                            </span>
                          </li>
                        </>
                      ) : (
                        <li>
                          <span className="pm-hub-breadcrumb-current" aria-current="page">
                            {PROJECT_PHASE_LABELS[
                              currentProject.phase === 'estimating'
                                ? 'estimating'
                                : currentProject.phase === 'preconstruction'
                                  ? 'preconstruction'
                                  : 'contracted'
                            ]}
                          </span>
                        </li>
                      )}
                    </ol>
                  </nav>
                  {currentDeal?.leadId && !hideCommercialSurfaces && canAccessProjectHubClientPortalControls(hubSurfaceRole, hubSurfaceDesignations) && (
                    <button
                      type="button"
                      className="pm-hub-client-portal-btn"
                      onClick={openHubClientPortal}
                      title="Open client portal for this project"
                      aria-label="Open client portal for this project"
                    >
                      <UserCircle size={15} strokeWidth={1.85} aria-hidden />
                    </button>
                  )}
                  {currentDeal?.leadId && currentDeal?.id && !supplierHubMode && canAccessInspirationBoard(hubSurfaceRole, hubSurfaceDesignations) && (
                    <button
                      type="button"
                      className="pm-hub-client-portal-btn"
                      onClick={openHubInspiration}
                      title="Open inspiration board for this project (I)"
                      aria-label="Open inspiration board for this project"
                      aria-keyshortcuts="I"
                    >
                      <LayoutGrid size={15} strokeWidth={1.85} aria-hidden />
                    </button>
                  )}
                  {currentProject?.dealId && !hideCommercialSurfaces && (isCurrentUserSalesRep || isCurrentUserProjectManager) && (
                    <button
                      type="button"
                      className="pm-hub-client-portal-btn"
                      onClick={openHubProposal}
                      title="Open proposal builder for this project (P)"
                      aria-label="Open proposal builder for this project"
                      aria-keyshortcuts="P"
                    >
                      <FileText size={15} strokeWidth={1.85} aria-hidden />
                    </button>
                  )}
                  </div>

                  {/* Precon billing (preconstruction) + PM — right side of row 1 */}
                  <div className="pm-hub-toolbar-row-1-end">
                  {currentProject.id ? (
                    <ProjectNotificationPrefsControl
                      projectId={currentProject.id}
                      projectName={currentProject.name}
                      compact
                    />
                  ) : null}
                  <button
                    type="button"
                    className="pm-hub-client-portal-btn"
                    onClick={() => setShowQuickSearch(true)}
                    title="Quick search (Space)"
                    aria-label="Quick search projects, tasks, and comments"
                  >
                    <Search size={15} strokeWidth={1.85} aria-hidden />
                  </button>
                  {currentProject.phase === 'estimating' && currentProject.id && userCanPromoteToPreconstruction && !supplierHubMode && (
                    <button
                      type="button"
                      className="pm-btn pm-btn-secondary pm-hub-promote-precon-btn"
                      onClick={async () => {
                        if (!currentProject.id) return;
                        await updateTimberProject(currentProject.id, { phase: 'preconstruction' });
                      }}
                    >
                      Move to Preconstruction
                    </button>
                  )}
                  {isEarlyFunnelProjectPhase(currentProject.phase) &&
                    currentProject.id &&
                    userCanEditPreconBillable && !partnerHubMode && (
                    <div className="pm-hub-billing-container">
                      <div className="pm-hub-pm-badge pm-hub-billing-badge">
                        <div className="pm-hub-billing-avatar" aria-hidden>
                          B
                        </div>
                        <div className="pm-hub-pm-info pm-hub-billing-info">
                          <span className="pm-hub-pm-label">Billing</span>
                          <select
                            id="pm-ongoing-billable"
                            className="pm-hub-billing-select"
                            value={currentProject.ongoingBillable ? 'billable' : 'nonbillable'}
                            onChange={async e => {
                              const billable = e.target.value === 'billable';
                              await updateTimberProject(currentProject.id!, { ongoingBillable: billable });
                            }}
                            title="Billable precon work is totaled separately on the all-projects time report"
                            aria-label="Preconstruction billing"
                          >
                            <option value="nonbillable">Non-Billable</option>
                            <option value="billable">Billable</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {!supplierHubMode && (
                  <div className="pm-hub-pm-container" ref={pmPickerRef}>
                    {currentPM ? (
                      <div
                        className={`pm-hub-pm-badge${architectHubMode ? ' pm-hub-pm-badge--readonly' : ''}`}
                        onMouseEnter={architectHubMode ? undefined : () => {
                          clearTimeout(pmTooltipTimeout.current);
                          setPmTooltipVisible(true);
                        }}
                        onMouseLeave={architectHubMode ? undefined : () => {
                          pmTooltipTimeout.current = setTimeout(() => setPmTooltipVisible(false), 200);
                        }}
                        onClick={architectHubMode ? undefined : () => setShowPMPicker(!showPMPicker)}
                      >
                        <div className="pm-hub-pm-avatar">
                          {currentPM.photoURL ? (
                            <img src={currentPM.photoURL} alt="" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.removeAttribute('style'); }} />
                          ) : null}
                          <span style={currentPM.photoURL ? { display: 'none' } : undefined}>
                            {currentPM.displayName.split(/\s+/).map(w => w[0]?.toUpperCase()).join('').slice(0, 2)}
                          </span>
                        </div>
                        <div className="pm-hub-pm-info">
                          <span className="pm-hub-pm-label">PM</span>
                          <span className="pm-hub-pm-name">{currentPM.displayName}</span>
                        </div>

                        {pmTooltipVisible && (
                          <div
                            className="pm-hub-pm-tooltip"
                            onMouseEnter={() => clearTimeout(pmTooltipTimeout.current)}
                            onMouseLeave={() => {
                              pmTooltipTimeout.current = setTimeout(() => setPmTooltipVisible(false), 200);
                            }}
                          >
                            <div className="pm-hub-pm-tooltip-name">{currentPM.displayName}</div>
                            {currentPM.title && <div className="pm-hub-pm-tooltip-title">{currentPM.title}</div>}
                            <div className="pm-hub-pm-tooltip-divider" />
                            <div className="pm-hub-pm-tooltip-row">
                              <Mail size={12} />
                              <a href={`mailto:${currentPM.email}`}>{currentPM.email}</a>
                            </div>
                            {currentPM.phone && (
                              <div className="pm-hub-pm-tooltip-row">
                                <Phone size={12} />
                                <a href={`tel:${currentPM.phone}`}>{currentPM.phone}</a>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : !architectHubMode ? (
                      <button
                        className="pm-hub-pm-assign-btn"
                        onClick={() => setShowPMPicker(!showPMPicker)}
                      >
                        <UserCircle size={14} />
                        Assign PM
                      </button>
                    ) : null}

                    {showPMPicker && !architectHubMode && (
                      <div className="pm-hub-pm-picker">
                        <div className="pm-hub-pm-picker-header">Project Manager</div>
                        {projectManagers.length === 0 ? (
                          <div className="pm-hub-pm-picker-empty">
                            No employees designated as PM yet. Add the designation in Settings &rarr; Users.
                          </div>
                        ) : (
                          <div className="pm-hub-pm-picker-list">
                            {projectManagers.map(pm => (
                              <button
                                key={pm.uid}
                                className={`pm-hub-pm-picker-item ${currentProject.projectManager === pm.uid ? 'active' : ''}`}
                                onClick={async () => {
                                  if (currentProject.id) {
                                    await updateTimberProject(currentProject.id, { projectManager: pm.uid });
                                  }
                                  setShowPMPicker(false);
                                }}
                              >
                                <span className="pm-hub-pm-picker-name">{pm.displayName}</span>
                                {pm.title && <span className="pm-hub-pm-picker-title">{pm.title}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                        {currentProject.projectManager && (
                          <button
                            className="pm-hub-pm-picker-remove"
                            onClick={async () => {
                              if (currentProject.id) {
                                await updateTimberProject(currentProject.id, { projectManager: '' });
                              }
                              setShowPMPicker(false);
                            }}
                          >
                            Remove PM
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  )}
                  </div>
                </div>

                {/* ── Row 2: title + views + actions ── */}
                {(() => {
                  const headerNumber = currentDeal?.projectNumber?.trim() || '';
                  const headerNumberDisplay = headerNumber
                    ? (headerNumber.startsWith('#') ? headerNumber : `#${headerNumber}`)
                    : '';
                  const headerDescriptor = (
                    (currentDeal?.descriptor || '').trim()
                    || parseLegacyTitle(currentDeal?.title || '').descriptor
                    || currentProject.name
                    || ''
                  ).trim();
                  return (
                <div className="pm-toolbar-row-2">
                  <div className="pm-hub-project-title">
                    <div
                      className="pm-hub-project-title-main pm-hub-project-title-hover"
                      onMouseEnter={handleProjectTitleMouseEnter}
                      onMouseLeave={handleProjectTitleMouseLeave}
                    >
                      <h2 className="pm-hub-project-heading">
                        {headerNumberDisplay && (
                          <span className="pm-hub-project-number">{headerNumberDisplay}</span>
                        )}
                        {headerDescriptor && (
                          <span className="pm-hub-project-descriptor">{headerDescriptor}</span>
                        )}
                        {currentProjectSimulatingUc && (
                          <span
                            className="pm-hub-sim-uc-flask"
                            title="Simulated under contract for planning — not a real contract"
                          >
                            <FlaskConical size={13} strokeWidth={2.25} aria-hidden />
                          </span>
                        )}
                        {projectMissingSiteAddress && (
                          <span
                            className="pm-hub-project-address-warn"
                            title="No project site address — add one to enable install weather and maps"
                            aria-label="No project site address"
                          >
                            <AlertTriangle size={12} aria-hidden />
                          </span>
                        )}
                      </h2>
                      {!supplierHubMode && (
                        <button
                          type="button"
                          className="pm-hub-rename-btn"
                          onClick={() => setRenamingProject(true)}
                          title="Edit project name, site address, designer, architect, and engineer"
                          aria-label="Edit project"
                        >
                          <PenLine size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="pm-toolbar-actions">
                    <div className="pm-hub-view-cluster">
                      <div className="pm-hub-views">
                        {([
                          { key: 'table', icon: <LayoutList size={14} />, label: 'Table' },
                          { key: 'files' as const, icon: <Paperclip size={14} />, label: 'Files' },
                          ...(canAccessSuppliersTab
                            ? [{ key: 'suppliers' as const, icon: <Building2 size={14} />, label: 'Suppliers' }]
                            : []),
                          ...(canAccessChangeOrdersTab
                            ? [{ key: 'changeOrders' as const, icon: <ClipboardList size={14} />, label: 'Change Orders' }]
                            : []),
                          ...(!architectHubMode && !supplierHubMode
                            ? [{ key: 'crew' as const, icon: <HardHat size={14} />, label: 'Crew' }]
                            : []),
                          ...(!architectHubMode
                            ? [{ key: 'gantt' as const, icon: <GanttIcon size={14} />, label: 'Timeline' }]
                            : []),
                        ] as { key: ViewMode; icon: React.ReactNode; label: string }[]).map(v => {
                          const hotkey =
                            selectedProjectId != null ? projectHubViewModeHotkeyLabel(v.key) : null;
                          const hotkeyBlocked =
                            (v.key === 'suppliers' && !canAccessSuppliersTab) ||
                            (v.key === 'changeOrders' && !canAccessChangeOrdersTab);
                          const showHotkeyTip = !!hotkey && !hotkeyBlocked;
                          return (
                          <button
                            key={v.key}
                            type="button"
                            className={`pm-view-btn ${viewMode === v.key ? 'active' : ''}`}
                            onClick={() => setViewMode(v.key)}
                            aria-keyshortcuts={showHotkeyTip ? hotkey! : undefined}
                          >
                            {v.icon}
                            <span>{v.label}</span>
                            {showHotkeyTip ? (
                              <span className="pm-view-btn-shortcut-tip" role="tooltip">
                                Shortcut: <kbd>{hotkey}</kbd>
                              </span>
                            ) : null}
                          </button>
                          );
                        })}
                      </div>
                      {canViewTimeResults && (
                      <button
                        type="button"
                        className="pm-hub-time-report-btn"
                        onClick={() => setShowTimeReport(true)}
                        title="Time report — hours by task, team, and activity log"
                        aria-label="Open time report"
                      >
                        <Clock size={13} strokeWidth={2} aria-hidden />
                      </button>
                      )}
                    </div>

                    {viewMode === 'gantt' && selectedProjectId && !supplierHubMode && (
                      <div className="pm-hub-gantt-lock-group">
                        <button
                          type="button"
                          className="pm-hub-gantt-lock-btn"
                          title="Lock every task to its current dates. Locked tasks will not move when other tasks shift."
                          aria-label="Lock every task to its current dates. Locked tasks will not move when other tasks shift."
                          onClick={() => setLockAllTasksConfirm('lock')}
                        >
                          <Lock size={13} strokeWidth={2.25} aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="pm-hub-gantt-lock-btn pm-hub-gantt-lock-btn--ghost"
                          title="Unlock every task in this project (completed tasks stay locked via their actual dates)."
                          aria-label="Unlock every task in this project (completed tasks stay locked via their actual dates)."
                          onClick={() => setLockAllTasksConfirm('unlock')}
                        >
                          <Unlock size={13} strokeWidth={2.25} aria-hidden />
                        </button>
                      </div>
                    )}

                    {viewMode === 'gantt' && selectedProjectId && !supplierHubMode && !architectHubMode
                      && ganttTimelineCollab.showCollaborationUi ? (
                      <GanttTimelineSyncChip
                        ref={ganttUserSyncChipRef}
                        peers={ganttTimelineCollab.freshPeers}
                        selfViewHash={ganttTimelineCollab.selfViewHash}
                        viewHashMatchedPeerCount={ganttTimelineCollab.viewHashMatchedPeerCount}
                        syncId={ganttTimelineCollab.syncId}
                        onSyncWithPeer={ganttTimelineCollab.syncWithPeer}
                        onLeaveSync={ganttTimelineCollab.leaveSync}
                      />
                    ) : null}

                    {viewMode === 'gantt' && selectedProjectId && !supplierHubMode && (
                      <div className="pm-hub-critical-path-wrap">
                        <button
                          type="button"
                          className={`pm-hub-critical-path-btn ${showCriticalPathOverlay ? 'is-on' : ''}`}
                          aria-pressed={showCriticalPathOverlay}
                          aria-describedby="pm-critical-path-help"
                          onClick={() => setShowCriticalPathOverlay((v) => !v)}
                        >
                          <GitBranch size={14} strokeWidth={2.25} aria-hidden />
                          <span>Critical path</span>
                        </button>
                        <div
                          id="pm-critical-path-help"
                          className="pm-hub-critical-path-tooltip"
                          role="tooltip"
                        >
                          <strong className="pm-hub-critical-path-tooltip-title">Critical path overlay</strong>
                          <p>
                            In project management, the <strong>critical path</strong> is the longest chain of work that
                            drives the earliest finish date: delay any task on that path and the project end date slips.
                          </p>
                          <p>
                            Here, the amber dashed connector on the timeline is a <strong>manual highlight</strong> only.
                            Mark tasks as critical path in the task panel (or in the onboarding checklist template). Those
                            tasks are chained in <strong>start-date order</strong> for visualization—this is not full CPM
                            analysis and does not change scheduling.
                          </p>
                          <p className="pm-hub-critical-path-tooltip-muted">
                            Schedule logic still follows your dependencies (the grey dashed links). Use this overlay to
                            communicate which milestones or deliverables you are treating as driving the project timeline.
                          </p>
                        </div>
                      </div>
                    )}

                    {user?.role === 'admin' && !supplierHubMode && !architectHubMode && (
                      <AdvancedFeaturesMenu
                        timelineDriftOn={showTimelineDrift}
                        onToggleTimelineDrift={() => setShowTimelineDrift((v) => !v)}
                        showMeetingsOn={showMeetings}
                        onToggleShowMeetings={() => setShowMeetings((v) => !v)}
                      />
                    )}
                    {!currentOnboarding && selectedProjectId && !supplierHubMode && !architectHubMode && currentProject?.phase === 'contracted' && (
                      <button
                        className="pm-btn pm-btn-sm"
                        onClick={handleGenerateChecklist}
                        disabled={generatingChecklist}
                        style={{ gap: 6 }}
                      >
                        {generatingChecklist ? <Loader2 size={13} className="spinning" /> : <ClipboardCheck size={13} />}
                        {generatingChecklist ? 'Generating...' : 'Onboarding'}
                      </button>
                    )}
                  </div>
                </div>
                  );
                })()}
              </>
            ) : (
              <div className="pm-toolbar-row-2">
                <div className="pm-hub-all-title-row">
                  <h2 className="pm-hub-all-title">All Projects</h2>
                  {viewMode === 'gantt' && !supplierHubMode && (
                    <>
                    {!ganttSplitOpen ? (
                    <>
                    <div className="pm-hub-views pm-hub-group-chips" role="tablist" aria-label="Group timeline filter">
                      {GROUP_TIMELINE_FAMILIES.map((family) => {
                        const familyHotkey = allProjectsFamilyHotkeyLabel(family);
                        const familyIssues = family === 'all'
                          ? null
                          : issuesForFamilyChip(groupTimelineConflictBoard, family);
                        const showConflict = !!familyIssues && hasConflictIssues(familyIssues);
                        return (
                        <button
                          key={family}
                          type="button"
                          role="tab"
                          aria-selected={groupTimelineFamily === family}
                          className={`pm-view-btn${groupTimelineFamily === family ? ' active' : ''}${showConflict ? ' pm-view-btn--has-conflict' : ''}`}
                          aria-keyshortcuts={familyHotkey ?? undefined}
                          onClick={() => selectAllProjectsTimelineFamily(family)}
                        >
                          {GROUP_TIMELINE_FAMILY_LABELS[family]}
                          {familyIssues ? (
                            <GroupTimelineConflictBadge
                              issues={familyIssues}
                              label={GROUP_TIMELINE_FAMILY_LABELS[family]}
                              onShowConflicts={showConflictTasks}
                            />
                          ) : null}
                          {familyHotkey ? (
                            <span className="pm-view-btn-shortcut-tip" role="tooltip">
                              Shortcut: <kbd>{familyHotkey}</kbd>
                            </span>
                          ) : null}
                        </button>
                        );
                      })}
                    </div>
                    <GroupTimelineViewControls
                      layout={groupTimelineLayout}
                      onLayoutChange={(next) => {
                        setGroupTimelineLayout(next);
                        storeGroupTimelineLayout(next);
                      }}
                      sortMode={groupTimelineSort}
                      onSortChange={(next) => {
                        setGroupTimelineSort(next);
                        storeGroupTimelineSort(next);
                      }}
                      onCollapseAll={() => ganttOutlineApiRef.current?.collapseAll()}
                      onExpandAll={() => ganttOutlineApiRef.current?.expandAll()}
                      pinnedCount={ganttPinnedCount}
                      pinLimit={GANTT_COMPARE_PIN_LIMIT}
                      onClearPins={() => ganttOutlineApiRef.current?.clearPins()}
                    />
                    </>
                    ) : null}
                    {!architectHubMode && ganttTimelineCollab.showCollaborationUi ? (
                      <GanttTimelineSyncChip
                        ref={ganttUserSyncChipRef}
                        peers={ganttTimelineCollab.freshPeers}
                        selfViewHash={ganttTimelineCollab.selfViewHash}
                        viewHashMatchedPeerCount={ganttTimelineCollab.viewHashMatchedPeerCount}
                        syncId={ganttTimelineCollab.syncId}
                        onSyncWithPeer={ganttTimelineCollab.syncWithPeer}
                        onLeaveSync={ganttTimelineCollab.leaveSync}
                        explainPaneLink
                      />
                    ) : null}
                    {user?.role === 'admin' && !supplierHubMode && !architectHubMode && viewMode === 'gantt' && (
                      <AdvancedFeaturesMenu
                        timelineDriftOn={showTimelineDrift}
                        onToggleTimelineDrift={() => setShowTimelineDrift((v) => !v)}
                        showMeetingsOn={showMeetings}
                        onToggleShowMeetings={() => setShowMeetings((v) => !v)}
                      />
                    )}
                    {!architectHubMode && !supplierHubMode ? (
                      <button
                        type="button"
                        className={`pm-view-btn${ganttSplitOpen ? ' active' : ''}`}
                        aria-pressed={ganttSplitOpen}
                        aria-keyshortcuts="S"
                        title={ganttSplitOpen ? 'Close the second timeline (S)' : 'Show a second timeline beside this one (S)'}
                        onClick={() => toggleSplitRef.current()}
                      >
                        <GitCompare size={14} aria-hidden />
                        <span>Split</span>
                        <span className="pm-view-btn-shortcut-tip" role="tooltip">
                          Shortcut: <kbd>S</kbd>
                        </span>
                      </button>
                    ) : null}
                    {ganttSplitOpen ? (
                      <button
                        type="button"
                        className={`pm-view-btn${ganttSplitSync ? ' active' : ''}`}
                        aria-pressed={ganttSplitSync}
                        aria-keyshortcuts="M"
                        title={
                          ganttSplitSync
                            ? 'Match is on — family, filters, zoom, and pan stay the same. Press M or click to move each timeline on its own'
                            : 'Press M or click so both timelines use the same header controls, zoom, and pan'
                        }
                        onClick={() => toggleMatchRef.current()}
                      >
                        <Link2 size={14} aria-hidden />
                        <span>Match</span>
                        <span className="pm-view-btn-shortcut-tip" role="tooltip">
                          Shortcut: <kbd>M</kbd>
                        </span>
                      </button>
                    ) : null}
                    {!ganttSplitOpen ? (
                    <>
                    <GroupTimelineColorControls
                      mode={groupTimelineColorMode}
                      onChange={(next) => {
                        setGroupTimelineColorMode(next);
                        storeGroupTimelineColorMode(next);
                      }}
                      legend={groupTimelineLegend}
                    />
                    {groupTimelineFamily === 'all' && (
                      <>
                        <GroupTimelineFilterControls
                          filter={groupTimelineFilter}
                          onChange={(next) => {
                            setGroupTimelineFilter(next);
                            storeGroupTimelineFilter(next);
                            setActiveSavedTimelineViewId(null);
                          }}
                          projects={groupTimelineFilterOptions.projects}
                          sections={groupTimelineFilterOptions.sections}
                          suppliers={groupTimelineFilterOptions.suppliers}
                          clearShortcut="C"
                        />
                        <GroupTimelineSavedViewsControl
                          views={savedTimelineViews}
                          activeViewId={activeSavedTimelineViewId}
                          currentUserId={user?.id}
                          ownerDisplayName={user?.name || user?.email || 'User'}
                          onApply={applySavedTimelineView}
                          onCaptureOutline={captureAllProjectsSavedViewOutline}
                          onSaved={setActiveSavedTimelineViewId}
                        />
                      </>
                    )}
                    {isGroupTimelineSupplierFocusFamily(groupTimelineFamily)
                      && groupTimelineFocusOptions.extraTasks.length > 0 && (
                      <GroupTimelineFocusBanner
                        extraTasks={groupTimelineFocusOptions.extraTasks}
                        onRemove={removeGroupTimelineExtraTask}
                      />
                    )}
                    </>
                    ) : null}
                    </>
                  )}
                </div>
                <div className="pm-toolbar-actions">
                  <button
                    type="button"
                    className="pm-hub-time-report-btn"
                    onClick={() => setShowQuickSearch(true)}
                    title="Quick search (Space)"
                    aria-label="Quick search projects, tasks, and comments"
                  >
                    <Search size={13} strokeWidth={2} aria-hidden />
                  </button>
                  {!architectHubMode && (
                  <button
                    type="button"
                    className={`pm-hub-time-report-btn${viewMode === 'gantt' ? ' pm-hub-time-report-btn-active' : ''}`}
                    onClick={toggleAllProjectsTimeline}
                    aria-label={viewMode === 'gantt' ? 'Close group timeline' : 'Open group timeline'}
                    aria-pressed={viewMode === 'gantt'}
                    aria-keyshortcuts="L"
                  >
                    <GanttIcon size={13} strokeWidth={2} aria-hidden />
                    <span className="pm-view-btn-shortcut-tip" role="tooltip">
                      {viewMode === 'gantt' ? 'Back to all projects' : 'Group timeline'}
                      {' · Shortcut: '}
                      <kbd>L</kbd>
                    </span>
                  </button>
                  )}
                  {canUseSimulateUnderContract && (
                    <div className={`pm-hub-sim-uc-menu${showSimulateUcMenu ? ' is-open' : ''}`} ref={simulateUcMenuRef}>
                      <button
                        type="button"
                        className={`pm-hub-time-report-btn${simulateUnderContractEnabled ? ' pm-hub-time-report-btn-active' : ''}`}
                        onClick={() => {
                          setShowSimulateUcMenu((v) => !v);
                          setSimulateUcQuery('');
                        }}
                        aria-label="Simulate under contract"
                        aria-haspopup="menu"
                        aria-expanded={showSimulateUcMenu}
                        title="Simulate under contract — pick projects from Estimating or Preconstruction"
                      >
                        <FlaskConical size={13} strokeWidth={2} aria-hidden />
                        {simulateUcSelectedProjects.length > 0 && (
                          <span className="pm-hub-sim-uc-count" aria-hidden>
                            {simulateUcSelectedProjects.length}
                          </span>
                        )}
                      </button>
                      {showSimulateUcMenu && (() => {
                        const q = simulateUcQuery.trim().toLowerCase();
                        const matches = (p: TimberProject) =>
                          !q || simulateUcPicker.labelFor(p).toLowerCase().includes(q);
                        const estimating = simulateUcPicker.estimating.filter(matches);
                        const precon = simulateUcPicker.preconstruction.filter(matches);
                        const renderPick = (p: TimberProject) => {
                          const selected = isSimulatingUnderContract(p, simulateUcProjectIds);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              role="menuitemcheckbox"
                              aria-checked={selected}
                              className={`pm-hub-sim-uc-option${selected ? ' selected' : ''}`}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!p.id) return;
                                void toggleSimulateUnderContractProject(p.id).catch(() => {
                                  /* persistError surfaced below */
                                });
                              }}
                            >
                              <span className="pm-hub-sim-uc-option-check" aria-hidden>
                                {selected ? <Check size={13} strokeWidth={2.5} /> : null}
                              </span>
                              <span className="pm-hub-sim-uc-option-label">
                                {simulateUcPicker.labelFor(p)}
                              </span>
                            </button>
                          );
                        };
                        return (
                        <div
                          className="pm-hub-sim-uc-dropdown"
                          role="menu"
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <p className="pm-hub-sim-uc-hint">
                            Pick estimating or preconstruction projects to overlay as under contract.
                          </p>
                          {(simulateUcPicker.estimating.length + simulateUcPicker.preconstruction.length) > 0 && (
                            <input
                              className="pm-hub-sim-uc-search"
                              type="search"
                              value={simulateUcQuery}
                              onChange={(e) => setSimulateUcQuery(e.target.value)}
                              placeholder="Filter projects"
                              aria-label="Filter projects to simulate"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div className="pm-hub-sim-uc-picker">
                            {estimating.length > 0 && (
                              <p className="pm-hub-sim-uc-section">Estimating</p>
                            )}
                            {estimating.map(renderPick)}
                            {precon.length > 0 && (
                              <p className="pm-hub-sim-uc-section">Preconstruction</p>
                            )}
                            {precon.map(renderPick)}
                            {estimating.length === 0 && precon.length === 0 && (
                              <p className="pm-hub-sim-uc-hint">
                                {q
                                  ? 'No matching estimating or preconstruction projects.'
                                  : 'No estimating or preconstruction projects to simulate.'}
                              </p>
                            )}
                          </div>
                          {simulateUcSelectedProjects.length > 0 && (
                            <button
                              type="button"
                              role="menuitem"
                              className="pm-hub-sim-uc-option"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void clearSimulateUnderContractProjects().catch(() => {
                                  /* persistError surfaced below */
                                });
                              }}
                            >
                              <span className="pm-hub-sim-uc-option-check" aria-hidden>
                                <X size={13} strokeWidth={2.5} />
                              </span>
                              <span className="pm-hub-sim-uc-option-label">
                                Clear all simulations
                              </span>
                            </button>
                          )}
                          {simulateUcPersistError && (
                            <p className="pm-hub-sim-uc-hint" role="alert">{simulateUcPersistError}</p>
                          )}
                          {simulateUnderContractEnabled && simulateUcTemplateError && (
                            <p className="pm-hub-sim-uc-hint">{simulateUcTemplateError}</p>
                          )}
                        </div>
                        );
                      })()}
                    </div>
                  )}
                  {viewMode === 'table' && !supplierHubMode && !architectHubMode && (
                    <button
                      type="button"
                      className="pm-hub-time-report-btn"
                      onClick={() => setShowWorkQueueModal(true)}
                      aria-label="Open work queue"
                      aria-keyshortcuts="P"
                    >
                      <ListOrdered size={13} strokeWidth={2} aria-hidden />
                      <span className="pm-view-btn-shortcut-tip" role="tooltip">
                        Work queue · Shortcut: <kbd>P</kbd>
                      </span>
                    </button>
                  )}
                  {userCanViewAllProjectsTimeReport && !supplierHubMode && !architectHubMode && (
                  <button
                    type="button"
                    className="pm-hub-time-report-btn"
                    onClick={() => setShowTimeReport(true)}
                    title="All projects time report — hours by task, team, and activity log"
                    aria-label="Open all projects time report"
                  >
                    <Clock size={13} strokeWidth={2} aria-hidden />
                  </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Content area */}
          <div className="pm-hub-content pm-scroll">

            {viewMode === 'table' && selectedProjectId && (
              <TaskTable
                tasks={tableTasksForViews}
                childTasksByParent={childTasksByParent}
                groups={mergedGroupsForViews}
                teamMembers={teamMembers}
                projectId={selectedProjectId}
                onUpdateTask={handleUpdateTask}
                onCreateTask={handleCreateTask}
                onDeleteTask={handleDeleteTaskSafe}
                onSelectTask={handleSelectTask}
                onOpenGroupComments={handleOpenGroupComments}
                isGroupUnread={isGroupUnread}
                onUpdateGroup={handleUpdateGroupSafe}
                onDeleteGroup={handleDeleteGroupSafe}
                onCreateGroup={handleCreateGroup}
                readOnlyGroupIds={
                  supplierHubMode || architectHubMode
                    ? mergedGroupsForViews.map((g) => g.id).filter((id): id is string => !!id)
                    : mergedGroupsForViews
                        .filter((g) => isSimulatedUcGroupId(g.id))
                        .map((g) => g.id)
                        .filter((id): id is string => !!id)
                }
                hideAddGroup={supplierHubMode || architectHubMode}
                prominentGroupComments={supplierHubMode || architectHubMode}
                hidePriority={architectHubMode}
                hideAction={architectHubMode}
                datesEditable={!architectHubMode && !supplierHubMode}
                onCommitTaskDates={handleCommitTaskDates}
                canOpenGroupComments={architectHubMode ? (group) => isDesignGroupName(group.name) : undefined}
              />
            )}

            {viewMode === 'table' && !selectedProjectId && (() => {
              const uid = user?.id || '';
              const standupAuthorName =
                crmUsers.find(u => u.uid === uid)?.displayName
                || user?.name
                || user?.email
                || 'Unknown';
              // Build a fast O(1) lookup so each row can resolve its source deal
              // (and the canonical naming triple) without rescanning the array.
              const dealsById = new Map(enhancedDeals.filter(d => d.id).map(d => [d.id!, d] as const));

              const hubSections = sortHubProjectSections(
                partitionHubProjects(hubVisibleProjects),
                dealsById,
              );
              const { estimating: estimatingProjects, preconstruction: preconProjects, contracted: contractedProjects, closed: closedProjects } = hubSections;

              const renderProjectRow = (p: typeof timberProjects[0]) => {
                const deal = p.dealId ? dealsById.get(p.dealId) : undefined;

                // Canonical display: bold #NNNN + descriptor on the title line,
                // 👤 client <Client Label> on the meta line. Falls back through
                // the parsed legacy title and finally `p.name` so projects with
                // no associated deal (e.g. "Admin") still render something.
                const num = (deal?.projectNumber || '').trim();
                const numberDisplay = num
                  ? (num.startsWith('#') ? num : `#${num}`)
                  : '';
                const descriptor = (
                  (deal?.descriptor || '').trim()
                  || parseLegacyTitle(deal?.title || '').descriptor
                  || p.name
                  || ''
                ).trim();
                const clientLabel = (
                  deal?.clientLabel
                  || p.clientName
                  || deal?.contactName
                  || ''
                ).trim();
                // Row badges come from the project's rollup doc (maintained by Cloud
                // Functions from tasks + onboarding checklist + comments + annotations)
                // instead of scanning org-wide task/comment listeners. The rollup keeps
                // the same phase rule: under-contract rows drop preconstruction history.
                const rollup = p.id ? hubRollups.get(p.id) : undefined;
                const stats = computeRollupRowStats(rollup, p.phase, uid);
                // Supplier hub: rollup docs are unreadable (and staff-wide);
                // derive row progress from the scoped Production tasks instead.
                const supplierRowStats = supplierHubMode && p.id
                  ? (() => {
                      const rows = projectTasks.filter(
                        (t) => t.projectId === p.id && !t.parentTaskId,
                      );
                      const total = rows.length;
                      const done = rows.filter((t) => t.status === 'completed').length;
                      const now = Date.now();
                      const overdue = rows.filter(
                        (t) =>
                          t.status !== 'completed'
                          && t.endDate instanceof Date
                          && t.endDate.getTime() < now,
                      ).length;
                      return {
                        total,
                        done,
                        progress: total ? Math.round((done / total) * 100) : 0,
                        overdue,
                      };
                    })()
                  : null;
                const { total, done, progress, overdue } = supplierRowStats ?? stats;
                const otherActionEntries = stats.otherActions.map(entry => ({
                  mapKey: entry.mapKey,
                  external: entry.external,
                  count: entry.count,
                  label: entry.external
                    ? entry.label
                    : crmUsers.find(c => c.uid === entry.uid)?.displayName || 'Someone',
                }));
                const myActionCount = stats.myActionCount;

                const hasUnreadHub = rollupHasUnreadHubActivity(rollup, uid, readReceipts);
                const rowLabel = [numberDisplay, descriptor || p.name].filter(Boolean).join(' ').trim();

                return (
                  <div key={p.id} className={`pm-all-row ${myActionCount > 0 ? 'pm-all-row-action' : ''}`} onClick={() => handleSelectProject(p.id!)}>
                    <div className="pm-all-row-content">
                      <div className="pm-all-row-name">
                        {numberDisplay && (
                          <span className="pm-all-row-number">{numberDisplay}</span>
                        )}
                        <span className="pm-all-row-descriptor">{descriptor || p.name}</span>
                        {isSimulatingUnderContract(p, simulateUcProjectIds) && (
                          <span
                            className="pm-hub-sim-uc-flask"
                            title="Simulated under contract for planning — not a real contract"
                          >
                            <FlaskConical size={12} strokeWidth={2.25} aria-hidden />
                          </span>
                        )}
                      </div>
                      <div className="pm-all-row-bottom">
                        {(clientLabel || p.location) && (
                          <div className="pm-all-row-meta">
                            {clientLabel && (
                              <span className="pm-all-row-client">
                                <User size={10} aria-hidden />
                                <span className="pm-all-row-client-label">client</span>
                                <span className="pm-all-row-client-name">{clientLabel}</span>
                              </span>
                            )}
                            {p.location && (
                              <span className="pm-all-row-loc"><MapPin size={10} /> {p.location}</span>
                            )}
                          </div>
                        )}
                        {!supplierHubMode && !architectHubMode && (
                        <div
                          className={`pm-all-row-standup-group${userCanViewArchitectStandupReports ? '' : ' pm-all-row-standup-group--pm-only'}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="pm-all-row-standup-lane">
                            <span className="pm-standup-lane-label">PM</span>
                            {p.id && (
                              <ProjectStandupCell
                                projectId={p.id}
                                projectLabel={rowLabel}
                                latest={{
                                  text: p.standupNote,
                                  audioUrl: p.standupNoteAudioUrl,
                                  updatedAt: p.standupNoteUpdatedAt,
                                  updatedByName: p.standupNoteUpdatedByName,
                                }}
                                currentUserId={uid}
                                currentUserName={standupAuthorName}
                              />
                            )}
                          </div>
                          {userCanViewArchitectStandupReports && p.id && (
                            <div className="pm-all-row-standup-lane pm-all-row-standup-lane--designer">
                              <span className="pm-standup-lane-label">Designer</span>
                              <ProjectStandupCell
                                projectId={p.id}
                                projectLabel={rowLabel}
                                latest={architectStandupLatestByProject.get(p.id) ?? {}}
                                currentUserId={uid}
                                currentUserName={standupAuthorName}
                                variant="architect"
                                readOnly={!userCanSubmitArchitectStandup}
                                placeholder="No report"
                                historyTitle="Designer report history"
                              />
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                    </div>
                    {!architectHubMode && (
                    <div className="pm-all-row-stats">
                      {!supplierHubMode && myActionCount > 0 && (
                        <span className="pm-all-row-badge pm-all-badge-mine">
                          <Flag size={9} /> Your action · {myActionCount}
                        </span>
                      )}
                      {!supplierHubMode && otherActionEntries.map(({ mapKey, label, count, external }) => (
                        <span
                          key={mapKey}
                          className={`pm-all-row-badge ${external ? 'pm-all-badge-external' : 'pm-all-badge-other'}`}
                        >
                          {external ? <Building2 size={9} aria-hidden /> : <User size={9} aria-hidden />}
                          {external
                            ? (label === 'External' ? `External · ${count}` : `External · ${label} · ${count}`)
                            : `${label} · ${count}`}
                        </span>
                      ))}
                      {overdue > 0 && (
                        <span className="pm-all-row-badge pm-all-badge-overdue">
                          <AlertTriangle size={9} /> {overdue} overdue
                        </span>
                      )}
                    </div>
                    )}
                    {!architectHubMode && (
                    <div className="pm-all-row-progress">
                      <div className="pm-all-row-bar">
                        <div className="pm-all-row-fill" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="pm-all-row-pct">{done}/{total}</span>
                    </div>
                    )}
                    <div className="pm-all-row-comments-cell" onClick={(e) => e.stopPropagation()}>
                      {!supplierHubMode && !architectHubMode && hasUnreadHub && (
                        <button
                          type="button"
                          className="pm-task-comments-btn pm-task-comments-btn--unread"
                          title="Unread comments — open list"
                          aria-label="Open list of unread comments"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (p.id) setUnreadHubPopover({ projectId: p.id, projectLabel: rowLabel });
                          }}
                        >
                          <MessageSquare size={13} strokeWidth={1.85} aria-hidden />
                          <span className="pm-task-comments-dot" aria-hidden />
                        </button>
                      )}
                    </div>
                    <ChevronRight size={14} className="pm-all-row-arrow" />
                  </div>
                );
              };

              return (
                <div className={`pm-all-list${userCanViewArchitectStandupReports && !supplierHubMode ? ' pm-all-list--designer-reports' : ''}`}>
                  {estimatingProjects.length > 0 && (
                    <div className="pm-all-section">
                      <div className="pm-all-section-header pm-all-section-estimating">
                        <Calculator size={14} />
                        <span>Estimating</span>
                        <span className="pm-all-section-count">{estimatingProjects.length}</span>
                      </div>
                      {estimatingProjects.map(renderProjectRow)}
                    </div>
                  )}

                  {preconProjects.length > 0 && (
                    <div className="pm-all-section">
                      <div className="pm-all-section-header pm-all-section-precon">
                        <HardHat size={14} />
                        <span>Preconstruction</span>
                        <span className="pm-all-section-count">{preconProjects.length}</span>
                      </div>
                      {preconProjects.map(renderProjectRow)}
                    </div>
                  )}

                  {contractedProjects.length > 0 && (
                    <div className="pm-all-section">
                      <div className="pm-all-section-header pm-all-section-contract">
                        <FileCheck size={14} />
                        <span>Under Contract</span>
                        <span className="pm-all-section-count">{contractedProjects.length}</span>
                      </div>
                      {contractedProjects.map(renderProjectRow)}
                    </div>
                  )}

                  {closedProjects.length > 0 && (
                    <div className="pm-all-section">
                      <button
                        className="pm-all-section-header pm-all-section-closed pm-all-section-toggle"
                        onClick={() => setShowClosedProjects(v => !v)}
                      >
                        {showClosedProjects ? <EyeOff size={14} /> : <Eye size={14} />}
                        <span>{showClosedProjects ? 'Hide' : 'Show'} Closed</span>
                        <span className="pm-all-section-count">{closedProjects.length}</span>
                      </button>
                      {showClosedProjects && closedProjects.map(renderProjectRow)}
                    </div>
                  )}
                </div>
              );
            })()}

            {viewMode === 'gantt' && (
              <div
                className={`pm-gantt-split${ganttSplitOpen && !selectedProjectId ? ' is-split' : ''}`}
                ref={ganttSplitStageRef}
              >
              <div
                className="pm-gantt-split-pane"
                style={ganttSplitOpen && !selectedProjectId ? { flex: `${ganttSplitRatio} 1 0` } : undefined}
              >
              {ganttSplitOpen && !selectedProjectId ? (
                <AllProjectsPaneToolbar
                  family={groupTimelineFamily}
                  onFamily={(family) => {
                    selectAllProjectsTimelineFamily(family);
                    if (ganttSplitSync) setGanttSplitFamily(family);
                  }}
                  showHotkeys
                  hotkeyLabel={allProjectsFamilyHotkeyLabel}
                  layout={groupTimelineLayout}
                  onLayout={(next) => {
                    setGroupTimelineLayout(next);
                    storeGroupTimelineLayout(next);
                    if (ganttSplitSync) setGanttSplitLayout(next);
                  }}
                  sortMode={groupTimelineSort}
                  onSort={(next) => {
                    setGroupTimelineSort(next);
                    storeGroupTimelineSort(next);
                    if (ganttSplitSync) setGanttSplitSort(next);
                  }}
                  onCollapseAll={() => {
                    matchingOutlineRef.current = true;
                    ganttOutlineApiRef.current?.collapseAll();
                    if (ganttSplitSync) ganttSplitApiRef.current?.collapseAll();
                    window.setTimeout(() => { matchingOutlineRef.current = false; }, 200);
                  }}
                  onExpandAll={() => {
                    matchingOutlineRef.current = true;
                    ganttOutlineApiRef.current?.expandAll();
                    if (ganttSplitSync) ganttSplitApiRef.current?.expandAll();
                    window.setTimeout(() => { matchingOutlineRef.current = false; }, 200);
                  }}
                  pinnedCount={ganttPinnedCount}
                  pinLimit={GANTT_COMPARE_PIN_LIMIT}
                  onClearPins={() => {
                    matchingOutlineRef.current = true;
                    ganttOutlineApiRef.current?.clearPins();
                    if (ganttSplitSync) ganttSplitApiRef.current?.clearPins();
                    window.setTimeout(() => { matchingOutlineRef.current = false; }, 200);
                  }}
                  colorMode={groupTimelineColorMode}
                  onColorMode={(next) => {
                    setGroupTimelineColorMode(next);
                    storeGroupTimelineColorMode(next);
                    if (ganttSplitSync) setGanttSplitColorMode(next);
                  }}
                  legend={groupTimelineLegend}
                  filter={groupTimelineFilter}
                  onFilter={(next) => {
                    setGroupTimelineFilter(next);
                    storeGroupTimelineFilter(next);
                    if (ganttSplitSync) setGanttSplitFilter(next);
                  }}
                  filterOptions={groupTimelineFilterOptions}
                  filterClearShortcut="C"
                  focusTasks={groupTimelineFocusOptions.extraTasks}
                  onRemoveFocus={(taskId) => {
                    removeGroupTimelineExtraTask(taskId);
                    if (ganttSplitSync) removeSplitExtraTask(taskId);
                  }}
                  conflictBoard={groupTimelineConflictBoard}
                  onShowConflicts={showConflictTasks}
                  savedViews={savedTimelineViews}
                  activeSavedViewId={activeSavedTimelineViewId}
                  currentUserId={user?.id}
                  onApplySavedView={applySavedTimelineView}
                  onCaptureSavedViewOutline={captureAllProjectsSavedViewOutline}
                  onSavedViewCreated={setActiveSavedTimelineViewId}
                />
              ) : null}
              {showTimelineDrift && viewMode === 'gantt' && (
                <div className="pm-timeline-drift-bar" role="status">
                  {timelineDriftFilterCauses.length === 0 ? (
                    <span className="pm-timeline-drift-bar-empty">No tagged moves yet</span>
                  ) : (
                    timelineDriftFilterCauses.map((cause) => {
                      const on = !timelineDriftCauseFilter || timelineDriftCauseFilter.has(cause);
                      return (
                        <button
                          key={cause}
                          type="button"
                          className={`pm-timeline-drift-chip${on ? ' is-on' : ''}`}
                          onClick={() => {
                            setTimelineDriftCauseFilter((prev) => {
                              const next = new Set(prev ?? timelineDriftFilterCauses);
                              if (next.has(cause)) next.delete(cause);
                              else next.add(cause);
                              if (next.size === 0 || next.size === timelineDriftFilterCauses.length) return null;
                              return next;
                            });
                          }}
                        >
                          {TIMELINE_DRIFT_CAUSE_LABELS[cause]}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              <GanttTimeline
                ganttRangeResetKey={
                  selectedProjectId ?? `group:${groupTimelineFamily ?? 'all'}`
                }
                tasks={
                  selectedProjectId
                    ? tasksForViews.filter(
                        (t) => !supplierVisibleTaskIds || !t.id || supplierVisibleTaskIds.has(t.id),
                      )
                    : groupTimelineTasks
                }
                groups={selectedProjectId ? mergedGroupsForViews : groupTimelineGroups}
                milestones={[]}
                proposalSignedAt={null}
                projectPhase={currentProject?.phase ?? null}
                displayMode={supplierHubMode}
                statusReadOnly={false}
                teamMembers={teamMembers}
                onUpdateTask={handleUpdateTask}
                onCommitTaskDates={handleCommitTaskDates}
                onCreateFsDependency={handleCreateFsDependency}
                onUpdateDependencyLag={handleUpdateDependencyLag}
                onRemoveDependency={handleRemoveDependency}
                onSelectTask={handleSelectTask}
                onToggleTaskLock={handleToggleTaskLock}
                showCriticalPathOverlay={selectedProjectId ? showCriticalPathOverlay : false}
                timelineDriftGhosts={showTimelineDrift ? timelineDriftGhosts : []}
                onTimelineUndo={handleTimelineUndo}
                timelineUndoCanUndo={timelineUndoStack.length > 0}
                timelineUndoBusy={timelineUndoBusy}
                getInstallTaskRangeRainyDays={selectedProjectId ? getInstallTaskRangeRainyDays : undefined}
                formatInstallDayWeatherLine={selectedProjectId ? installWeather.formatDayLine : undefined}
                paymentFlags={selectedProjectId && !hidePaymentFlags ? ganttPaymentFlags : undefined}
                projectMeetings={selectedProjectId ? timelineProjectMeetings : undefined}
                dealStructures={selectedProjectId ? dealStructures : undefined}
                structureFilter={selectedProjectId ? structureFilter : null}
                onStructureFilterChange={selectedProjectId ? setStructureFilter : undefined}
                resolveTaskBarColor={selectedProjectId ? undefined : resolveGroupTimelineTaskBarColor}
                simulatedProjectIds={simulateUcProjectIds}
                timelineSections={
                  !selectedProjectId && groupTimelineBuilt.sections.length
                    ? groupTimelineBuilt.sections
                    : undefined
                }
                allProjectsMode={!selectedProjectId}
                outlineApiRef={ganttOutlineApiRef}
                onPinnedCountChange={setGanttPinnedCount}
                conflictBoard={!selectedProjectId && !supplierHubMode ? groupTimelineConflictBoard : undefined}
                conflictFamily={!selectedProjectId ? groupTimelineFamily : undefined}
                onConflictBadgeClick={!selectedProjectId ? showConflictTasks : undefined}
                onGanttOutlineChange={(slice) => {
                  ganttTimelineCollab.onGanttOutlineChange(slice);
                  if (!ganttSplitOpen || !ganttSplitSync || matchingOutlineRef.current) return;
                  matchingOutlineRef.current = true;
                  ganttSplitApiRef.current?.applyOutlineState(slice);
                  window.setTimeout(() => { matchingOutlineRef.current = false; }, 200);
                }}
                onViewportChange={(viewport) => {
                  ganttTimelineCollab.onViewportChange(viewport);
                  pushSplitViewport('left', viewport);
                }}
                ganttTimelinePresence={ganttTimelineCollab.ganttTimelinePresence}
              />
              </div>
              {ganttSplitOpen && !selectedProjectId && ganttSplitBuilt ? (
                <>
                  <div
                    className="pm-gantt-split-divider"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize timelines"
                    onPointerDown={(event) => {
                      const stage = ganttSplitStageRef.current;
                      if (!stage) return;
                      event.preventDefault();
                      const move = (ev: PointerEvent) => {
                        const rect = stage.getBoundingClientRect();
                        if (rect.width <= 0) return;
                        const next = (ev.clientX - rect.left) / rect.width;
                        setGanttSplitRatio(Math.min(0.75, Math.max(0.25, next)));
                      };
                      const up = () => {
                        window.removeEventListener('pointermove', move);
                        window.removeEventListener('pointerup', up);
                      };
                      window.addEventListener('pointermove', move);
                      window.addEventListener('pointerup', up);
                    }}
                  />
                  <div className="pm-gantt-split-pane" style={{ flex: `${1 - ganttSplitRatio} 1 0` }}>
                    <AllProjectsPaneToolbar
                      family={ganttSplitFamily}
                      onFamily={(family) => {
                        setGanttSplitFamily(family);
                        if (ganttSplitSync) selectAllProjectsTimelineFamily(family);
                      }}
                      layout={ganttSplitLayout}
                      onLayout={(next) => {
                        setGanttSplitLayout(next);
                        if (ganttSplitSync) {
                          setGroupTimelineLayout(next);
                          storeGroupTimelineLayout(next);
                        }
                      }}
                      sortMode={ganttSplitSort}
                      onSort={(next) => {
                        setGanttSplitSort(next);
                        if (ganttSplitSync) {
                          setGroupTimelineSort(next);
                          storeGroupTimelineSort(next);
                        }
                      }}
                      onCollapseAll={() => {
                        matchingOutlineRef.current = true;
                        ganttSplitApiRef.current?.collapseAll();
                        if (ganttSplitSync) ganttOutlineApiRef.current?.collapseAll();
                        window.setTimeout(() => { matchingOutlineRef.current = false; }, 200);
                      }}
                      onExpandAll={() => {
                        matchingOutlineRef.current = true;
                        ganttSplitApiRef.current?.expandAll();
                        if (ganttSplitSync) ganttOutlineApiRef.current?.expandAll();
                        window.setTimeout(() => { matchingOutlineRef.current = false; }, 200);
                      }}
                      pinnedCount={ganttSplitPinnedCount}
                      pinLimit={GANTT_COMPARE_PIN_LIMIT}
                      onClearPins={() => {
                        matchingOutlineRef.current = true;
                        ganttSplitApiRef.current?.clearPins();
                        if (ganttSplitSync) ganttOutlineApiRef.current?.clearPins();
                        window.setTimeout(() => { matchingOutlineRef.current = false; }, 200);
                      }}
                      colorMode={ganttSplitColorMode}
                      onColorMode={(next) => {
                        setGanttSplitColorMode(next);
                        if (ganttSplitSync) {
                          setGroupTimelineColorMode(next);
                          storeGroupTimelineColorMode(next);
                        }
                      }}
                      legend={ganttSplitLegend}
                      filter={ganttSplitFilter}
                      onFilter={(next) => {
                        setGanttSplitFilter(next);
                        if (ganttSplitSync) {
                          setGroupTimelineFilter(next);
                          storeGroupTimelineFilter(next);
                        }
                      }}
                      filterOptions={groupTimelineFilterOptions}
                      filterClearShortcut="C"
                      focusTasks={ganttSplitFocusTasks}
                      onRemoveFocus={(taskId) => {
                        removeSplitExtraTask(taskId);
                        if (ganttSplitSync) removeGroupTimelineExtraTask(taskId);
                      }}
                      conflictBoard={groupTimelineConflictBoard}
                      onShowConflicts={showSplitConflictTasks}
                    />
                    <GanttTimeline
                      ganttRangeResetKey={`split:${ganttSplitFamily}`}
                      initialZoom={ganttSplitSeedZoom}
                      tasks={ganttSplitTasks}
                      groups={ganttSplitGroups}
                      milestones={[]}
                      proposalSignedAt={null}
                      projectPhase={null}
                      displayMode={false}
                      statusReadOnly={false}
                      teamMembers={teamMembers}
                      onUpdateTask={handleUpdateTask}
                      onCommitTaskDates={handleCommitTaskDates}
                      onCreateFsDependency={handleCreateFsDependency}
                      onUpdateDependencyLag={handleUpdateDependencyLag}
                      onRemoveDependency={handleRemoveDependency}
                      onSelectTask={handleSelectTask}
                      onToggleTaskLock={handleToggleTaskLock}
                      showCriticalPathOverlay={false}
                      timelineDriftGhosts={showTimelineDrift ? timelineDriftGhosts : []}
                      onTimelineUndo={handleTimelineUndo}
                      timelineUndoCanUndo={timelineUndoStack.length > 0}
                      timelineUndoBusy={timelineUndoBusy}
                      resolveTaskBarColor={resolveSplitTaskBarColor}
                      simulatedProjectIds={simulateUcProjectIds}
                      timelineSections={ganttSplitBuilt.sections.length ? ganttSplitBuilt.sections : undefined}
                      allProjectsMode
                      outlineApiRef={ganttSplitApiRef}
                      onPinnedCountChange={setGanttSplitPinnedCount}
                      conflictBoard={!supplierHubMode ? groupTimelineConflictBoard : undefined}
                      conflictFamily={ganttSplitFamily}
                      onConflictBadgeClick={showSplitConflictTasks}
                      onGanttOutlineChange={(slice) => {
                        if (!ganttSplitSync || matchingOutlineRef.current) return;
                        matchingOutlineRef.current = true;
                        ganttOutlineApiRef.current?.applyOutlineState(slice);
                        window.setTimeout(() => { matchingOutlineRef.current = false; }, 200);
                      }}
                      onViewportChange={(viewport) => pushSplitViewport('right', viewport)}
                    />
                  </div>
                </>
              ) : null}
              </div>
            )}

            {viewMode === 'crew' && selectedProjectId && !architectHubMode && !supplierHubMode && (
              <div className="pm-hub-files-view">
                <CrewSlackHubPanel
                  projectId={selectedProjectId}
                  projectName={currentProject?.name}
                />
              </div>
            )}

            {(viewMode === 'files' || filesWarm) && selectedProjectId && (
              <div className={viewMode === 'files' ? 'pm-hub-files-view' : undefined} hidden={viewMode !== 'files'}>
                <ProjectFiles
                  key={selectedProjectId}
                  projectId={selectedProjectId}
                  leadId={currentDeal?.leadId}
                  leadName={currentProject?.clientName}
                  dealId={currentProject?.dealId}
                  deal={currentDeal ?? null}
                  vendorRfqProjectId={cncRfqForProject?.id}
                  vendorReleaseOrgId={cncRfqForProject?.supplierOrgId}
                  cncRfqProjectId={cncRfqForProject?.id}
                  timberProjectName={currentProject?.name}
                  filesDeepLink={filesDeepLink}
                  filesInitialTrackId={filesInitialTrackId}
                  filesInitialView={filesInitialView}
                  filesInitialFolderPath={filesInitialFolderPath}
                  onViewDeepLinkConsumed={clearFilesViewQuery}
                  supplierReadOnly={supplierHubMode}
                  architectReadOnly={architectHubMode}
                  onFilesDeepLinkConsumed={clearFilesDeepLinkParams}
                  onTrackDeepLinkConsumed={clearFilesTrackQuery}
                  filesInitialRequestReleaseIds={filesInitialRequestReleaseIds}
                  onRequestDeepLinkConsumed={clearFilesRequestQuery}
                  onDeepLinkError={(title, body) => pushPmToast('error', title, body)}
                  onOpenDesignerStandardsSettings={() => navigate('/settings/project-defaults')}
                  onOpenSuppliers={
                    canAccessSuppliersTab
                      ? () => {
                          const q = new URLSearchParams(searchParams);
                          q.set('tab', 'suppliers');
                          navigate(`/project-hub/${selectedProjectId}?${q.toString()}`);
                        }
                      : undefined
                  }
                  onFilesDeepLinkForeignProject={(foreignProjectId) => {
                    const q = new URLSearchParams(searchParams);
                    q.set('tab', 'files');
                    navigate(`/project-hub/${foreignProjectId}?${q.toString()}`);
                  }}
                />
              </div>
            )}
            {viewMode === 'files' && !selectedProjectId && (
              <div className="pm-hub-placeholder">
                <Paperclip size={40} />
                <p>Select a project to view files</p>
              </div>
            )}

            {canAccessSuppliersTab && (viewMode === 'suppliers' || suppliersWarm) && selectedProjectId && currentDeal?.id && (
              <div className={viewMode === 'suppliers' ? 'pm-hub-suppliers pm-scroll' : undefined} hidden={viewMode !== 'suppliers'}>
                <DealSupplierRfqPanels
                  key={currentDeal.id}
                  dealId={currentDeal.id}
                  timberProjectId={currentProject?.id || selectedProjectId}
                  hubProjectManagerUid={currentProject?.projectManager}
                  onOpenFilesRequest={(releaseIds) => {
                    const q = new URLSearchParams(searchParams);
                    q.set('tab', 'files');
                    q.set('view', 'rfqs');
                    q.set('request', releaseIds.join(','));
                    navigate(`/project-hub/${selectedProjectId}?${q.toString()}`);
                  }}
                />
              </div>
            )}
            {canAccessSuppliersTab && viewMode === 'suppliers' && selectedProjectId && !currentDeal?.id && (
              <div className="pm-hub-placeholder">
                <Building2 size={40} />
                <p>This project has no linked deal — supplier RFQs require a deal.</p>
              </div>
            )}
            {canAccessSuppliersTab && viewMode === 'suppliers' && !selectedProjectId && (
              <div className="pm-hub-placeholder">
                <Building2 size={40} />
                <p>Select a project to view suppliers</p>
              </div>
            )}

            {canAccessChangeOrdersTab && viewMode === 'changeOrders' && selectedProjectId && currentDeal?.id && (
              <div className="pm-hub-suppliers pm-scroll">
                <DealChangeOrderPanel
                  dealId={currentDeal.id}
                  projectId={currentProject?.id}
                  clientName={currentDeal.contactName || currentDeal.company || currentDeal.title}
                  projectName={getProjectDisplayName(currentDeal)}
                  projectNumber={currentDeal.projectNumber}
                />
              </div>
            )}
            {canAccessChangeOrdersTab && viewMode === 'changeOrders' && selectedProjectId && !currentDeal?.id && (
              <div className="pm-hub-placeholder">
                <ClipboardList size={40} />
                <p>This project has no linked deal — change orders require a deal.</p>
              </div>
            )}
            {canAccessChangeOrdersTab && viewMode === 'changeOrders' && !selectedProjectId && (
              <div className="pm-hub-placeholder">
                <ClipboardList size={40} />
                <p>Select a project to view change orders</p>
              </div>
            )}
          </div>
        </div>

        {/* Task detail slide-over */}
        {selectedTask && detailTaskForPanel && supplierHubMode && (
          <SupplierTaskPanel
            task={detailTaskForPanel}
            groupName={
              detailTaskForPanel.groupId
                ? taskGroups.find((g) => g.id === detailTaskForPanel.groupId)?.name
                : undefined
            }
            projectLabel={(() => {
              const label = groupTimelineProjectLabels.get(detailTaskForPanel.projectId);
              const num = (label?.projectNumber || '').trim();
              const numberDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
              return [numberDisplay, label?.descriptor || ''].filter(Boolean).join(' ').trim();
            })()}
            readOnly={isOnboardingBridgeTaskId(selectedTask.id)}
            onUpdate={handleUpdateTask}
            onOpenProductionComments={() =>
              openSupplierProductionComments(detailTaskForPanel.projectId)
            }
            onClose={() => {
              setSelectedTask(null);
              setPanelParentTask(null);
            }}
          />
        )}
        {selectedTask && detailTaskForPanel && !supplierHubMode && (
          <TaskDetailPanel
            task={detailTaskForPanel}
            isOnboardingChecklistTask={isOnboardingBridgeTaskId(selectedTask.id)}
            simulatedUnderContract={isSimulatingUnderContract(
              timberProjects.find((p) => p.id === detailTaskForPanel.projectId) ?? currentProject,
              simulateUcProjectIds,
            )}
            showTaskActivityHistory={canViewTaskActivityHistory(user?.role, userDesignations)}
            showActionRequiredWaitAnalytics={canViewActionRequiredWaitAnalytics(user?.role)}
            teamMembers={taskPanelTeamMembers}
            currentUserId={user?.id || ''}
            currentUserName={user?.name || ''}
            sameProjectTasks={(selectedProjectId ? tasksForViews : groupTimelineSourceTasks)
              .filter((t) => t.projectId === selectedTask.projectId)
              .filter((t) => !supplierVisibleTaskIds || !t.id || supplierVisibleTaskIds.has(t.id))}
            onCommitTaskDates={handleCommitTaskDates}
            onCreateFsDependency={handleCreateFsDependency}
            onUpdateDependencyLag={handleUpdateDependencyLag}
            onRemoveDependency={handleRemoveDependency}
            onUpdate={architectHubMode ? async () => undefined : handleUpdateTask}
            onDelete={architectHubMode ? async () => undefined : handleDeleteTaskSafe}
            onLogTime={canLogTaskTime ? handleLogTime : undefined}
            onUpdateTimeEntry={canViewTimeResults ? updateProjectTimeEntry : undefined}
            timeEntries={canLogTaskTime || canViewTimeResults ? projectTimeEntries : []}
            project={currentProject}
            projectContext={taskDetailPanelProjectContext}
            phaseCutoverDate={currentProject?.contractedAt ?? proposalSignedAt ?? null}
            taskGroupName={
              detailTaskForPanel?.groupId
                ? taskGroups.find((g) => g.id === detailTaskForPanel.groupId)?.name
                : undefined
            }
            childTasks={panelChildTasks}
            dealId={taskPanelDealId}
            supplierOptions={taskPanelSupplierOptions}
            showSupplierField={taskPanelShowSupplier}
            derivedSupplierLabel={taskPanelDerivedSupplierLabel}
            derivedSupplierLabelForTask={derivedSupplierLabelForTask}
            onCreateSubtask={architectHubMode ? undefined : handleCreateSubtask}
            onSelectSubtask={handleSelectSubtask}
            onBackToParent={detailTaskForPanel?.parentTaskId ? handleBackToParentTask : undefined}
            parentTaskTitle={panelParentTask?.title ?? null}
            taskReminders={panelTaskReminders}
            onCreateTaskReminder={handleCreateTaskReminder}
            onCancelTaskReminder={handleCancelTaskReminder}
            onClose={() => {
              setSelectedTask(null);
              setPanelParentTask(null);
            }}
          />
        )}

        <GroupCommentsPopout
          groupContext={groupCommentsContext}
          popoutToken={groupCommentsPopoutToken}
          dealId={groupCommentsDealId}
          structureMentionDealId={groupCommentsDealId ?? currentProject?.dealId ?? undefined}
          structureMentions={
            groupCommentsDealId
              ? proposalStructuresByDealId.get(groupCommentsDealId) ?? dealStructures
              : dealStructures
          }
          structuresByDealId={proposalStructuresByDealId}
          projectPdfAnnotations={groupCommentsProjectAnnotations}
          projectMeetingNotes={supplierHubMode || architectHubMode ? [] : projectMeetingNotes}
          leadComms={supplierHubMode || architectHubMode ? [] : leadCommsForProject}
          leadIdForComms={supplierHubMode || architectHubMode ? null : currentDeal?.leadId || null}
          isProjectManager={isCurrentUserProjectManager && !supplierHubMode && !architectHubMode}
          readOnly={!!architectPreviewUid}
          architectSurface={architectHubMode}
          initialTab={pendingGroupCommentsTab}
          highlightCommentId={pendingHighlightGroupCommentId}
          onHighlightCommentConsumed={() => setPendingHighlightGroupCommentId(null)}
          initialPlanReviewId={pendingOpenPlanReviewId}
          onInitialPlanReviewConsumed={() => setPendingOpenPlanReviewId(null)}
          onClose={handleCloseGroupComments}
          groupMemberTasks={groupCommentMemberTasks}
          teamMembers={teamMembers}
          currentUserId={user?.id}
          onUpdateTask={architectHubMode ? undefined : handleUpdateTask}
          onCompleteTask={architectHubMode ? undefined : handleRequestGroupTaskComplete}
          onOpenPdf={(u, t, docId, opts) => {
            void (async () => {
              const resolved = await resolveAnnotationViewerOpenTarget({
                documentId: docId,
                documentUrl: u,
                title: t,
              });
              if (!resolved) {
                pushPmToast(
                  'error',
                  'Could not open this file comment',
                  'The file may have been removed or you may not have access. Try opening it from the Files tab.',
                );
                return;
              }
              setTaskPdfViewer({
                url: resolved.url,
                title: resolved.title || t,
                documentId: resolved.documentId || docId,
                annotationId: opts?.annotationId,
                pageNumber: opts?.pageNumber,
              });
            })();
          }}
          onOpenImage={(u, t, docId, opts) => {
            void (async () => {
              const resolved = await resolveAnnotationViewerOpenTarget({
                documentId: docId,
                documentUrl: u,
                title: t,
              });
              if (!resolved) {
                pushPmToast(
                  'error',
                  'Could not open this file comment',
                  'The file may have been removed or you may not have access. Try opening it from the Files tab.',
                );
                return;
              }
              setTaskImageViewer({
                url: resolved.url,
                title: resolved.title || t,
                documentId: resolved.documentId || docId,
                mimeType: opts?.mimeType || resolved.mimeType,
                annotationId: opts?.annotationId,
              });
            })();
          }}
          onOpenCsv={(u, t, docId, opts) => {
            void (async () => {
              const resolved = await resolveAnnotationViewerOpenTarget({
                documentId: docId,
                documentUrl: u,
                title: t,
              });
              if (!resolved) {
                pushPmToast(
                  'error',
                  'Could not open this file comment',
                  'The file may have been removed or you may not have access. Try opening it from the Files tab.',
                );
                return;
              }
              setTaskCsvViewer({
                url: resolved.url,
                title: resolved.title || t,
                documentId: resolved.documentId || docId,
                annotationId: opts?.annotationId,
              });
            })();
          }}
        />
      </div>

      {taskPdfViewer && currentDeal?.leadId && selectedProjectId && (
        <Suspense fallback={null}>
          <PdfViewerModal
            key={`${taskPdfViewer.documentId}:${taskPdfViewer.annotationId ?? ''}:${taskPdfViewer.pageNumber ?? ''}`}
            url={taskPdfViewer.url}
            title={taskPdfViewer.title}
            documentId={taskPdfViewer.documentId}
            leadId={currentDeal.leadId}
            leadName={currentProject?.clientName}
            projectId={selectedProjectId}
            dealId={currentProject?.dealId}
            initialAnnotationId={taskPdfViewer.annotationId}
            initialPageNumber={taskPdfViewer.pageNumber}
            onClose={() => setTaskPdfViewer(null)}
          />
        </Suspense>
      )}
      {taskImageViewer && currentDeal?.leadId && selectedProjectId && (
        <Suspense fallback={null}>
          <ImageViewerModal
            url={taskImageViewer.url}
            title={taskImageViewer.title}
            documentId={taskImageViewer.documentId}
            leadId={currentDeal.leadId}
            leadName={currentProject?.clientName}
            projectId={selectedProjectId}
            dealId={currentProject?.dealId}
            mimeType={taskImageViewer.mimeType}
            initialAnnotationId={taskImageViewer.annotationId}
            onClose={() => setTaskImageViewer(null)}
          />
        </Suspense>
      )}
      {taskCsvViewer && selectedProjectId && (
        <Suspense fallback={null}>
          <CsvViewerModal
            url={taskCsvViewer.url}
            title={taskCsvViewer.title}
            documentId={taskCsvViewer.documentId}
            leadId={resolveFileViewerLeadId(currentDeal?.leadId, undefined, selectedProjectId)}
            leadName={currentProject?.clientName}
            projectId={selectedProjectId}
            dealId={currentProject?.dealId}
            initialAnnotationId={taskCsvViewer.annotationId}
            onClose={() => setTaskCsvViewer(null)}
          />
        </Suspense>
      )}

      {dependencyDatePrompt && (() => {
        // Baseline (no optimistic overlay) so modal previews compute real deltas / old→new.
        const promptTask = schedulingBaselineNodes.find((n) => n.id === dependencyDatePrompt.taskId);
        if (!promptTask) return null;
        return (
          <DependencyDateCommitModal
            task={promptTask}
            newStart={dependencyDatePrompt.start}
            newEnd={dependencyDatePrompt.end}
            schedulingNodes={schedulingBaselineNodes}
            onApply={(mode, remember) => resolveDependencyDatePrompt(mode, remember)}
            onCancel={cancelDependencyDatePrompt}
          />
        );
      })()}

      {pendingCompletionTask && (
        <CompleteTaskActualsModal
          taskTitle={pendingCompletionTask.title}
          scheduledStart={pendingCompletionTask.scheduledStart}
          scheduledEnd={pendingCompletionTask.scheduledEnd}
          onCancel={() => setPendingCompletionTask(null)}
          onConfirm={({ actualStart, actualEnd }) => {
            void (async () => {
              const ctx = pendingCompletionTask;
              setPendingCompletionTask(null);
              await applyTaskCompletion(ctx, actualStart, actualEnd);
            })();
          }}
        />
      )}

      {showTimeReport &&
        (selectedProjectId !== null
          ? !!currentProject && canViewTimeResults
          : userCanViewAllProjectsTimeReport) && (
        <ProjectReport
          project={currentProject ?? null}
          projects={hubVisibleProjects}
          timeEntries={timeReportEntries}
          crmUsers={crmUsers}
          onClose={() => setShowTimeReport(false)}
        />
      )}

      {showWorkQueueModal && (
        <ArchEngineerWorkQueueModal
          open={showWorkQueueModal}
          onClose={() => setShowWorkQueueModal(false)}
          queuesByUserId={aeWorkQueuesByUser}
          crmUsers={crmUsers}
          timberProjects={timberProjects}
          enhancedDeals={enhancedDeals}
          projectTasks={projectTasks}
          taskGroups={taskGroups}
          projectOnboardings={projectOnboardings}
          currentUserId={user?.id || ''}
          viewerDisplayName={user?.name || ''}
          canEditQueue={isCurrentUserProjectManager}
          onSaveQueue={persistArchEngineerWorkQueue}
          onNavigateProject={(projectId) => handleSelectProject(projectId)}
        />
      )}

      {moveReasonPrompt && (
        <RecordMoveReasonPopup
          taskTitle={moveReasonPrompt.taskTitle}
          onSkip={() => setMoveReasonPrompt(null)}
          onRecord={(cause) => {
            const id = moveReasonPrompt.activityId;
            setMoveReasonPrompt(null);
            setShowTimelineDrift(true);
            void updateDoc(doc(db, 'taskActivity', id), { delayCause: cause }).catch((err) => {
              console.error(err);
            });
          }}
        />
      )}

      <PmToastStack toasts={pmToasts} onDismiss={dismissPmToast} />

      {/* Onboarding Checklist Modal */}
      {showOnboardingModal && currentOnboarding && selectedProjectId && !architectHubMode && (
        <OnboardingModal
          onboarding={currentOnboarding}
          crmUsers={crmUsers}
          onToggleItem={handleOnboardingToggle}
          onChangeAssignee={handleOnboardingAssigneeChange}
          onChangeDepartment={handleOnboardingDepartmentChange}
          onReorder={handleOnboardingReorder}
          onSyncTemplate={handleSyncTemplate}
          onClose={() => setShowOnboardingModal(false)}
        />
      )}

      {/* Create project modal — pick precon or contracted */}
      {showCreateModal && (
        <div className="pm-delete-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="pm-delete-modal" style={{ maxWidth: 500, textAlign: 'left' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ textAlign: 'center', marginBottom: 16 }}>New Project</h3>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                className={`pm-btn ${createMode === 'precon' ? 'pm-btn-primary' : 'pm-btn-ghost'}`}
                style={{ flex: 1 }}
                onClick={() => setCreateMode('precon')}
              >
                <HardHat size={14} style={{ marginRight: 4 }} /> Estimating
              </button>
              <button
                className={`pm-btn ${createMode === 'contracted' ? 'pm-btn-primary' : 'pm-btn-ghost'}`}
                style={{ flex: 1 }}
                onClick={() => setCreateMode('contracted')}
              >
                <FileCheck size={14} style={{ marginRight: 4 }} /> Contracted
              </button>
            </div>

            {createMode === 'precon' && (
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {activeDealsForPrecon.length === 0 ? (
                  <p style={{ textAlign: 'center', color: 'var(--pm-text-muted)', padding: 20, fontSize: 13 }}>No active deals available for preconstruction.</p>
                ) : (
                  activeDealsForPrecon.map(deal => (
                    <button
                      key={deal.id}
                      className="pm-precon-deal-btn"
                      onClick={async () => {
                        if (!deal.id || !user?.id) return;
                        try {
                          const projId = await createPreconProject(deal, user.id);
                          setShowCreateModal(false);
                          handleSelectProject(projId);
                        } catch (err) {
                          console.error('Failed to create precon project:', err);
                        }
                      }}
                    >
                      <span className="pm-precon-deal-name">{deal.contactName}</span>
                      <span className="pm-precon-deal-title">{deal.title}</span>
                      <span className="pm-precon-deal-meta">{deal.projectType || 'Custom'} · {deal.location || '—'}</span>
                    </button>
                  ))
                )}
              </div>
            )}

            {createMode === 'contracted' && (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <p style={{ fontSize: 13, color: 'var(--pm-text-secondary)', marginBottom: 12 }}>Create a project from a won deal.</p>
                <button
                  className="pm-btn pm-btn-primary"
                  onClick={() => { setShowCreateModal(false); setShowConvertModal(true); }}
                >
                  Select Won Deal
                </button>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="pm-btn pm-btn-ghost" onClick={() => setShowCreateModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Convert to project modal (contracted deals) */}
      {showConvertModal && (
        <ConvertToProjectModal
          deals={wonDeals}
          userId={user?.id || ''}
          onClose={() => setShowConvertModal(false)}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="pm-delete-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="pm-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-delete-modal-icon">
              <AlertTriangle size={28} />
            </div>
            <h3>Delete Project</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This will permanently remove the project and cannot be undone.</p>
            <div className="pm-delete-modal-actions">
              <button className="pm-btn pm-btn-ghost" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="pm-btn pm-btn-danger" onClick={() => handleDeleteProject(deleteConfirm.id)}>
                Delete Project
              </button>
            </div>
          </div>
        </div>
      )}

      {lockAllTasksConfirm && (
        <div className="pm-delete-overlay" onClick={() => setLockAllTasksConfirm(null)}>
          <div
            className="pm-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pm-lock-all-tasks-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`pm-lock-all-tasks-modal-icon pm-lock-all-tasks-modal-icon--${lockAllTasksConfirm}`}
              aria-hidden
            >
              {lockAllTasksConfirm === 'lock' ? (
                <Lock size={26} strokeWidth={2.25} />
              ) : (
                <Unlock size={26} strokeWidth={2.25} />
              )}
            </div>
            <h3 id="pm-lock-all-tasks-title">
              {lockAllTasksConfirm === 'lock' ? 'Lock every task?' : 'Unlock every task?'}
            </h3>
            <p>
              {lockAllTasksConfirm === 'lock' ? (
                <>
                  Lock every task in this project? Locked tasks will not move when other tasks shift.
                </>
              ) : (
                <>
                  Unlock every task in this project? Completed tasks will remain locked.
                </>
              )}
            </p>
            <div className="pm-delete-modal-actions">
              <button type="button" className="pm-btn pm-btn-ghost" onClick={() => setLockAllTasksConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="pm-btn pm-btn-primary"
                onClick={() => {
                  const lock = lockAllTasksConfirm === 'lock';
                  setLockAllTasksConfirm(null);
                  void handleLockAllTasks(lock);
                }}
              >
                {lockAllTasksConfirm === 'lock' ? 'Lock all' : 'Unlock all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Project wizard — locks Project Number, edits ClientLabel + Descriptor.
          Writes to the EnhancedDeal (canonical source of truth); the Cloud
          Function trigger fans out the new name to the TimberProject and other
          projections. We also do an immediate optimistic update on the
          TimberProject.name for instant UI feedback. */}
      {renamingProject && currentProject && currentDeal && (() => {
        const fallbackTitle = parseLegacyTitle(currentDeal.title);
        const linkedLead = currentDeal.leadId
          ? leads.find(l => l.id === currentDeal.leadId)
          : null;
        const initialLabel =
          currentDeal.clientLabel ||
          defaultClientLabelFromLead(linkedLead) ||
          fallbackTitle.label ||
          (currentDeal.contactName?.split(' ').pop() ?? '');
        const initialDescriptor =
          currentDeal.descriptor ||
          fallbackTitle.descriptor ||
          defaultDescriptorFromProjectType(currentDeal.projectType) ||
          (currentDeal.projectName ?? '');
        return (
          <NameProjectWizard
            mode="edit"
            layout="compact"
            title="Edit project"
            deferProjectNumber={!currentDeal.projectNumber}
            allowAssignProjectNumber={!currentDeal.projectNumber}
            assignNumberHint={
              !isEarlyFunnelProjectPhase(currentProject.phase)
                ? 'Under contract jobs need a project number for QuickBooks, files, and the client portal.'
                : undefined
            }
            initialValue={{
              projectNumber: currentDeal.projectNumber || '',
              clientLabel: initialLabel,
              descriptor: initialDescriptor,
            }}
            afterFields={
              <>
                <ProjectHubAddressFields
                  compact
                  value={renameAddressDraft}
                  onChange={setRenameAddressDraft}
                />
                <CompanyBillingAddressFields
                  companyName={linkedLead?.company || currentDeal.company || ''}
                />
                {(canEditDealMeta || canEditProjectEngineer) ? (
                  <ProjectDealTeamFields
                    compact
                    value={renameTeamDraft}
                    onChange={setRenameTeamDraft}
                    designersList={designersList}
                    architectsList={assignableArchitects}
                    engineeringFirms={eorFirmOptions}
                    canEditDesigner={canEditDealMeta}
                    canEditArchitect={canEditDealMeta}
                    canEditEngineer={canEditProjectEngineer}
                    engineerDisplay={projectEorDisplay}
                  />
                ) : null}
              </>
            }
            warningMessage="Updates P&L, files, calendar, and portal labels. Project #, proposal URL, and QuickBooks job stay unchanged."
            onClose={() => setRenamingProject(false)}
            onConfirm={async (next) => {
              if (!currentDeal.id || !currentProject?.id) return;
              const trimmedNumber = next.projectNumber.trim();
              const newName = [trimmedNumber, next.clientLabel.trim(), next.descriptor.trim()].filter(Boolean).join(' ').trim();
              await updateEnhancedDeal(currentDeal.id, {
                ...(trimmedNumber ? { projectNumber: trimmedNumber } : {}),
                clientLabel: next.clientLabel.trim(),
                descriptor: next.descriptor.trim(),
                title: newName,
                projectName: next.descriptor.trim(),
              });
              await saveDealTeamFields(renameTeamDraft, renameTeamBaselineRef.current);
              const addressDraft = renameAddressDraftRef.current;
              const addressChanged = !projectSiteAddressDraftsEqual(
                addressDraft,
                renameAddressBaselineRef.current,
              );
              if (addressChanged) {
                const finalLocation =
                  addressDraft.location.trim() || formatLocation(addressDraft.address);
                if (finalLocation.trim() || addressDraft.address.street || addressDraft.address.city) {
                  await setProjectAddressEverywhere(currentDeal.id, {
                    address: addressDraft.address,
                    location: finalLocation,
                    coordinates: addressDraft.coordinates,
                  });
                }
              }
              await updateTimberProject(currentProject.id, { name: newName });
              setRenamingProject(false);
            }}
          />
        );
      })()}

      {unreadHubPopover && (
        <ProjectHubUnreadPopover
          open
          projectLabel={unreadHubPopover.projectLabel}
          items={unreadHubPopoverItems}
          loading={!unreadPopoverData?.ready}
          onClose={() => setUnreadHubPopover(null)}
          onPick={handleUnreadHubPopoverPick}
        />
      )}

      {selectedProjectId && (
        <ProjectHubMetaFlyout
          hover={projectMetaHover}
          siteAddress={headerSiteAddress}
          client={headerClient}
          designerUid={assignedDesigner.uid}
          designerName={assignedDesigner.name}
          designersList={designersList}
          architectName={externalArchitectName}
          architectDraft={externalArchitectName}
          onArchitectDraftChange={() => {}}
          onArchitectBlur={() => {}}
          onArchitectEscape={() => {}}
          eor={projectEor}
          engineeringFirms={engineeringFirms ?? []}
          eorFirmDraft=""
          onEorFirmDraftChange={() => {}}
          onSelectEngineer={() => {}}
          canEditDealMeta={false}
          canEditEngineer={false}
          onDesignerChange={() => {}}
          onMouseEnter={handleMetaFlyoutMouseEnter}
          onMouseLeave={handleMetaFlyoutMouseLeave}
        />
      )}

      {viewMode === 'table' && selectedProjectId && !architectHubMode && (
        <CommentsHelpButton
          onReplay={startCommentsTour}
          onReplayArchitectDecisions={
            architectHubMode
              ? undefined
              : () => {
                  clearArchitectDecisionsTourSeen('staff');
                  window.dispatchEvent(new Event('lctf:replay-architect-decisions-tour'));
                }
          }
        />
      )}

      <HubQuickSearch
        open={showQuickSearch}
        onClose={() => setShowQuickSearch(false)}
        getResults={getProjectQuickSearchResults}
        onSelect={handleProjectQuickSearchSelect}
        placeholder="Search projects, tasks, groups, comments…"
        ariaLabel="Project Hub quick search"
        renderIcon={(result) => {
          if (result.kind === 'project') return <FolderKanban size={16} />;
          if (result.kind === 'task') return <ListTodo size={16} />;
          if (result.kind === 'comment') return <MessageSquare size={16} />;
          if (result.kind === 'group') return <LayoutList size={16} />;
          return <Search size={16} />;
        }}
      />

      </ProjectUploadModalProvider>
    </div>
    </ProjectHubScopedDataProvider>
  );
};

export default ProjectHub;
